import {
  createContext,
  memo,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import type { CSSProperties, MutableRefObject, ReactNode, RefObject } from "react";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Drawer,
  Dropdown,
  Form,
  Input,
  Menu,
  Message,
  Modal,
  Popconfirm,
  Select,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from "@arco-design/web-react";
import type { FormInstance, PaginationProps, TableColumnProps } from "@arco-design/web-react";
import {
  ChevronDown,
  ChevronUp,
  Download,
  FileSignature,
  Link2,
  Copy,
  MessageSquareText,
  PenLine,
  ArrowLeftRight,
  Save,
  Search,
  TriangleAlert,
  Trash2,
  Upload as UploadIcon,
  Users,
  X,
  Plus,
  Maximize2,
  Minimize2,
  SlidersHorizontal,
} from "lucide-react";

import { useCurrentAuthUser } from "../../auth";
import type { FeeAdjustmentRecord } from "../../fee-adjustment";
import { canUsePermission } from "../../../lib/auth";
import { useRosterFeeAdjustmentRequests } from "../../fee-adjustment";
import {
  FeeAdjustmentRecords,
  FeeAdjustmentRequestModal,
} from "./FeeAdjustmentSalesWorkspace";
import {
  buildContractReceivingAccountText,
  contractTemplateTypeOptions,
  customerNoveltyTypeOptions,
  getContractInvoiceLabel,
  signTeamStyleOptions,
} from "../config";
import {
  collectSalesHandoverRequiredErrors,
  calculateRosterReceivableSummary,
  formatItineraryPeople,
} from "../service";
import type { SalesHandoverRequiredFieldKey } from "../service";
import type {
  HandoverItineraryOption,
  OrderHandoverContractSigning,
  OrderHandoverMajorOption,
  OrderHandoverRoster,
  OrderHandoverOrderLine,
  OrderHandoverRosterMemberCreatePayload,
  OrderHandoverRosterMemberUpdatePayload,
  OrderHandoverRouteContext,
  OrderHandoverSchoolSiteOption,
  OrderHandoverSalesHandover,
  OrderHandoverSalesHandoverUpdate,
  OrderHandoverStaffOption,
  OrderHandoverSummary,
  OrderRosterMember,
} from "../types";
import { useOrderHandoverRuntime, useOrderHandoverRosterReturn } from "../runtime";
import type { OrderHandoverRosterReturnState, OrderHandoverRosterViewState } from "../runtime";
import { OrderHandoverBusinessChangeCard } from "./OrderHandoverBusinessChangeCard";
import { FulfillmentOrderReadOnlyPanel } from "../../fulfillment-order";
import { OrderHandoverGuidePanel } from "./OrderHandoverGuidePanel";
import "./OrderHandoverDetailPage.css";

const { Text } = Typography;

type EditableSelectOption = {
  label: string;
  value: string;
  disabled?: boolean;
  searchText?: string;
};

type BooleanSelectValue = "true" | "false";

type BooleanSelectOption = {
  label: string;
  value: BooleanSelectValue;
  searchText: string;
};

type BooleanSalesField =
  | "isAdvanceMoney"
  | "isCollectTransportFee"
  | "isCollectIntangibleOrSchoolEnterpriseFee"
  | "teacherLocalPosFlag";

type SalesHandoverFormValues = Omit<
  OrderHandoverSalesHandoverUpdate,
  "contractSignerNames" | BooleanSalesField
> & {
  contractSignerNames?: string;
  contractSignerMemberId?: string;
} & Partial<Record<BooleanSalesField, BooleanSelectValue>>;

type SalesSheetFieldName = keyof SalesHandoverFormValues;

const CUSTOMER_NOVELTY_TYPE_OPTIONS: EditableSelectOption[] = customerNoveltyTypeOptions.map(
  (item) => ({ label: item, value: item, searchText: item }),
);
const CONTRACT_TEMPLATE_TYPE_OPTIONS: EditableSelectOption[] = contractTemplateTypeOptions.map(
  (item) => ({ label: item, value: item, searchText: item }),
);
const SIGN_TEAM_STYLE_OPTIONS: EditableSelectOption[] = signTeamStyleOptions.map((item) => ({
  label: item,
  value: item,
  searchText: item,
}));
const BOOLEAN_OPTIONS: BooleanSelectOption[] = [
  { label: "是", value: "true", searchText: "是 true" },
  { label: "否", value: "false", searchText: "否 false" },
];
const STAFF_PLACEHOLDER_NAMES = new Set(["待填写", "待选择", "未维护", "未分配"]);
const SalesRequiredMissingContext = createContext<ReadonlySet<string>>(new Set());
const ROSTER_TRAVELER_TYPE_OPTIONS: EditableSelectOption[] = ["学生", "老师", "家属", "其他"].map(
  (item) => ({ label: item, value: item, searchText: item }),
);
const ROSTER_CERTIFICATE_TYPE_OPTIONS: EditableSelectOption[] = ["身份证", "护照", "军人证", "其他"].map(
  (item) => ({ label: item, value: item, searchText: item }),
);
const ROSTER_GENDER_OPTIONS: EditableSelectOption[] = ["男", "女", "未知"].map((item) => ({
  label: item,
  value: item,
  searchText: item,
}));
const ROSTER_CREATE_DRAWER_A11Y_PROPS = {
  role: "dialog",
  "aria-label": "新增名单成员",
};
const ROSTER_CREATE_TEXT_INPUT_PROPS = {
  type: "text",
  autoComplete: "off",
} as const;
const MAINLAND_MOBILE_PATTERN = /^1[3-9]\d{9}$/;
const OTHER_CERTIFICATE_NO_PATTERN = /^[A-Za-z0-9-]{5,30}$/;
const MAINLAND_ID_CARD_WEIGHTS = [7, 9, 10, 5, 8, 4, 2, 1, 6, 3, 7, 9, 10, 5, 8, 4, 2] as const;
const MAINLAND_ID_CARD_CHECK_CODES = "10X98765432";

type Props = {
  context: OrderHandoverRouteContext;
  onOpenRosterImport: () => void;
  onOpenMemberFeeAdjustment?: (memberId: string, rosterId: string, view: OrderHandoverRosterViewState) => void;
  rosterReturnState?: OrderHandoverRosterReturnState;
};

type SalesInfoBadgeTone =
  | "source"
  | "contract"
  | "money"
  | "staff"
  | "teacher"
  | "route"
  | "warning"
  | "neutral";
type SalesInfoTone = SalesInfoBadgeTone | "link" | "danger";

type SalesInfoBadge = {
  text: string;
  tone: SalesInfoBadgeTone;
};

type ActualReceivableLine = {
  label: string;
  count: string;
  price: string;
  total: string;
  totalAmount: number;
  remark: string;
};

const LEGACY_CONTRACT_SHEET_WIDTH = 1900;
const LEGACY_CONTRACT_SHEET_COLUMN_WIDTHS = [110, 155, 131, 155, 164, 225, 149, 225, 128, 162, 296] as const;
const LEGACY_CONTRACT_SHEET_DEFAULT_HEIGHT = 880;
const LEGACY_CONTRACT_SHEET_DEFAULT_PREVIEW_SCALE = 0.62;
const LEGACY_CONTRACT_SHEET_MIN_PREVIEW_SCALE = 0.42;

type RosterMemberEditableDraft = {
  memberName: string;
  travelerTypeLabel: string;
  teacherTypeLabel: string;
  classGroupLabel: string;
  certificateType: string;
  certificateNo: string;
  mobile: string;
  gender: string;
  age: string;
  birthday: string;
  studentNo: string;
  roomingLabel: string;
  trafficTypeLabel: string;
  vehicleAssignmentLabel: string;
  bigTrafficInfo: string;
  guidanceNote: string;
  actualReceivableAmount: string;
  lineAmount: string;
  mainSignerAddress: string;
  remark: string;
};

type RosterSelectionStore = {
  subscribe: (listener: () => void) => () => void;
  subscribeMember: (memberId: string, listener: () => void) => () => void;
  emit: (memberIds?: string[]) => void;
  getSnapshot: () => number;
};

type RosterEditingStore = {
  subscribeMember: (memberId: string, listener: () => void) => () => void;
  subscribeOperations: (listener: () => void) => () => void;
  emit: (memberIds: string[]) => void;
  getSnapshot: () => number;
};

type RosterSelectScope = "page" | "all";

type RosterColumnOptions = {
  editingMemberIdsRef: MutableRefObject<Set<string>>;
  editingStore: RosterEditingStore;
  majorOptions: EditableSelectOption[];
  travelerTypeOptions: EditableSelectOption[];
  teacherTypeOptions: EditableSelectOption[];
  saving: boolean;
  selectedIdsRef: MutableRefObject<Set<string>>;
  selectionStore: RosterSelectionStore;
  visibleMemberIdsRef: MutableRefObject<string[]>;
  currentPageMemberIdsRef: MutableRefObject<string[]>;
  selectScopeRef: MutableRefObject<RosterSelectScope | null>;
  locked: boolean;
  feeAdjustmentRecords: FeeAdjustmentRecord[];
  feeAdjustmentAvailable: boolean;
  showFeeAdjustment: boolean;
  onToggleSelection: (memberId: string, checked: boolean) => void;
  onOpenMemberFeeAdjustment: (member: OrderRosterMember) => void;
  onRequestSelectAll: () => void;
  onClearVisibleSelection: () => void;
  onDraftInputChange: (
    memberId: string,
    field: keyof RosterMemberEditableDraft,
    value: string,
  ) => void;
  onOpenEdit: (member: OrderRosterMember) => void;
  onCancelEdit: (member: OrderRosterMember) => void;
  onSaveEdit: (member: OrderRosterMember) => void;
  onToggleStop: (member: OrderRosterMember) => void;
  onClearMemberFlag: (
    member: OrderRosterMember,
    flag: "signer" | "hidden" | "linkman" | "mainSigner",
  ) => void;
  onClearAllMemberFlags: (member: OrderRosterMember) => void;
};


type RosterTableProps = {
  columns: TableColumnProps<OrderRosterMember>[];
  data: OrderRosterMember[];
  loading?: boolean;
  pagination: PaginationProps;
  onPaginationChange?: (current: number, pageSize: number) => void;
};

/** 合同签署人表分页（前端本地分页）。 */
const CONTRACT_SIGNER_TABLE_PAGINATION: PaginationProps = {
  sizeCanChange: true,
  showTotal: true,
  defaultPageSize: 10,
  sizeOptions: [5, 10, 20, 50],
  hideOnSinglePage: false,
};

/** 出行名单表分页（前端本地分页）。 */
const ROSTER_TABLE_PAGINATION: PaginationProps = {
  sizeCanChange: true,
  showTotal: true,
  defaultPageSize: 20,
  sizeOptions: [10, 20, 50, 100],
  hideOnSinglePage: false,
};

/** 优先使用 Clipboard API，不可用或被拒绝时回退到浏览器复制命令。 */
async function writeClipboardText(value: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(value);
      return;
    } catch {
      // 权限拒绝时继续使用兼容路径。
    }
  }
  const textarea = document.createElement("textarea");
  textarea.value = value;
  textarea.readOnly = true;
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  try {
    textarea.select();
    if (!document.execCommand?.("copy")) {
      throw new Error("当前浏览器不支持自动复制");
    }
  } finally {
    textarea.remove();
  }
}

/** 出行名单表横向滚动宽度。 */
const ROSTER_TABLE_SCROLL_X = 2764;

/** Arco scroll.y 是最大高度，短名单按实际内容展开，避免估算行高裁切搜索结果。 */
const ROSTER_TABLE_BODY_MAX_Y = 480;

/** 出行名单表格，避免选中行时重绘整张表。 */
const MemoizedRosterTable = memo(function RosterMemberTable({
  columns,
  data,
  loading = false,
  pagination,
  onPaginationChange,
}: RosterTableProps) {
  return (
    <Table
      rowKey="id"
      columns={columns}
      data={data}
      loading={loading}
      pagination={pagination}
      onChange={(paginationConfig) => {
        onPaginationChange?.(
          Number(paginationConfig.current ?? 1),
          Number(paginationConfig.pageSize ?? ROSTER_TABLE_PAGINATION.defaultPageSize),
        );
      }}
      border={false}
      size="mini"
      tableLayoutFixed
      scroll={{ x: ROSTER_TABLE_SCROLL_X, y: ROSTER_TABLE_BODY_MAX_Y }}
      noDataElement="当前订单暂无出行名单成员"
    />
  );
});

/** 订单交接详情工作台。 */
export function OrderHandoverDetailFeaturePage({ context, onOpenRosterImport, onOpenMemberFeeAdjustment, rosterReturnState }: Props) {
  const runtime = useOrderHandoverRuntime(context, { initialRosterView: rosterReturnState });
  const pageRef = useRef<HTMLElement | null>(null);
  useOrderHandoverRosterReturn(rosterReturnState, runtime.roster?.id, runtime.detailLoading, pageRef);
  const currentUser = useCurrentAuthUser().data;
  const canManage = canUsePermission(currentUser, "customization.order_handover:manage");
  const canAccessFeeAdjustment = canUsePermission(currentUser, "fee_adjustment.request:access");
  const canCreateFeeAdjustment = canUsePermission(currentUser, "fee_adjustment.request:create");
  const [form] = Form.useForm<SalesHandoverFormValues>();
  const [rosterCreateForm] = Form.useForm<RosterMemberEditableDraft>();
  const [salesCollapsed, setSalesCollapsed] = useState(true);
  const handleActiveSalesEditorField = useCallback(
    (field: SalesSheetFieldName | undefined) => {
      void field;
    },
    [],
  );
  const [salesInlineDraftValues, setSalesInlineDraftValues] = useState<Partial<SalesHandoverFormValues>>({});
  const [, setSelectedRosterVersion] = useState(0);
  const [rosterCreateVisible, setRosterCreateVisible] = useState(false);
  const [feeAdjustmentRequestMembers, setFeeAdjustmentRequestMembers] = useState<OrderRosterMember[]>([]);
  const [rosterDeleteConfirmMember, setRosterDeleteConfirmMember] = useState<OrderRosterMember>();
  const [rosterBatchDeleteConfirmVisible, setRosterBatchDeleteConfirmVisible] = useState(false);
  const [rosterFlagAction, setRosterFlagAction] = useState<
    "signer" | "hidden" | "linkman" | "mainSigner" | null
  >(null);
  const [editingRosterCount, setEditingRosterCount] = useState(0);
  const editingRosterMemberIdsRef = useRef<Set<string>>(new Set());
  const [, setEditingRosterVersion] = useState(0);
  const [rosterSaveErrorMessage, setRosterSaveErrorMessage] = useState("");
  const [rosterCreateErrorMessage, setRosterCreateErrorMessage] = useState("");
  const [salesFinalizeErrorMessage, setSalesFinalizeErrorMessage] = useState("");
  const [salesRequiredMissingFields, setSalesRequiredMissingFields] = useState<
    SalesHandoverRequiredFieldKey[]
  >([]);
  const salesCardRef = useRef<HTMLDivElement | null>(null);
  const routeCellRef = useRef<HTMLTableCellElement | null>(null);
  const [rosterFinalizeErrorMessage, setRosterFinalizeErrorMessage] = useState("");
  const [routeTeamIdNeedsConfirm, setRouteTeamIdNeedsConfirm] = useState(false);
  const [customerQuoteId, setCustomerQuoteId] = useState(rosterReturnState?.customerQuoteId ?? "");
  const [customerQuoteAdoptErrorMessage, setCustomerQuoteAdoptErrorMessage] = useState("");
  const [contractPublishErrorMessage, setContractPublishErrorMessage] = useState("");
  const [contractSigningErrorMessage, setContractSigningErrorMessage] = useState("");
  const [rosterPage, setRosterPage] = useState(rosterReturnState?.page ?? 1);
  const [rosterPageSize, setRosterPageSize] = useState(
    rosterReturnState?.pageSize ?? Number(ROSTER_TABLE_PAGINATION.defaultPageSize ?? 20),
  );
  const [rosterSelectAllVisible, setRosterSelectAllVisible] = useState(false);
  const [rosterFiltersOpen, setRosterFiltersOpen] = useState(() =>
    hasSavedRosterFilters(rosterReturnState),
  );
  const [rosterSelectScope, setRosterSelectScope] = useState<RosterSelectScope | null>(null);
  const rosterSelectScopeRef = useRef<RosterSelectScope | null>(null);
  const currentPageMemberIdsRef = useRef<string[]>([]);
  rosterSelectScopeRef.current = rosterSelectScope;
  const [resendingSignRecordId, setResendingSignRecordId] = useState<string>();
  const editedRosterMembersRef = useRef<Record<string, RosterMemberEditableDraft>>({});
  const selectedRosterIdsRef = useRef<Set<string>>(new Set());
  const visibleRosterMemberIdsRef = useRef<string[]>([]);
  const rosterSelectionVersionRef = useRef(0);
  const rosterEditingVersionRef = useRef(0);
  const rosterSelectionListenersRef = useRef(new Set<() => void>());
  const rosterSelectionMemberListenersRef = useRef(new Map<string, Set<() => void>>());
  const rosterEditingMemberListenersRef = useRef(new Map<string, Set<() => void>>());
  const rosterEditingOperationListenersRef = useRef(new Set<() => void>());
  const runtimeRef = useRef(runtime);
  const salesDraftValuesRef = useRef<Partial<SalesHandoverFormValues>>({});
  const syncingSalesFormRef = useRef(false);
  const selected = runtime.selectedItinerary;
  const pendingItinerary = runtime.pendingItinerary;
  const sales = runtime.salesHandover;
  const contractSigning = runtime.contractSigning;
  const amount = runtime.summary?.amountText ?? "¥0.00";
  const customerQuote = runtime.customerQuoteOptions.find(
    (option) => option.customerQuoteId === customerQuoteId,
  );
  const salesFormValues = useMemo(
    () => buildSalesFormValues(sales, runtime.rosterMembers),
    [sales, runtime.rosterMembers],
  );
  const salesFormValuesRef = useRef(salesFormValues);
  salesFormValuesRef.current = salesFormValues;
  const salesDraftValues = useMemo(
    () => mergeItinerarySalesDraftValues(salesFormValues, runtime.pendingItinerary),
    [runtime.pendingItinerary, salesFormValues],
  );
  /** 已填字段不再标红：用当前表单/草稿再过滤一轮缺失集合。 */
  const visibleSalesRequiredMissingFields = useMemo(() => {
    if (salesRequiredMissingFields.length === 0) {
      return salesRequiredMissingFields;
    }
    const stillMissing = new Set(
      collectSalesHandoverRequiredErrors({
        sales,
        draft: {
          ...salesFormValues,
          ...salesDraftValues,
          ...salesInlineDraftValues,
        },
        signType: runtime.summary?.signType,
      }).map((item) => item.field),
    );
    return salesRequiredMissingFields.filter((field) => stillMissing.has(field));
  }, [
    runtime.summary?.signType,
    sales,
    salesDraftValues,
    salesFormValues,
    salesInlineDraftValues,
    salesRequiredMissingFields,
  ]);
  const contractSignerOptions = useMemo(
    () => toRosterContractSignerOptions(runtime.rosterMembers),
    [runtime.rosterMembers],
  );
  const contractSignerDisplayName = getContractSignerDisplayName(sales, runtime.rosterMembers);
  const majorNameOptions = useMemo(
    () => toMajorOptions(sales?.majorNamesText, runtime.majorOptions),
    [sales?.majorNamesText, runtime.majorOptions],
  );
  const rosterExistingClassGroups = useMemo(
    () => Array.from(new Set(runtime.rosterMembers
      .filter((member) => !member.isStopped)
      .map((member) => member.classGroup?.trim())
      .filter((value): value is string => Boolean(value)))),
    [runtime.rosterMembers],
  );
  const rosterClassGroupOptions = useMemo(
    () => mergeSelectOptions(majorNameOptions, ...rosterExistingClassGroups),
    [majorNameOptions, rosterExistingClassGroups],
  );
  const rosterTravelerTypeOptions = ROSTER_TRAVELER_TYPE_OPTIONS;
  const rosterTeacherTypeOptions: EditableSelectOption[] = [];
  const rosterCertificateTypeOptions = ROSTER_CERTIFICATE_TYPE_OPTIONS;
  const rosterGenderOptions = ROSTER_GENDER_OPTIONS;
  const schoolSiteOptions = useMemo(
    () => toSchoolSiteOptions(sales?.schoolSiteText, runtime.schoolSiteOptions),
    [sales?.schoolSiteText, runtime.schoolSiteOptions],
  );

  useEffect(() => {
    if (runtime.detailLoading || runtime.customerQuoteOptionsLoading) return;
    setCustomerQuoteId((currentCustomerQuoteId) => {
      if (
        currentCustomerQuoteId
        && runtime.customerQuoteOptions.some(
          (option) => option.customerQuoteId === currentCustomerQuoteId,
        )
      ) {
        return currentCustomerQuoteId;
      }
      return runtime.selectedCustomerQuoteId
        ?? runtime.customerQuoteOptions[0]?.customerQuoteId
        ?? "";
    });
  }, [runtime.customerQuoteOptions, runtime.selectedCustomerQuoteId, runtime.detailLoading, runtime.customerQuoteOptionsLoading]);
  const serviceConsultantSelectOptions = useMemo(
    () =>
      mergeStaffOptions(runtime.serviceConsultantOptions, {
        id: sales?.serviceConsultantUserId,
        name: sales?.serviceConsultantName,
      }),
    [
      runtime.serviceConsultantOptions,
      sales?.serviceConsultantName,
      sales?.serviceConsultantUserId,
    ],
  );
  const catchOrderSelectOptions = useMemo(
    () =>
      mergeStaffOptions(runtime.staffOptions, {
        id: sales?.catchOrderUserId,
        name: sales?.catchOrderUserName,
      }),
    [runtime.staffOptions, sales?.catchOrderUserId, sales?.catchOrderUserName],
  );
  const salesUserSelectOptions = useMemo(
    () =>
      mergeStaffOptions(runtime.staffOptions, {
        id: sales?.salesUserId,
        name: sales?.salesUserName,
      }),
    [runtime.staffOptions, sales?.salesUserId, sales?.salesUserName],
  );
  const dispatcherSelectOptions = useMemo(
    () =>
      mergeStaffOptions(runtime.staffOptions, {
        id: sales?.dispatcherUserId,
        name: sales?.dispatcherUserName ?? selected?.dispatcherUserName,
      }),
    [
      runtime.staffOptions,
      sales?.dispatcherUserId,
      sales?.dispatcherUserName,
      selected?.dispatcherUserName,
    ],
  );
  const itineraryColumns = useMemo(
    () =>
      buildItineraryColumns(
        (option) => runtime.selectItinerary(option),
        runtime.itinerarySelecting,
        canManage,
        selected?.id,
      ),
    [canManage, runtime, selected?.id],
  );
  const filteredRosterMembers = useMemo(
    () =>
      filterRosterMembers(runtime.rosterMembers, {
        keyword: runtime.rosterKeyword,
        type: runtime.rosterTypeFilter,
        classGroup: runtime.rosterClassGroupFilter,
        stopped: runtime.rosterStoppedFilter,
      }),
    [
      runtime.rosterMembers,
      runtime.rosterKeyword,
      runtime.rosterTypeFilter,
      runtime.rosterClassGroupFilter,
      runtime.rosterStoppedFilter,
    ],
  );
  const rosterPagination = useMemo<PaginationProps>(
    () => ({
      ...ROSTER_TABLE_PAGINATION,
      current: rosterPage,
      pageSize: rosterPageSize,
      total: filteredRosterMembers.length,
    }),
    [filteredRosterMembers.length, rosterPage, rosterPageSize],
  );
  const currentPageRosterMemberIds = useMemo(
    () =>
      filteredRosterMembers
        .slice((rosterPage - 1) * rosterPageSize, rosterPage * rosterPageSize)
        .map((member) => member.id),
    [filteredRosterMembers, rosterPage, rosterPageSize],
  );
  currentPageMemberIdsRef.current = currentPageRosterMemberIds;
  const visibleRosterMemberIds = useMemo(
    () => filteredRosterMembers.map((member) => member.id),
    [filteredRosterMembers],
  );
  visibleRosterMemberIdsRef.current = visibleRosterMemberIds;
  const feeAdjustmentRosterId = runtime.roster?.id ?? "";
  const feeAdjustmentAvailable = Boolean(
    feeAdjustmentRosterId
      && runtime.detail?.orderId
      && customerQuoteId
      && runtime.customerQuoteOptions.some(
        (option) => option.customerQuoteId === customerQuoteId,
      )
      && selected?.isConfirmed
      && selected?.pricingFinalized,
  );
  const feeAdjustmentsQuery = useRosterFeeAdjustmentRequests(
    feeAdjustmentRosterId,
    canAccessFeeAdjustment && feeAdjustmentAvailable,
    customerQuoteId,
  );
  const { frozenRosterReceivableTotal, feeAdjustmentTotal, currentRosterReceivableTotal } = useMemo(
    () => calculateRosterReceivableSummary(runtime.rosterMembers, feeAdjustmentsQuery.data ?? []),
    [runtime.rosterMembers, feeAdjustmentsQuery.data],
  );
  const handleRosterExport = useCallback(
    (exportType: "standard" | "guide" | "template") => {
      if (exportType !== "standard") {
        runtime.exportRoster(exportType);
        return;
      }
      const includeFeeAdjustmentColumns = Boolean(
        canAccessFeeAdjustment && feeAdjustmentAvailable,
      );
      const processedReceivableByMemberId = new Map<string, number>();
      for (const record of feeAdjustmentsQuery.data ?? []) {
        if (record.status !== "processed") {
          continue;
        }
        for (const member of record.members) {
          const amount = Number(member.receivable_delta_amount || "0");
          processedReceivableByMemberId.set(
            member.roster_member_id,
            (processedReceivableByMemberId.get(member.roster_member_id) ?? 0) + amount,
          );
        }
      }
      runtime.exportRoster("standard", {
        members: filteredRosterMembers,
        includeFeeAdjustmentColumns,
        feeAdjustments: filteredRosterMembers.map((member) => {
          const base = Number(member.actualReceivableAmount || "0");
          const adjustment = processedReceivableByMemberId.get(member.id) ?? 0;
          return {
            rosterMemberId: member.id,
            baseReceivableAmount: formatDecimalText(base),
            processedReceivableAdjustmentAmount: formatDecimalText(adjustment),
            finalReceivableAmount: formatDecimalText(base + adjustment),
            statusLabel: "",
          };
        }),
      });
    },
    [
      canAccessFeeAdjustment,
      feeAdjustmentAvailable,
      feeAdjustmentsQuery.data,
      filteredRosterMembers,
      runtime,
    ],
  );
  const rosterTypeOptions = useMemo(
    () => buildRosterTypeOptions(runtime.rosterMembers, rosterTravelerTypeOptions),
    [runtime.rosterMembers, rosterTravelerTypeOptions],
  );
  const rosterClassGroupFilterOptions = useMemo(
    () => buildRosterClassGroupFilterOptions(runtime.rosterMembers),
    [runtime.rosterMembers],
  );
  const selectedRosterCount = selectedRosterIdsRef.current.size;
  const selectedHasHiddenContract = runtime.rosterMembers.some(
    (member) => selectedRosterIdsRef.current.has(member.id) && !member.isShowInContract,
  );
  const rosterLocked = !canManage || isRosterLocked(runtime.summary?.orderStatus, runtime.roster);
  const rosterStructureReadOnly = rosterLocked;
  const rosterReadOnlyMessage = canManage
    ? getRosterReadOnlyMessage(runtime.summary?.orderStatus, runtime.roster)
    : "缺少合同交接维护权限，当前名单仅可查看";
  const contractHandoverLocked = !canManage || isContractHandoverLocked(runtime.summary?.orderStatus);
  const contractHandoverReadOnlyMessage = canManage
    ? getContractHandoverReadOnlyMessage(runtime.summary?.orderStatus)
    : "缺少合同交接维护权限，当前交接信息仅可查看";
  const customerQuoteAdoptBlockedReason = getCustomerQuoteAdoptBlockedReason(
    canManage,
    runtime.summary?.orderStatus,
    runtime.roster,
  );
  const salesHandoverFinalized = isSalesHandoverFinalized(runtime.summary?.orderStatus);
  const contractPublished = runtime.summary?.orderStatus?.trim() === "formed";
  const contractSigningNeedsAttention =
    contractSigning?.signStateCode === "void_applying" ||
    contractSigning?.signStateCode === "partial_voided";
  const showContractSigningCard = Boolean(contractPublished || contractSigningNeedsAttention);
  const isTourismBureauContract = contractSigning?.providerCode === "tourism_bureau";
  const unpublishConfirmTitle = isTourismBureauContract
    ? "确认取消挂网？将回退挂网与合同交接定稿。旅游局合同：未签/部分已签直接作废；全部已签（含单人）将发起作废并等待签署人同意后才能取消挂网（列表显示「作废中」）。"
    : "确认取消挂网？仅回退「合同挂网」和「合同交接」定稿；名单/行程/核价定稿保持不变。电子合同暂保留，再次挂网时会自动作废旧合同并生成新签署。";
  const unpublishAllConfirmTitle = isTourismBureauContract
    ? "确认取消全部定稿？将回退挂网、合同交接、名单、行程、核价。旅游局合同：未签/部分已签直接作废；全部已签须待签署人同意作废后才能操作。"
    : "确认取消全部定稿？将回退全部 5 项：合同挂网、合同交接、出行名单、行程方案、核价表。电子合同记录仍保留。";
  const rosterFinalized = isRosterFinalized(runtime.roster);
  const signType = runtime.summary?.signType?.trim() || "团队";
  const isSingleOrder = signType === "单订房" || signType === "单订车";
  const itineraryReady = Boolean(selected?.isConfirmed);
  const pricingReady = Boolean(selected?.pricingFinalized);
  // 单订房/单订车不要求行程+核价定稿（对齐老系统）
  const canFinalizeRoster =
    (isSingleOrder || (itineraryReady && pricingReady)) &&
    (isSingleOrder || Boolean(runtime.selectedCustomerQuoteId));
  const isRosterEditing = editingRosterCount > 0;
  const finalizeHint = contractPublished
    ? isTourismBureauContract
      ? "取消挂网：回退挂网+合同交接；旅游局未签/部分已签直接作废，全部已签须待同意作废完成后再取消挂网。"
      : "取消挂网：只回退「合同挂网 + 合同交接」；取消全部定稿：回退全部 5 项（挂网、合同交接、名单、行程、核价）。电子合同记录保留。"
    : isRosterEditing
      ? "名单编辑中：请先保存或取消编辑，再进行名单定稿或合同交接定稿。"
    : salesHandoverFinalized
      ? "请按步骤回退：先「取消合同定稿」，再「名单取消定稿」。电子合同记录保留，再次挂网时会自动作废旧合同。"
    : !canFinalizeRoster && !rosterFinalized
      ? isSingleOrder
        ? "单订房/单订车可直接进行名单定稿。"
        : !itineraryReady || !pricingReady
          ? "请先完成行程方案与核价表定稿，再进行名单定稿。"
          : "请先采用成交报价，再进行名单定稿。"
    : !rosterFinalized && !salesHandoverFinalized
      ? "请先完成出行名单定稿，再定稿合同交接。"
      : "提示：定稿顺序为行程/核价 → 成交报价 → 名单 → 合同交接 → 挂网；回退时需按相反顺序。";
  const itineraryCheckDone = isSingleOrder || contractPublished || itineraryReady;
  const pricingCheckDone = isSingleOrder || contractPublished || pricingReady;
  const finalizeCheckSteps = [
    {
      key: "itinerary",
      done: itineraryCheckDone,
      text: `行程方案：${
        isSingleOrder
          ? "单订免检"
          : itineraryCheckDone
            ? `已定稿 ${selected?.version ?? ""}`.trim()
            : "待定稿"
      }`,
    },
    {
      key: "pricing",
      done: pricingCheckDone,
      text: `核价表：${isSingleOrder ? "单订免检" : pricingCheckDone ? "已完成" : "待定稿"}`,
    },
    {
      key: "roster",
      done: rosterFinalized,
      text: `出行名单：${rosterFinalized ? "已定稿" : "待定稿"}（${formatRosterState(runtime.rosterStats)}）`,
    },
    {
      key: "sales",
      done: salesHandoverFinalized,
      text: `合同交接：${formatSalesFinalizeState(runtime.summary?.orderStatus)}`,
    },
    {
      key: "publish",
      done: contractPublished,
      text: `合同挂网：${contractPublished ? "已挂网" : "待挂网"}`,
    },
  ];
  const finalizeCheckCurrentIndex = finalizeCheckSteps.findIndex((step) => !step.done);
  const rosterPeopleText = formatRosterPeople(runtime.rosterStats);
  const rosterSelectionStore = useMemo<RosterSelectionStore>(
    () => ({
      subscribe: (listener) => {
        rosterSelectionListenersRef.current.add(listener);
        return () => rosterSelectionListenersRef.current.delete(listener);
      },
      subscribeMember: (memberId, listener) => {
        let listeners = rosterSelectionMemberListenersRef.current.get(memberId);
        if (!listeners) {
          listeners = new Set();
          rosterSelectionMemberListenersRef.current.set(memberId, listeners);
        }
        listeners.add(listener);
        return () => {
          listeners?.delete(listener);
          if (listeners?.size === 0) {
            rosterSelectionMemberListenersRef.current.delete(memberId);
          }
        };
      },
      emit: (memberIds) => {
        rosterSelectionVersionRef.current += 1;
        setSelectedRosterVersion(rosterSelectionVersionRef.current);
        rosterSelectionListenersRef.current.forEach((listener) => listener());
        memberIds?.forEach((memberId) => {
          rosterSelectionMemberListenersRef.current.get(memberId)?.forEach((listener) => listener());
        });
      },
      getSnapshot: () => rosterSelectionVersionRef.current,
    }),
    [],
  );
  const rosterEditingStore = useMemo<RosterEditingStore>(
    () => ({
      subscribeMember: (memberId, listener) => {
        let listeners = rosterEditingMemberListenersRef.current.get(memberId);
        if (!listeners) {
          listeners = new Set();
          rosterEditingMemberListenersRef.current.set(memberId, listeners);
        }
        listeners.add(listener);
        return () => {
          listeners?.delete(listener);
          if (listeners?.size === 0) {
            rosterEditingMemberListenersRef.current.delete(memberId);
          }
        };
      },
      subscribeOperations: (listener) => {
        rosterEditingOperationListenersRef.current.add(listener);
        return () => rosterEditingOperationListenersRef.current.delete(listener);
      },
      emit: (memberIds) => {
        rosterEditingVersionRef.current += 1;
        setEditingRosterVersion(rosterEditingVersionRef.current);
        const uniqueMemberIds = new Set(memberIds);
        uniqueMemberIds.forEach((memberId) => {
          rosterEditingMemberListenersRef.current.get(memberId)?.forEach((listener) => listener());
        });
        rosterEditingOperationListenersRef.current.forEach((listener) => listener());
      },
      getSnapshot: () => rosterEditingVersionRef.current,
    }),
    [],
  );
  useEffect(() => {
    runtimeRef.current = runtime;
  }, [runtime]);
  useEffect(() => {
    const currentMemberIds = new Set(runtime.rosterMembers.map((member) => member.id));
    let changed = false;
    const removedMemberIds: string[] = [];
    selectedRosterIdsRef.current.forEach((memberId) => {
      if (!currentMemberIds.has(memberId)) {
        selectedRosterIdsRef.current.delete(memberId);
        removedMemberIds.push(memberId);
        changed = true;
      }
    });
    if (changed) {
      rosterSelectionStore.emit(removedMemberIds);
    }
  }, [runtime.rosterMembers, rosterSelectionStore]);
  const handleRosterDraftInputChange = useCallback(
    (memberId: string, field: keyof RosterMemberEditableDraft, value: string) => {
      editedRosterMembersRef.current = {
        ...editedRosterMembersRef.current,
        [memberId]: {
          ...(editedRosterMembersRef.current[memberId] ?? emptyRosterMemberDraft()),
          [field]: value,
        },
      };
    },
    [],
  );
  const handleToggleRosterStop = useCallback((member: OrderRosterMember) => {
    if (member.isStopped) {
      runtimeRef.current.updateRosterStop(member.id, false);
      return;
    }
    setRosterDeleteConfirmMember(member);
  }, []);
  const handleConfirmRosterDelete = useCallback(() => {
    if (!rosterDeleteConfirmMember) {
      return;
    }
    runtimeRef.current.updateRosterStop(rosterDeleteConfirmMember.id, true);
    setRosterDeleteConfirmMember(undefined);
  }, [rosterDeleteConfirmMember]);
  const handleOpenRosterBatchDelete = useCallback(() => {
    if (selectedRosterIdsRef.current.size === 0) {
      Message.warning("请先选择名单成员");
      return;
    }
    setRosterBatchDeleteConfirmVisible(true);
  }, []);
  const handleOpenRosterBatchAdjust = useCallback(() => {
    if (selectedRosterIdsRef.current.size === 0) {
      Message.warning("请先选择名单成员");
      return;
    }
    const selectedIds = selectedRosterIdsRef.current;
    setFeeAdjustmentRequestMembers(
      runtimeRef.current.rosterMembers.filter((member) => selectedIds.has(member.id)),
    );
  }, []);
  const handleConfirmRosterBatchDelete = useCallback(async () => {
    const selectedMemberIds = Array.from(selectedRosterIdsRef.current);
    if (selectedMemberIds.length === 0) {
      setRosterBatchDeleteConfirmVisible(false);
      return;
    }
    try {
      await runtimeRef.current.updateRosterStopsAsync(selectedMemberIds, true);
      selectedRosterIdsRef.current = new Set();
      rosterSelectionStore.emit(selectedMemberIds);
      setRosterBatchDeleteConfirmVisible(false);
      setRosterSaveErrorMessage("");
    } catch (error) {
      setRosterSaveErrorMessage(getErrorMessage(error, "批量删除名单成员失败，请稍后重试。"));
    }
  }, [rosterSelectionStore]);
  const handleToggleRosterSelection = useCallback(
    (memberId: string, checked: boolean) => {
      if (checked) {
        selectedRosterIdsRef.current.add(memberId);
      } else {
        selectedRosterIdsRef.current.delete(memberId);
        setRosterSelectScope(null);
      }
      rosterSelectionStore.emit([memberId]);
    },
    [rosterSelectionStore],
  );
  const handleRequestRosterSelectAll = useCallback(() => {
    if (visibleRosterMemberIdsRef.current.length === 0) {
      return;
    }
    setRosterSelectAllVisible(true);
  }, []);
  const handleClearVisibleRosterSelection = useCallback(() => {
    const selectedMemberIds = Array.from(selectedRosterIdsRef.current);
    selectedRosterIdsRef.current = new Set();
    setRosterSelectScope(null);
    rosterSelectionStore.emit(selectedMemberIds);
  }, [rosterSelectionStore]);
  const handleConfirmRosterSelectScope = useCallback(
    (scope: RosterSelectScope) => {
      const targetIds =
        scope === "page" ? currentPageMemberIdsRef.current : visibleRosterMemberIdsRef.current;
      targetIds.forEach((memberId) => selectedRosterIdsRef.current.add(memberId));
      setRosterSelectScope(scope);
      setRosterSelectAllVisible(false);
      rosterSelectionStore.emit(targetIds);
    },
    [rosterSelectionStore],
  );
  const handleOpenRosterMemberEditing = useCallback((member: OrderRosterMember) => {
    const previousEditingMemberIds = Array.from(editingRosterMemberIdsRef.current);
    editedRosterMembersRef.current = {
      [member.id]: buildRosterMemberDraft(member),
    };
    setRosterSaveErrorMessage("");
    editingRosterMemberIdsRef.current = new Set([member.id]);
    setEditingRosterCount(1);
    rosterEditingStore.emit([...previousEditingMemberIds, member.id]);
  }, [rosterEditingStore]);
  const handleOpenRosterBatchEditing = useCallback(() => {
    if (filteredRosterMembers.length === 0) {
      Message.warning("当前筛选结果暂无名单成员");
      return;
    }
    const previousEditingMemberIds = Array.from(editingRosterMemberIdsRef.current);
    editedRosterMembersRef.current = Object.fromEntries(
      filteredRosterMembers.map((member) => [member.id, buildRosterMemberDraft(member)]),
    );
    editingRosterMemberIdsRef.current = new Set(filteredRosterMembers.map((member) => member.id));
    setRosterSaveErrorMessage("");
    setEditingRosterCount(editingRosterMemberIdsRef.current.size);
    rosterEditingStore.emit([
      ...previousEditingMemberIds,
      ...filteredRosterMembers.map((member) => member.id),
    ]);
  }, [filteredRosterMembers, rosterEditingStore]);
  const handleCancelRosterMemberEditing = useCallback((member: OrderRosterMember) => {
    const nextEditingMemberIds = new Set(editingRosterMemberIdsRef.current);
    nextEditingMemberIds.delete(member.id);
    editingRosterMemberIdsRef.current = nextEditingMemberIds;
    setEditingRosterCount(nextEditingMemberIds.size);
    setRosterSaveErrorMessage("");
    const remainingDrafts = { ...editedRosterMembersRef.current };
    delete remainingDrafts[member.id];
    editedRosterMembersRef.current = remainingDrafts;
    rosterEditingStore.emit([member.id]);
  }, [rosterEditingStore]);
  const handleCancelRosterBatchEditing = useCallback(() => {
    const previousEditingMemberIds = Array.from(editingRosterMemberIdsRef.current);
    editingRosterMemberIdsRef.current = new Set();
    editedRosterMembersRef.current = {};
    setRosterSaveErrorMessage("");
    setEditingRosterCount(0);
    rosterEditingStore.emit(previousEditingMemberIds);
  }, [rosterEditingStore]);
  const handleSaveRosterMemberEditing = useCallback(async (member: OrderRosterMember) => {
    const draft = editedRosterMembersRef.current[member.id];
    if (!draft || !isRosterDraftChanged(member, draft)) {
      setRosterSaveErrorMessage("");
      const nextEditingMemberIds = new Set(editingRosterMemberIdsRef.current);
      nextEditingMemberIds.delete(member.id);
      editingRosterMemberIdsRef.current = nextEditingMemberIds;
      setEditingRosterCount(nextEditingMemberIds.size);
      const remainingDrafts = { ...editedRosterMembersRef.current };
      delete remainingDrafts[member.id];
      editedRosterMembersRef.current = remainingDrafts;
      rosterEditingStore.emit([member.id]);
      return;
    }
    const validationErrors = validateRosterMemberDraftChanges(member, draft);
    if (validationErrors.length > 0) {
      setRosterSaveErrorMessage(validationErrors.join("；"));
      return;
    }
    try {
      await runtimeRef.current.updateRosterMemberAsync(
        member.id,
        buildRosterMemberUpdatePayload(member, draft),
      );
      const remainingDrafts = { ...editedRosterMembersRef.current };
      delete remainingDrafts[member.id];
      editedRosterMembersRef.current = remainingDrafts;
      setRosterSaveErrorMessage("");
      const nextEditingMemberIds = new Set(editingRosterMemberIdsRef.current);
      nextEditingMemberIds.delete(member.id);
      editingRosterMemberIdsRef.current = nextEditingMemberIds;
      setEditingRosterCount(nextEditingMemberIds.size);
      rosterEditingStore.emit([member.id]);
    } catch (error) {
      setRosterSaveErrorMessage(getErrorMessage(error, "名单保存失败，请稍后重试。"));
    }
  }, [rosterEditingStore]);

  const handleOpenRosterCreateDrawer = useCallback(() => {
    rosterCreateForm.resetFields();
    rosterCreateForm.setFieldsValue({
      ...emptyRosterMemberDraft(),
      classGroupLabel: rosterExistingClassGroups.length === 1 ? rosterExistingClassGroups[0] : "",
    });
    setRosterSaveErrorMessage("");
    setRosterCreateErrorMessage("");
    setRosterCreateVisible(true);
  }, [rosterCreateForm, rosterExistingClassGroups]);

  const handleCloseRosterCreateDrawer = useCallback(() => {
    if (runtimeRef.current.rosterMemberCreating) {
      return;
    }
    setRosterCreateVisible(false);
    setRosterCreateErrorMessage("");
    rosterCreateForm.resetFields();
  }, [rosterCreateForm]);

  const handleSaveRosterCreate = useCallback(async () => {
    try {
      setRosterCreateErrorMessage("");
      const values = await rosterCreateForm.validate();
      await runtimeRef.current.createRosterMemberAsync(buildRosterMemberCreatePayload(values));
      setRosterSaveErrorMessage("");
      setRosterCreateErrorMessage("");
      setRosterCreateVisible(false);
      rosterCreateForm.resetFields();
    } catch (error) {
      if (isFormValidationError(error)) {
        return;
      }
      setRosterCreateErrorMessage(getErrorMessage(error, "名单成员新增失败，请稍后重试。"));
    }
  }, [rosterCreateForm]);

  const handleToggleSalesFinalize = useCallback(async (finalized: boolean) => {
    if (editingRosterMemberIdsRef.current.size > 0) {
      Message.warning("请先保存或取消名单编辑，再进行合同交接定稿");
      return;
    }
    if (finalized && runtimeRef.current.salesEditVisible) {
      Message.warning("请先保存或取消合同交接编辑，再进行合同交接定稿");
      return;
    }
    if (finalized) {
      const missing = collectSalesHandoverRequiredErrors({
        sales: runtimeRef.current.salesHandover,
        draft: {
          ...salesFormValuesRef.current,
          ...salesDraftValuesRef.current,
        },
        signType: runtimeRef.current.summary?.signType,
      });
      if (missing.length > 0) {
        const message = `合同交接定稿前请完善必填项：${missing.map((item) => item.label).join("、")}`;
        setSalesFinalizeErrorMessage(message);
        setSalesRequiredMissingFields(missing.map((item) => item.field));
        Message.warning(message);
        setSalesCollapsed(false);
        if (!runtimeRef.current.salesEditVisible) {
          runtimeRef.current.openSalesEdit();
        }
        window.setTimeout(() => {
          salesCardRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
          const firstField = missing[0]?.field;
          if (firstField) {
            const firstCell = salesCardRef.current?.querySelector(
              `[data-required-field="${firstField}"]`,
            );
            firstCell?.scrollIntoView?.({ behavior: "smooth", block: "center" });
          }
        }, 80);
        return;
      }
      setSalesRequiredMissingFields([]);
    }
    try {
      setSalesFinalizeErrorMessage("");
      await runtimeRef.current.updateSalesFinalizeAsync(finalized);
      setSalesFinalizeErrorMessage("");
      if (finalized) {
        setSalesCollapsed(false);
        setRouteTeamIdNeedsConfirm(true);
        window.setTimeout(() => {
          routeCellRef.current?.scrollIntoView?.({ behavior: "smooth", block: "center" });
        }, 80);
      } else {
        setRouteTeamIdNeedsConfirm(false);
      }
    } catch (error) {
      setSalesFinalizeErrorMessage(getErrorMessage(error, "合同交接定稿状态更新失败，请稍后重试。"));
    }
  }, []);

  const handleToggleRosterFinalize = useCallback(async (finalized: boolean) => {
    if (editingRosterMemberIdsRef.current.size > 0) {
      Message.warning("请先保存或取消名单编辑，再进行名单定稿");
      return;
    }
    try {
      setRosterFinalizeErrorMessage("");
      await runtimeRef.current.updateRosterFinalizeAsync(finalized);
      setRosterSaveErrorMessage("");
      setRosterFinalizeErrorMessage("");
    } catch (error) {
      setRosterFinalizeErrorMessage(getErrorMessage(error, "名单定稿状态更新失败，请稍后重试。"));
    }
  }, []);

  const handleApplyContract = useCallback(async () => {
    const scrollToSigningCard = () => {
      const card = document.getElementById("order-handover-contract-signing");
      card?.scrollIntoView?.({ behavior: "smooth", block: "start" });
    };
    try {
      setContractSigningErrorMessage("");
      await runtimeRef.current.applyContractAsync();
      setContractSigningErrorMessage("");
      Message.success("合同已生成，正在刷新签订进度");
      scrollToSigningCard();
    } catch (error) {
      setContractSigningErrorMessage(getErrorMessage(error, "合同签订申请失败，请稍后重试。"));
      // 409「签署中」等场景仍会触发 runtime 异步刷新；滚到卡片查看当前合同状态。
      scrollToSigningCard();
    }
  }, []);

  const handlePublishContract = useCallback(async () => {
    if (editingRosterMemberIdsRef.current.size > 0) {
      Message.warning("请先保存或取消名单编辑，再进行合同挂网");
      return;
    }
    try {
      setContractPublishErrorMessage("");
      setContractSigningErrorMessage("");
      await runtimeRef.current.publishContractAsync();
      setContractPublishErrorMessage("");
      Message.success("已挂网；线上合同已自动作废旧合同并生成新签署（如适用）");
    } catch (error) {
      setContractPublishErrorMessage(getErrorMessage(error, "合同挂网失败，请稍后重试。"));
    }
  }, []);

  const handleUnpublishContract = useCallback(async () => {
    try {
      setContractPublishErrorMessage("");
      await runtimeRef.current.unpublishContractAsync();
      setContractPublishErrorMessage("");
      Message.success(
        runtimeRef.current.contractSigning?.providerCode === "tourism_bureau"
          ? "已取消挂网；旅游局合同已作废"
          : "已取消挂网",
      );
      runtimeRef.current.refreshContractSigningAsync?.();
    } catch (error) {
      setContractPublishErrorMessage(getErrorMessage(error, "取消挂网失败，请稍后重试。"));
    }
  }, []);

  const handleUnpublishAllLocks = useCallback(async () => {
    try {
      setContractPublishErrorMessage("");
      await runtimeRef.current.unpublishAllLocksAsync();
      setContractPublishErrorMessage("");
      Message.success("已取消全部定稿（挂网、合同交接、名单、行程、核价）");
    } catch (error) {
      setContractPublishErrorMessage(getErrorMessage(error, "一键取消定稿失败，请稍后重试。"));
    }
  }, []);

  const handleOpenMemberFeeAdjustment = useCallback((member: OrderRosterMember) => {
    if (!onOpenMemberFeeAdjustment || !canAccessFeeAdjustment || !feeAdjustmentAvailable) return;
    const current = runtimeRef.current;
    const content = pageRef.current?.closest<HTMLElement>("[data-testid='admin-content-shell']");
    const table = pageRef.current?.querySelector<HTMLElement>(".order-handover-roster-table-wrap .arco-table-body");
    onOpenMemberFeeAdjustment(member.id, feeAdjustmentRosterId, {
      keyword: current.rosterKeyword, type: current.rosterTypeFilter, classGroup: current.rosterClassGroupFilter, stopped: current.rosterStoppedFilter,
      page: rosterPage, pageSize: rosterPageSize, customerQuoteId,
      windowScrollTop: window.scrollY, contentScrollTop: content?.scrollTop ?? 0, tableScrollTop: table?.scrollTop ?? 0, tableScrollLeft: table?.scrollLeft ?? 0,
    });
  }, [onOpenMemberFeeAdjustment, canAccessFeeAdjustment, feeAdjustmentAvailable, feeAdjustmentRosterId, rosterPage, rosterPageSize, customerQuoteId]);

  const rosterColumns = useMemo(
    () =>
      buildRosterColumns({
        editingMemberIdsRef: editingRosterMemberIdsRef,
        editingStore: rosterEditingStore,
        majorOptions: rosterClassGroupOptions,
        travelerTypeOptions: rosterTravelerTypeOptions,
        teacherTypeOptions: rosterTeacherTypeOptions,
        saving: runtime.rosterMemberSaving,
        selectedIdsRef: selectedRosterIdsRef,
        selectionStore: rosterSelectionStore,
        visibleMemberIdsRef: visibleRosterMemberIdsRef,
        locked: rosterStructureReadOnly,
        currentPageMemberIdsRef,
        selectScopeRef: rosterSelectScopeRef,
        feeAdjustmentRecords: feeAdjustmentsQuery.data ?? [],
        feeAdjustmentAvailable,
        showFeeAdjustment: canAccessFeeAdjustment,
        onOpenMemberFeeAdjustment: handleOpenMemberFeeAdjustment,
        onToggleSelection: handleToggleRosterSelection,
        onRequestSelectAll: handleRequestRosterSelectAll,
        onClearVisibleSelection: handleClearVisibleRosterSelection,
        onDraftInputChange: handleRosterDraftInputChange,
        onOpenEdit: handleOpenRosterMemberEditing,
        onCancelEdit: handleCancelRosterMemberEditing,
        onSaveEdit: handleSaveRosterMemberEditing,
        onToggleStop: handleToggleRosterStop,
        onClearMemberFlag: handleClearMemberFlag,
        onClearAllMemberFlags: handleClearAllMemberFlags,
      }),
    [
      handleRosterDraftInputChange,
      handleCancelRosterMemberEditing,
      handleClearVisibleRosterSelection,
      handleClearAllMemberFlags,
      handleClearMemberFlag,
      handleOpenRosterMemberEditing,
      canAccessFeeAdjustment,
      handleOpenMemberFeeAdjustment,
      feeAdjustmentAvailable,
      feeAdjustmentsQuery.data,
      handleSaveRosterMemberEditing,
      handleToggleRosterSelection,
      handleToggleRosterStop,
      rosterClassGroupOptions,
      rosterTravelerTypeOptions,
      rosterTeacherTypeOptions,
      rosterEditingStore,
      rosterSelectionStore,
      rosterStructureReadOnly,
      runtime.rosterMemberSaving,
    ],
  );
  useEffect(() => {
    if (!runtime.salesEditVisible) {
      setSalesInlineDraftValues((current) => (Object.keys(current).length === 0 ? current : {}));
      return;
    }
    syncingSalesFormRef.current = true;
    form.setFieldsValue(salesDraftValues);
    salesDraftValuesRef.current = salesDraftValues;
    setSalesInlineDraftValues((current) => (Object.keys(current).length === 0 ? current : {}));
    syncingSalesFormRef.current = false;
  }, [form, runtime.salesEditVisible, salesDraftValues]);
  useEffect(() => {
    if (runtime.pendingItinerary) {
      setSalesCollapsed(false);
    }
  }, [runtime.pendingItinerary]);

  /** 保存销售交接表单，并把名单主签合同人同步为销售交接签定人快照。 */
  async function handleSaveSalesHandover(values: SalesHandoverFormValues) {
    const mergedValues: SalesHandoverFormValues = {
      ...values,
      ...salesDraftValuesRef.current,
    };
    const selectedMainSigner = getRosterMemberById(
      runtime.rosterMembers,
      mergedValues.contractSignerMemberId,
    );
    const contractSignerNames = selectedMainSigner?.name ?? mergedValues.contractSignerNames;
    const payload = normalizeSalesHandoverFormValues({
      ...mergedValues,
      contractSignerNames,
    });
    const signType = runtime.summary?.signType?.trim();
    if (signType === "单订车" || signType === "单订房") {
      const pricingLines = filterSingleOrderPricingLines(runtime.orderLines, signType);
      const saleDraft = buildSingleOrderSalePriceDraft(
        pricingLines,
        mergedValues.singleOrderPricingLines,
      );
      const invalidSale = pricingLines.find((line) => {
        const raw = saleDraft[line.id] ?? "";
        return raw.trim() === "" || Number.isNaN(Number(raw));
      });
      if (invalidSale) {
        Message.error("请填写有效的订车/订房销售价（数字，可为空时填0）");
        return;
      }
      const feeRaw = mergedValues.singleOrderServiceFee ?? "0";
      if (feeRaw.trim() === "" || Number.isNaN(Number(feeRaw))) {
        Message.error("请填写有效的单订服务费（数字，可为0）");
        return;
      }
      payload.singleOrderServiceFee = feeRaw;
      payload.singleOrderPricingLines = pricingLines.map((line) => ({
        id: line.id,
        salesPriceAmount: saleDraft[line.id] ?? "0",
      }));
    }
    const currentMainSignerIds = runtime.rosterMembers
      .filter((member) => member.isMainSigner)
      .map((member) => member.id);
    const mainSignerIdsToClear = currentMainSignerIds.filter(
      (memberId) => memberId !== selectedMainSigner?.id,
    );
    if (mainSignerIdsToClear.length > 0) {
      await runtime.updateRosterFlagsAsync({
        memberIds: mainSignerIdsToClear,
        isMainSigner: false,
      });
    }
    if (selectedMainSigner && !selectedMainSigner.isMainSigner) {
      await runtime.updateRosterFlagsAsync({
        memberIds: [selectedMainSigner.id],
        isMainSigner: true,
      });
    }
    await runtime.savePendingItinerary();
    runtime.saveSalesHandover({
      ...payload,
      contractSignerNames,
      serviceConsultantNameSnapshot:
        getStaffDisplayName(
          serviceConsultantSelectOptions,
          payload.serviceConsultantUserId,
        ) ??
        payload.serviceConsultantNameSnapshot,
      catchOrderUserNameSnapshot:
        getStaffDisplayName(catchOrderSelectOptions, payload.catchOrderUserId) ??
        payload.catchOrderUserNameSnapshot,
    });
  }

  /** 放弃本次编辑，恢复为后端最近一次返回的销售交接数据。 */
  function handleCancelSalesHandoverEdit() {
    form.resetFields();
    form.setFieldsValue(salesFormValues);
    salesDraftValuesRef.current = salesFormValues;
    setSalesInlineDraftValues({});
    handleActiveSalesEditorField(undefined);
    setSalesRequiredMissingFields([]);
    runtime.closeSalesEdit();
  }

  /** 批量切换选中名单成员标记：已全部具备该标记时再次点击则取消。 */
  async function handleRosterFlag(action: "signer" | "hidden" | "linkman" | "mainSigner") {
    const selectedMemberIds = Array.from(selectedRosterIdsRef.current);
    if (selectedMemberIds.length === 0) {
      Message.warning("请先选择名单成员");
      return;
    }
    const selectedMembers = runtime.rosterMembers.filter((member) =>
      selectedMemberIds.includes(member.id),
    );
    if (selectedMembers.length === 0) {
      Message.warning("请先选择名单成员");
      return;
    }
    if (action === "mainSigner" && selectedMemberIds.length !== 1) {
      Message.warning("主签合同人只能选择1人");
      return;
    }
    if (
      (action === "signer" || action === "linkman" || action === "mainSigner") &&
      selectedMembers.some((member) => !member.isShowInContract)
    ) {
      const isClearing =
        (action === "signer" && selectedMembers.every((member) => member.isSigner)) ||
        (action === "linkman" && selectedMembers.every((member) => member.isTeamLinkman)) ||
        (action === "mainSigner" && selectedMembers[0]?.isMainSigner);
      if (!isClearing) {
        Message.warning("已标记不进合同，请先取消后再标记签合同、主要联系人或主签合同人");
        return;
      }
    }

    setRosterFlagAction(action);
    try {
      if (action === "mainSigner") {
        const mainSignerMemberId = selectedMemberIds[0];
        if (!mainSignerMemberId) {
          Message.warning("请先选择名单成员");
          return;
        }
        const selectedMember = selectedMembers[0];
        if (selectedMember?.isMainSigner) {
          await runtime.updateRosterFlagsAsync({
            memberIds: [mainSignerMemberId],
            isMainSigner: false,
          });
          Message.success("已取消主签合同人");
          return;
        }
        const mainSignerIdsToClear = runtime.rosterMembers
          .filter((member) => member.isMainSigner && member.id !== mainSignerMemberId)
          .map((member) => member.id);
        if (mainSignerIdsToClear.length > 0) {
          await runtime.updateRosterFlagsAsync({
            memberIds: mainSignerIdsToClear,
            isMainSigner: false,
          });
        }
        await runtime.updateRosterFlagsAsync({
          memberIds: [mainSignerMemberId],
          isSigner: true,
          isMainSigner: true,
        });
        Message.success("已设为主签合同人");
        return;
      }

      if (action === "signer") {
        const shouldClear = selectedMembers.every((member) => member.isSigner);
        await runtime.updateRosterFlagsAsync({
          memberIds: selectedMemberIds,
          isSigner: !shouldClear,
          ...(shouldClear ? { isMainSigner: false } : {}),
        });
        Message.success(shouldClear ? "已取消签合同标记" : "已标记签合同");
        return;
      }

      if (action === "hidden") {
        const shouldClear = selectedMembers.every((member) => !member.isShowInContract);
        await runtime.updateRosterFlagsAsync(
          shouldClear
            ? {
                memberIds: selectedMemberIds,
                isShowInContract: true,
              }
            : {
                memberIds: selectedMemberIds,
                isShowInContract: false,
                isSigner: false,
                isTeamLinkman: false,
                isMainSigner: false,
              },
        );
        Message.success(shouldClear ? "已恢复进合同展示" : "已标记不进合同");
        return;
      }

      const shouldClear = selectedMembers.every((member) => member.isTeamLinkman);
      await runtime.updateRosterFlagsAsync({
        memberIds: selectedMemberIds,
        isTeamLinkman: !shouldClear,
      });
      Message.success(shouldClear ? "已取消主要联系人标记" : "已标记主要联系人");
    } catch (error) {
      Message.error(getErrorMessage(error, "名单标记更新失败，请稍后重试。"));
    } finally {
      setRosterFlagAction(null);
    }
  }

  /** 标记列单标签移除，不依赖批量勾选。 */
  async function handleClearMemberFlag(
    member: OrderRosterMember,
    flag: "signer" | "hidden" | "linkman" | "mainSigner",
  ) {
    if (rosterStructureReadOnly) {
      return;
    }
    try {
      if (flag === "signer") {
        await runtime.updateRosterFlagsAsync({
          memberIds: [member.id],
          isSigner: false,
          isMainSigner: false,
        });
        Message.success("已移除签合同标记");
        return;
      }
      if (flag === "hidden") {
        await runtime.updateRosterFlagsAsync({
          memberIds: [member.id],
          isShowInContract: true,
        });
        Message.success("已恢复进合同展示");
        return;
      }
      if (flag === "linkman") {
        await runtime.updateRosterFlagsAsync({
          memberIds: [member.id],
          isTeamLinkman: false,
        });
        Message.success("已移除主要联系人标记");
        return;
      }
      await runtime.updateRosterFlagsAsync({
        memberIds: [member.id],
        isMainSigner: false,
      });
      Message.success("已移除主签合同人标记");
    } catch (error) {
      Message.error(getErrorMessage(error, "名单标记更新失败，请稍后重试。"));
    }
  }

  /** 标记列一键清空该成员全部合同标签。 */
  async function handleClearAllMemberFlags(member: OrderRosterMember) {
    if (rosterStructureReadOnly) {
      return;
    }
    try {
      await runtime.updateRosterFlagsAsync({
        memberIds: [member.id],
        isSigner: false,
        isShowInContract: true,
        isTeamLinkman: false,
        isMainSigner: false,
      });
      Message.success("已清除全部标签");
    } catch (error) {
      Message.error(getErrorMessage(error, "名单标记更新失败，请稍后重试。"));
    }
  }

  if (!selected) {
    return (
      <main className="order-handover-page">
        <Card className="order-handover-summary-card" bordered={false} title="订单信息">
          {runtime.detailLoading ? (
            <div className="order-handover-detail-loading" aria-label="正在加载中">
              <Spin size={24} />
              <span>正在加载中</span>
            </div>
          ) : runtime.detailErrorMessage ? (
            <Alert type="warning" content={runtime.detailErrorMessage} showIcon />
          ) : (
            <Alert type="info" content="当前项目暂无可展示行程，请先在需求定制中生成行程。" showIcon />
          )}
        </Card>
      </main>
    );
  }

  const summaryDateRange = pendingItinerary
    ? `${selected.dateRange} (${selected.days}天)`
    : runtime.summary?.dateRange ?? `${selected.dateRange} (${selected.days}天)`;
  const summaryPeopleText = pendingItinerary
    ? formatItineraryPeople(selected)
    : runtime.summary?.peopleText ?? formatItineraryPeople(selected);
  const summaryProjectTitle = pendingItinerary
    ? selected.title
    : runtime.summary?.projectTitle ?? selected.title;

  return (
    <main className="order-handover-page" ref={pageRef}>
      <Card className="order-handover-summary-card" bordered={false} title="订单信息">
        <div className="order-handover-summary-grid">
          <SummaryMetric label="订单号" value={runtime.orderNo.replace("CUST", "ORD")} />
          <SummaryMetric label="签单类型" value={signType} />
          <SummaryMetric label="合同" value={runtime.summary?.contractNo ?? "待生成"} tone="link" />
          <SummaryMetric
            label="团编码"
            value={
              runtime.summary?.teamId ??
              (salesHandoverFinalized || contractPublished ? "待生成" : "待定稿生成")
            }
            tone="link"
          />
          <SummaryMetric label="出行" value={summaryDateRange} />
          <SummaryMetric label="人数" value={summaryPeopleText} tone="people" />
        </div>
        <div className="order-handover-summary-line">
          <span>来源</span>
          <Tag color="magenta">{runtime.summary?.sourceText ?? "需求定制"}</Tag>
          <span>|</span>
          <span>项目</span>
          <strong>{summaryProjectTitle}</strong>
        </div>
        <div className="order-handover-summary-line">
          <span>票务派生</span>
          <span>{runtime.summary?.ticketSyncNote ?? "合同交接表定稿后，团队主名单同步到高铁票务详情"}</span>
          <span className="order-handover-link-placeholder" aria-disabled="true">
            查看高铁票务工作台
          </span>
          <span className="order-handover-link-placeholder" aria-disabled="true">
            查看高铁票务详情
          </span>
        </div>
        {runtime.detailErrorMessage ? (
          <Alert type="warning" content={runtime.detailErrorMessage} showIcon />
        ) : null}
      </Card>

      <Card className="order-handover-customer-quote-card" bordered title="成交报价">
        <div className="order-handover-customer-quote-route">
          <span>行程线路</span>
          <div className="order-handover-customer-quote-route-main is-inline">
            <button
              type="button"
              className={`order-handover-customer-quote-route-title${
                runtime.itinerarySelecting ? " is-loading" : ""
              }${contractHandoverLocked ? " is-locked" : ""}`}
              aria-label={`切换行程：${selected.title}`}
              aria-busy={runtime.itinerarySelecting || undefined}
              disabled={contractHandoverLocked || runtime.itinerarySelecting}
              onClick={() => {
                setRouteTeamIdNeedsConfirm(false);
                runtime.openItineraryDrawer();
              }}
            >
              {runtime.itinerarySelecting ? (
                <Spin size={14} />
              ) : contractHandoverLocked ? null : (
                <ArrowLeftRight size={14} aria-hidden strokeWidth={2.25} />
              )}
              <strong>{selected.title}</strong>
              {!contractHandoverLocked && !runtime.itinerarySelecting ? (
                <em className="order-handover-customer-quote-route-switch">切换</em>
              ) : null}
            </button>
            {selected.version ? <Tag color="arcoblue">{selected.version}</Tag> : null}
            <span>{selected.dateRange || "待选择日期"}</span>
          </div>
        </div>
        <div className="order-handover-customer-quote-layout">
          <label>
            <span>成交报价版本</span>
            <select
              aria-label="成交报价版本"
              value={customerQuoteId}
              disabled={runtime.customerQuoteOptionsLoading || runtime.itinerarySelecting}
              onChange={(event) => {
                setCustomerQuoteAdoptErrorMessage("");
                setCustomerQuoteId(event.target.value);
              }}
            >
              <option value="">请选择已定稿报价</option>
              {runtime.customerQuoteOptions.map((option) => (
                <option value={option.customerQuoteId} key={option.customerQuoteId}>
                  V{option.versionNo} · {option.versionName}
                </option>
              ))}
            </select>
          </label>
          {runtime.itinerarySelecting ? (
            <div className="order-handover-customer-quote-loading" aria-label="正在加载成交报价">
              <Spin size={20} />
              <span>正在加载成交报价…</span>
            </div>
          ) : customerQuote ? (
            <div className="order-handover-customer-quote-summary">
              <div className="is-student-price">
                <span>学生人均</span>
                <strong>{formatCustomerQuoteUnitPrice(customerQuote.studentUnitPrice)}</strong>
              </div>
              <div className="is-teacher-price">
                <span>老师人均</span>
                <strong>{formatCustomerQuoteUnitPrice(customerQuote.teacherUnitPrice)}</strong>
              </div>
              <div className="is-total">
                <span>标准报价总额</span>
                <strong>{formatCustomerQuoteAmount(customerQuote.standardQuoteTotalAmount)}</strong>
              </div>
              <div className="is-student-count">
                <span>学生人数</span>
                <strong>{customerQuote.studentCount} 人</strong>
              </div>
              <div className="is-teacher-count">
                <span>老师人数</span>
                <strong>{customerQuote.teacherCount} 人</strong>
              </div>
            </div>
          ) : (
            <span className="order-handover-muted">当前签约行程暂无已定稿报价</span>
          )}
          <div className="order-handover-customer-quote-adopt">
            <Button
              type="primary"
              loading={runtime.customerQuoteSelecting}
              disabled={
                Boolean(customerQuoteAdoptBlockedReason) ||
                !customerQuoteId ||
                runtime.itinerarySelecting
              }
              onClick={() => {
                if (!customerQuote) {
                  return;
                }
                setCustomerQuoteAdoptErrorMessage("");
                void runtime.selectCustomerQuote(customerQuoteId);
              }}
            >
              采用成交报价
            </Button>
            {customerQuoteAdoptBlockedReason || !customerQuoteId ? (
              <Tooltip
                content={customerQuoteAdoptBlockedReason || "请先选择已定稿的成交报价"}
                trigger={["hover", "focus"]}
                position="bottom"
              >
                <button
                  type="button"
                  className="order-handover-customer-quote-warning"
                  aria-label={`采用成交报价不可用：${customerQuoteAdoptBlockedReason || "请先选择已定稿的成交报价"}`}
                >
                  <TriangleAlert size={16} aria-hidden="true" />
                </button>
              </Tooltip>
            ) : null}
          </div>
          <div className="order-handover-customer-quote-links">
            {customerQuote && runtime.detail?.orderId && feeAdjustmentRosterId ? (
              <a
                className="order-handover-customer-quote-link"
                href={`/quote/customer-quotes/${customerQuote.customerQuoteId}?orderId=${encodeURIComponent(runtime.detail.orderId)}&rosterId=${encodeURIComponent(feeAdjustmentRosterId)}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                查看行程报价
              </a>
            ) : null}
            <a
              className="order-handover-customer-quote-link"
              href={`/supply-chain/fulfillment-orders/itineraries/${encodeURIComponent(selected.id)}/enter`}
              target="_blank"
              rel="noopener noreferrer"
            >
              查看确认件
            </a>
          </div>
        </div>
        {customerQuoteAdoptErrorMessage || runtime.customerQuoteErrorMessage ? (
          <Alert
            className="order-handover-inline-alert"
            type="error"
            content={customerQuoteAdoptErrorMessage || runtime.customerQuoteErrorMessage}
            showIcon
          />
        ) : null}
      </Card>

      <Card
        className="order-handover-check-card order-handover-roster-finalize-card"
        bordered
        data-testid="order-handover-roster-finalize-card"
      >
        <div className="order-handover-check-title">定稿检查（挂网前置条件）</div>
        <ol className="order-handover-check-steps" aria-label="定稿检查进度">
          {finalizeCheckSteps.map((step, index) => {
            const stateClass =
              step.done ? "is-done" : index === finalizeCheckCurrentIndex ? "is-current" : "is-pending";
            return (
              <li key={step.key} className={`order-handover-check-step ${stateClass}`}>
                <span className="order-handover-check-step-dot" aria-hidden="true">
                  {step.done ? "✓" : index + 1}
                </span>
                <span className="order-handover-check-step-text">{step.text}</span>
              </li>
            );
          })}
        </ol>
        {salesFinalizeErrorMessage ? (
          <Alert
            className="order-handover-inline-alert"
            type="error"
            content={salesFinalizeErrorMessage}
            showIcon
          />
        ) : null}
        {rosterFinalizeErrorMessage ? (
          <Alert
            className="order-handover-inline-alert"
            type="error"
            content={rosterFinalizeErrorMessage}
            showIcon
          />
        ) : null}
        {contractPublishErrorMessage ? (
          <Alert
            className="order-handover-inline-alert"
            type="error"
            content={contractPublishErrorMessage}
            showIcon
          />
        ) : null}
        <div className="order-handover-check-footer">
          <div className="order-handover-check-button-group">
            {contractPublished ? (
              <>
                <Popconfirm
                  title={unpublishConfirmTitle}
                  okText="确认取消挂网"
                  cancelText="返回"
                  onOk={() => handleUnpublishContract()}
                >
                  <Button
                    className="order-handover-finalize-button"
                    type="outline"
                    status="warning"
                    loading={runtime.contractUnpublishing}
                    disabled={!canManage || runtime.contractUnpublishingAll}
                  >
                    取消挂网
                  </Button>
                </Popconfirm>
                <Popconfirm
                  title={unpublishAllConfirmTitle}
                  okText="确认全部取消"
                  cancelText="返回"
                  onOk={() => handleUnpublishAllLocks()}
                >
                  <Button
                    className="order-handover-finalize-button"
                    type="outline"
                    status="danger"
                    loading={runtime.contractUnpublishingAll}
                    disabled={!canManage || runtime.contractUnpublishing}
                  >
                    取消全部定稿
                  </Button>
                </Popconfirm>
              </>
            ) : (
              <>
                <Button
                  className={
                    rosterFinalized
                      ? "order-handover-finalize-button is-secondary"
                      : "order-handover-finalize-button"
                  }
                  type={rosterFinalized || !canFinalizeRoster ? "outline" : "primary"}
                  status={rosterFinalized ? "warning" : undefined}
                  loading={runtime.rosterFinalizing}
                  disabled={
                    !canManage
                    || isRosterEditing
                    || (!rosterFinalized && !canFinalizeRoster)
                    // 合同交接已定稿后，须先取消合同定稿，再取消名单（对齐步骤回退）
                    || (rosterFinalized && salesHandoverFinalized)
                  }
                  onClick={() => void handleToggleRosterFinalize(!rosterFinalized)}
                >
                  {rosterFinalized ? "名单取消定稿" : "名单定稿"}
                </Button>
                <Button
                  className={
                    salesHandoverFinalized
                      ? "order-handover-finalize-button is-secondary"
                      : "order-handover-finalize-button"
                  }
                  type={salesHandoverFinalized || !rosterFinalized ? "outline" : "primary"}
                  status={salesHandoverFinalized ? "warning" : undefined}
                  loading={runtime.salesFinalizing}
                  disabled={
                    !canManage
                    || isRosterEditing
                    || (!salesHandoverFinalized && !rosterFinalized)
                  }
                  onClick={() => void handleToggleSalesFinalize(!salesHandoverFinalized)}
                >
                  {salesHandoverFinalized ? "取消合同定稿" : "合同交接定稿"}
                </Button>
                <Button
                  className="order-handover-finalize-button"
                  type={salesHandoverFinalized && rosterFinalized ? "primary" : "outline"}
                  loading={runtime.contractPublishing}
                  disabled={!canManage || isRosterEditing || !salesHandoverFinalized || !rosterFinalized}
                  icon={<FileSignature size={16} />}
                  onClick={() => void handlePublishContract()}
                >
                  合同挂网
                </Button>
              </>
            )}
          </div>
          <span className="order-handover-check-hint">
            {finalizeHint}
          </span>
        </div>
      </Card>

      {showContractSigningCard ? (
        <ContractSigningCard
          canFinalize={canManage}
          applying={runtime.contractApplying}
          contractSigning={contractSigning}
          errorMessage={contractSigningErrorMessage}
          onApply={handleApplyContract}
          refreshingInvites={runtime.contractSignInviting}
          resendingSignRecordId={resendingSignRecordId}
          onRefreshInvites={() => {
            void (async () => {
              try {
                setContractSigningErrorMessage("");
                await runtimeRef.current.issueSignInvitationsAsync();
              } catch (error) {
                setContractSigningErrorMessage(
                  getErrorMessage(error, "刷新签署链接失败，请稍后重试。"),
                );
              }
            })();
          }}
          onResendSms={(signRecordId) => {
            void (async () => {
              try {
                setContractSigningErrorMessage("");
                setResendingSignRecordId(signRecordId);
                await runtimeRef.current.resendSignSmsAsync(signRecordId);
              } catch (error) {
                setContractSigningErrorMessage(
                  getErrorMessage(error, "重发短信失败。未启用短信时可复制签署链接发给签约人。"),
                );
              } finally {
                setResendingSignRecordId(undefined);
              }
            })();
          }}
        />
      ) : null}

      <Card
        className="order-handover-roster-card"
        bordered
      >
        <div className="order-handover-roster-header" data-testid="order-handover-roster-header">
          <div className="order-handover-roster-toolbar">
            <button
              type="button"
              className={`order-handover-roster-title is-toggle${
                rosterFiltersOpen ? " is-open" : ""
              }${hasActiveRosterFilters(runtime) ? " has-active-filters" : ""}`}
              aria-expanded={rosterFiltersOpen}
              aria-controls="order-handover-roster-filters"
              data-testid="order-handover-roster-title-toggle"
              onClick={() => setRosterFiltersOpen((open) => !open)}
            >
              <Users size={18} />
              <span>出行名单</span>
              <Tag color="arcoblue">{rosterPeopleText}</Tag>
              <span className="order-handover-roster-filter-hint">
                {rosterFiltersOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                <span>{rosterFiltersOpen ? "收起筛选" : "筛选"}</span>
                {hasActiveRosterFilters(runtime) ? (
                  <Tag size="small" color="orangered">
                    已筛
                  </Tag>
                ) : null}
              </span>
            </button>
            <div
              className={buildRosterActionsClassName(editingRosterCount > 0)}
              data-testid="order-handover-roster-actions"
            >
              <div className="order-handover-roster-action-group is-primary">
                <Button
                  icon={<UploadIcon size={16} />}
                  disabled={rosterStructureReadOnly}
                  onClick={onOpenRosterImport}
                >
                  导入名单
                </Button>
              </div>
              <Text className="order-handover-muted order-handover-roster-stats">
                {formatRosterState(runtime.rosterStats)}
              </Text>
              <span className="order-handover-roster-action-divider" aria-hidden />
              <div className="order-handover-roster-action-group is-secondary">
                <Dropdown
                  trigger="click"
                  droplist={
                    <Menu>
                      <Menu.Item key="standard" onClick={() => handleRosterExport("standard")}>
                        导出出行名单
                      </Menu.Item>
                      <Menu.Item key="guide" onClick={() => handleRosterExport("guide")}>
                        导出导游版
                      </Menu.Item>
                      <Menu.Item key="template" onClick={() => handleRosterExport("template")}>
                        下载模板
                      </Menu.Item>
                    </Menu>
                  }
                >
                  <Button icon={<Download size={16} />} loading={runtime.rosterExporting}>
                    导出出行名单
                  </Button>
                </Dropdown>
                <Button
                  type="outline"
                  className="order-handover-roster-batch-adjust"
                  icon={<SlidersHorizontal size={14} />}
                  disabled={!feeAdjustmentAvailable || !canCreateFeeAdjustment}
                  onClick={handleOpenRosterBatchAdjust}
                >
                  费用调整
                </Button>
                {editingRosterCount > 0 ? (
                  <Button
                    type="outline"
                    icon={<X size={16} />}
                    disabled={runtime.rosterMemberSaving}
                    onClick={handleCancelRosterBatchEditing}
                  >
                    取消编辑
                  </Button>
                ) : (
                  <Button
                    type="outline"
                    icon={<PenLine size={16} />}
                    disabled={rosterStructureReadOnly}
                    onClick={handleOpenRosterBatchEditing}
                  >
                    编辑名单
                  </Button>
                )}
                <Button
                  type="primary"
                  className="order-handover-roster-create-button"
                  icon={<Plus size={16} />}
                  disabled={rosterStructureReadOnly}
                  onClick={handleOpenRosterCreateDrawer}
                >
                  新增名单成员
                </Button>
              </div>
            </div>
          </div>
          {rosterFiltersOpen ? (
            <div
              id="order-handover-roster-filters"
              className="order-handover-roster-filters"
              data-testid="order-handover-roster-filters"
            >
              <Input.Search
                className="order-handover-roster-search"
                allowClear
                placeholder="姓名/电话/证件号"
                value={runtime.rosterKeyword}
                onChange={runtime.setRosterKeyword}
              />
              <Select
                className="order-handover-roster-filter"
                value={runtime.rosterTypeFilter}
                onChange={runtime.setRosterTypeFilter}
                aria-label="人员类型"
                data-testid="roster-filter-traveler-type"
              >
                {rosterTypeOptions.map((option) => (
                  <Select.Option key={option} value={option}>
                    {option === "全部" ? "全部人员类型" : option}
                  </Select.Option>
                ))}
              </Select>
              <Select
                className="order-handover-roster-filter"
                value={runtime.rosterClassGroupFilter}
                onChange={runtime.setRosterClassGroupFilter}
                aria-label="班级类型"
                data-testid="roster-filter-class-group"
              >
                {rosterClassGroupFilterOptions.map((option) => (
                  <Select.Option key={option} value={option}>
                    {option === "全部" ? "全部班级类型" : option}
                  </Select.Option>
                ))}
              </Select>
              <Select
                className="order-handover-roster-filter"
                value={runtime.rosterStoppedFilter}
                onChange={runtime.setRosterStoppedFilter}
                aria-label="名单状态"
                data-testid="roster-filter-stopped"
              >
                <Select.Option value="active">有效</Select.Option>
                <Select.Option value="stopped">已删除</Select.Option>
                <Select.Option value="all">全部</Select.Option>
              </Select>
            </div>
          ) : null}
        </div>
        {rosterLocked ? (
          <Alert
            className="order-handover-roster-save-alert"
            type="warning"
            content={rosterReadOnlyMessage}
            showIcon
          />
        ) : null}
        {selectedRosterCount > 0 ? (
          <div className="order-handover-roster-bulkbar" data-testid="order-handover-roster-bulkbar">
            <span>
              {`已选 ${selectedRosterCount} 人（${
                rosterSelectScope === "page"
                  ? "仅当前页"
                  : rosterSelectScope === "all"
                    ? "全部筛选结果，含全部页"
                    : "手动勾选"
              }）`}
            </span>
            <Button
              size="small"
              disabled={
                rosterStructureReadOnly ||
                selectedHasHiddenContract ||
                runtime.rosterFlagsSaving ||
                runtime.rosterStopSaving ||
                runtime.rosterMemberSaving
              }
              loading={rosterFlagAction === "signer"}
              onClick={() => void handleRosterFlag("signer")}
            >
              标记签合同
            </Button>
            <Button
              size="small"
              disabled={
                rosterStructureReadOnly ||
                runtime.rosterFlagsSaving ||
                runtime.rosterStopSaving ||
                runtime.rosterMemberSaving
              }
              loading={rosterFlagAction === "hidden"}
              onClick={() => void handleRosterFlag("hidden")}
            >
              标记不进合同
            </Button>
            <Button
              size="small"
              disabled={
                rosterStructureReadOnly ||
                selectedHasHiddenContract ||
                runtime.rosterFlagsSaving ||
                runtime.rosterStopSaving ||
                runtime.rosterMemberSaving
              }
              loading={rosterFlagAction === "linkman"}
              onClick={() => void handleRosterFlag("linkman")}
            >
              标记主要联系人
            </Button>
            <Button
              size="small"
              disabled={
                rosterStructureReadOnly ||
                selectedHasHiddenContract ||
                runtime.rosterFlagsSaving ||
                runtime.rosterStopSaving ||
                runtime.rosterMemberSaving
              }
              loading={rosterFlagAction === "mainSigner"}
              onClick={() => void handleRosterFlag("mainSigner")}
            >
              设为主签合同人
            </Button>
            <Button
              size="small"
              status="danger"
              disabled={
                rosterStructureReadOnly ||
                runtime.rosterFlagsSaving ||
                runtime.rosterStopSaving ||
                runtime.rosterMemberSaving
              }
              onClick={handleOpenRosterBatchDelete}
            >
              批量删除
            </Button>
          </div>
        ) : null}
        {rosterSaveErrorMessage ? (
          <Alert
            className="order-handover-roster-save-alert"
            type="error"
            content={rosterSaveErrorMessage}
            showIcon
          />
        ) : null}
        <div
          className={`order-handover-roster-table-wrap${
            runtime.rosterFlagsSaving || runtime.rosterStopSaving || runtime.rosterMemberSaving
              ? " is-busy"
              : ""
          }`}
        >
          <MemoizedRosterTable
            columns={rosterColumns}
            data={filteredRosterMembers}
            loading={
              runtime.rosterFlagsSaving || runtime.rosterStopSaving || runtime.rosterMemberSaving
            }
            pagination={rosterPagination}
            onPaginationChange={(current, pageSize) => {
              setRosterPage(current);
              setRosterPageSize(pageSize);
            }}
          />
        </div>
        <div className="order-handover-roster-footer">
          {canAccessFeeAdjustment && feeAdjustmentAvailable ? (
            <>
              <RosterSummaryItem label="冻结基准应收合计" value={formatFeeAdjustmentMoney(frozenRosterReceivableTotal)} tone="danger" />
              <RosterSummaryItem label="费用调整合计" value={formatFeeAdjustmentMoney(feeAdjustmentTotal)} tone="warning" />
              <RosterSummaryItem label="当前个人应收合计" value={formatFeeAdjustmentMoney(currentRosterReceivableTotal)} tone="danger" />
            </>
          ) : null}
          <RosterSummaryItem
            label="住宿汇总"
            value={summarizeRosterAccommodation(runtime.roster, runtime.rosterMembers)}
          />
          <RosterSummaryItem
            label="车辆汇总"
            value={summarizeRosterVehicle(runtime.roster, runtime.rosterMembers)}
          />
          <RosterSummaryItem
            label="特殊标记"
            value={summarizeRosterSpecial(runtime.roster, runtime.rosterMembers)}
            tone={hasRosterSpecial(runtime.roster, runtime.rosterMembers) ? "warning" : undefined}
          />
          <RosterSummaryItem
            label="名单应收"
            value={runtime.roster?.validation.receivableAmountText ?? "¥0.00"}
            tone="danger"
          />
          <RosterSummaryItem
            label="合同金额校验"
            value={formatRosterAmountMatch(runtime.roster?.validation.amountMatched)}
            tone={runtime.roster?.validation.amountMatched ? "success" : "warning"}
          />
        </div>
        <div className="order-handover-roster-progress">
          <span>名单完整度</span>
          <strong>{formatRosterCompletion(runtime.rosterStats)}</strong>
          <div className="order-handover-roster-progress-track" aria-hidden="true">
            <div
              className="order-handover-roster-progress-bar"
              style={{ width: `${calcRosterProgress(runtime.rosterStats)}%` }}
            />
          </div>
        </div>
        <OrderHandoverBusinessChangeCard />
      </Card>

      <section className="order-handover-record-panels" aria-label="费用调整、确认件与导游">
        <Tabs defaultActiveTab="fees" destroyOnHide>
          <Tabs.TabPane key="fees" title="本团费用调整记录">
            <FeeAdjustmentRecords
              records={feeAdjustmentsQuery.data ?? []}
              loading={canAccessFeeAdjustment && feeAdjustmentAvailable && feeAdjustmentsQuery.isLoading}
              error={canAccessFeeAdjustment && feeAdjustmentAvailable && feeAdjustmentsQuery.isError}
            />
          </Tabs.TabPane>
          <Tabs.TabPane key="confirmation" title="确认件">
            <FulfillmentOrderReadOnlyPanel key={selected.id} itineraryId={selected.id} />
          </Tabs.TabPane>
          <Tabs.TabPane key="guides" title="导游">
            <OrderHandoverGuidePanel
              key={`guide-${selected.id}`}
              projectId={context.projectId}
              itineraryId={selected.id}
            />
          </Tabs.TabPane>
        </Tabs>
      </section>

      <div ref={salesCardRef} className="order-handover-sales-card-anchor">
      <Card
        className="order-handover-sales-card"
        bordered
        title="合同交接信息（签约后填写）"
        extra={
          <Space size={8}>
            <Text className="order-handover-muted">{formatSalesHandoverMeta(sales)}</Text>
            <Button
              type="text"
              icon={salesCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
              onClick={() => setSalesCollapsed((value) => !value)}
            >
              {salesCollapsed ? "展开" : "折叠"}
            </Button>
            {runtime.salesEditVisible && !contractHandoverLocked ? (
              <Button icon={<X size={16} />} onClick={handleCancelSalesHandoverEdit}>
                取消
              </Button>
            ) : null}
            <Button
              type="outline"
              icon={runtime.salesEditVisible ? <Save size={16} /> : <PenLine size={16} />}
              disabled={contractHandoverLocked}
              loading={runtime.salesSaving || runtime.rosterFlagsSaving}
              onClick={() => {
                if (!runtime.salesEditVisible) {
                  setSalesCollapsed(false);
                  runtime.openSalesEdit();
                  return;
                }
                form.validate().then(() => {
                  handleSaveSalesHandover(form.getFieldsValue() as SalesHandoverFormValues);
                });
              }}
            >
              {runtime.salesEditVisible ? "保存草稿" : "编辑"}
            </Button>
          </Space>
        }
      >
        {salesCollapsed ? null : (
          <>
            <Form
              form={form}
              layout="vertical"
              className="order-handover-sales-inline-form"
              onValuesChange={(changedValues, allValues) => {
                if (syncingSalesFormRef.current) {
                  return;
                }
                const nextDraftValues: Partial<SalesHandoverFormValues> = {
                  ...(changedValues as Partial<SalesHandoverFormValues>),
                };
                if ("contractTemplateType" in changedValues) {
                  const receivingAccountText = buildContractReceivingAccountText(
                    (allValues as SalesHandoverFormValues).contractTemplateType,
                  );
                  if (receivingAccountText !== undefined) {
                    nextDraftValues.receivingAccountText = receivingAccountText;
                    syncingSalesFormRef.current = true;
                    form.setFieldsValue({ receivingAccountText });
                    syncingSalesFormRef.current = false;
                  }
                }
                salesDraftValuesRef.current = {
                  ...salesDraftValuesRef.current,
                  ...nextDraftValues,
                };
                const changedRequiredFields = Object.keys(
                  nextDraftValues,
                ) as SalesHandoverRequiredFieldKey[];
                if (changedRequiredFields.length > 0) {
                  setSalesRequiredMissingFields((current) =>
                    current.filter((field) => !changedRequiredFields.includes(field)),
                  );
                }
                const shouldRefreshSalesPreview = [
                  "contractTemplateType",
                  "contractSignerMemberId",
                  "contractPeopleCount",
                  "contractUnitPriceAmount",
                ].some((field) => field in nextDraftValues);
                if (shouldRefreshSalesPreview) {
                  setSalesInlineDraftValues((current) => ({
                    ...current,
                    ...nextDraftValues,
                  }));
                }
              }}
            >
              <ContractHandoverSheet
                amount={amount}
                catchOrderSelectOptions={catchOrderSelectOptions}
                contractSignerDisplayName={contractSignerDisplayName}
                contractSignerOptions={contractSignerOptions}
                dispatcherSelectOptions={dispatcherSelectOptions}
                draftValues={
                  runtime.salesEditVisible
                    ? { ...salesDraftValues, ...salesInlineDraftValues }
                    : salesDraftValues
                }
                editing={runtime.salesEditVisible}
                itinerary={selected}
                itinerarySelectDisabled={contractHandoverLocked}
                preferLiveItinerary={Boolean(pendingItinerary)}
                majorNameOptions={majorNameOptions}
                majorOptionsLoading={runtime.majorOptionsLoading}
                onActiveFieldChange={handleActiveSalesEditorField}
                onDraftValuesChange={(values) => {
                  salesDraftValuesRef.current = {
                    ...salesDraftValuesRef.current,
                    ...values,
                  };
                  setSalesInlineDraftValues((current) => ({
                    ...current,
                    ...values,
                  }));
                  const changedFields = Object.keys(values) as SalesHandoverRequiredFieldKey[];
                  if (changedFields.length > 0) {
                    setSalesRequiredMissingFields((current) =>
                      current.filter((field) => !changedFields.includes(field)),
                    );
                  }
                }}
                requiredMissingFields={visibleSalesRequiredMissingFields}
                onSelectItinerary={() => {
                  setRouteTeamIdNeedsConfirm(false);
                  runtime.openItineraryDrawer();
                }}
                orderLines={runtime.orderLines}
                routeCellRef={routeCellRef}
                routeTeamIdNeedsConfirm={routeTeamIdNeedsConfirm}
                rosterMembers={runtime.rosterMembers}
                rosterStats={runtime.rosterStats}
                sales={sales}
                salesUserSelectOptions={salesUserSelectOptions}
                schoolSiteOptions={schoolSiteOptions}
                schoolSiteOptionsLoading={runtime.staffOptionsLoading}
                serviceConsultantSelectOptions={serviceConsultantSelectOptions}
                staffOptionsLoading={runtime.staffOptionsLoading}
                summary={runtime.summary}
              />
            </Form>
            {runtime.salesSaveErrorMessage ? (
              <Alert className="order-handover-inline-alert" type="error" content={runtime.salesSaveErrorMessage} showIcon />
            ) : null}
          </>
        )}
      </Card>
      </div>
      {contractHandoverReadOnlyMessage ? (
        <Alert
          className="order-handover-inline-alert"
          type="warning"
          content={contractHandoverReadOnlyMessage}
          showIcon
        />
      ) : null}

      <Drawer
        width={760}
        title="选择行程"
        visible={runtime.itineraryDrawerVisible}
        footer={null}
        onCancel={runtime.closeItineraryDrawer}
      >
        <div className="order-handover-drawer-search">
          <Input.Search
            searchButton={<Search size={16} />}
            placeholder="搜索行程标题、学校、目的地、版本"
          />
        </div>
        {runtime.itinerarySelectErrorMessage ? (
          <Alert className="order-handover-inline-alert" type="error" content={runtime.itinerarySelectErrorMessage} showIcon />
        ) : null}
        <Table
          className="order-handover-itinerary-table"
          rowKey="id"
          columns={itineraryColumns}
          data={runtime.itineraryOptions}
          pagination={false}
          border={false}
          noDataElement="当前项目暂无可选择行程"
          rowClassName={(record) => (record.id === selected?.id ? "is-selected" : "")}
        />
      </Drawer>

      <Drawer
        width={720}
        title="新增名单成员"
        visible={rosterCreateVisible}
        {...ROSTER_CREATE_DRAWER_A11Y_PROPS}
        okText="保存新增"
        cancelText="取消"
        confirmLoading={runtime.rosterMemberCreating}
        onOk={() => void handleSaveRosterCreate()}
        onCancel={handleCloseRosterCreateDrawer}
        unmountOnExit
      >
        {rosterCreateErrorMessage ? (
          <Alert
            className="order-handover-inline-alert"
            type="error"
            content={rosterCreateErrorMessage}
            showIcon
          />
        ) : null}
        <RosterMemberCreateForm
          form={rosterCreateForm}
          classGroupOptions={rosterClassGroupOptions}
          travelerTypeOptions={rosterTravelerTypeOptions}
          teacherTypeOptions={rosterTeacherTypeOptions}
          certificateTypeOptions={rosterCertificateTypeOptions}
          genderOptions={rosterGenderOptions}
        />
      </Drawer>

      <FeeAdjustmentRequestModal
        visible={feeAdjustmentRequestMembers.length > 0}
        rosterId={feeAdjustmentRosterId}
        members={feeAdjustmentRequestMembers}
        onClose={() => setFeeAdjustmentRequestMembers([])}
      />

      <Modal
        title="选择全选范围"
        visible={rosterSelectAllVisible}
        footer={null}
        onCancel={() => setRosterSelectAllVisible(false)}
        unmountOnExit
      >
        <p>
          当前页共 {currentPageMemberIdsRef.current.length} 人，全部筛选结果共{" "}
          {visibleRosterMemberIdsRef.current.length} 人。
        </p>
        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          <Button long type="outline" onClick={() => handleConfirmRosterSelectScope("page")}>
            仅当前页
          </Button>
          <Button long type="primary" onClick={() => handleConfirmRosterSelectScope("all")}>
            全部筛选结果（全部页）
          </Button>
        </Space>
      </Modal>

      <Modal
        title="确认删除名单成员"
        visible={Boolean(rosterDeleteConfirmMember)}
        okText="删除"
        cancelText="取消"
        confirmLoading={runtime.rosterStopSaving}
        okButtonProps={{ status: "danger" }}
        onOk={handleConfirmRosterDelete}
        onCancel={() => setRosterDeleteConfirmMember(undefined)}
        unmountOnExit
      >
        {rosterDeleteConfirmMember ? (
          <span>
            确认删除「{rosterDeleteConfirmMember.name}」吗？删除后该成员会移入已删除名单，默认列表不再展示。
          </span>
        ) : null}
      </Modal>

      <Modal
        title="确认批量删除名单成员"
        visible={rosterBatchDeleteConfirmVisible}
        okText="批量删除"
        cancelText="取消"
        confirmLoading={runtime.rosterStopSaving}
        okButtonProps={{ status: "danger" }}
        onOk={() => void handleConfirmRosterBatchDelete()}
        onCancel={() => setRosterBatchDeleteConfirmVisible(false)}
        unmountOnExit
      >
        <span>
          确认删除已选 {selectedRosterCount} 名成员吗？删除后成员会移入已删除名单，默认列表不再展示。
        </span>
      </Modal>

    </main>
  );
}

/**
 *
 */
function formatFeeAdjustmentMoney(value: string): string {
  return value.startsWith("¥") ? value : `¥${value}`;
}

/** 渲染顶部订单摘要指标。 */
function SummaryMetric({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "link" | "danger" | "people";
}) {
  return (
    <div className="order-handover-summary-metric">
      <span>{label}</span>
      <strong className={tone ? `is-${tone}` : undefined}>{value}</strong>
    </div>
  );
}

/** 渲染合同签订状态卡片。 */
function ContractSigningCard({
  canFinalize,
  applying,
  contractSigning,
  errorMessage,
  onApply,
  onRefreshInvites,
  onResendSms,
  refreshingInvites,
  resendingSignRecordId,
}: {
  canFinalize: boolean;
  applying: boolean;
  contractSigning: OrderHandoverContractSigning | undefined;
  errorMessage: string;
  onApply: () => void;
  onRefreshInvites?: () => void;
  onResendSms?: (signRecordId: string) => void;
  refreshingInvites?: boolean;
  resendingSignRecordId?: string;
}) {
  const signing = contractSigning ?? {
    signState: 1,
    signStateCode: "not_applied",
    signStateText: "未申请",
    canApply: false,
    canInvalidate: false,
    signRecords: [],
  };
  const displayedProviderName = signing.providerName ?? "当前版本无需电子签";
  const signedCount = signing.signRecords.filter((record) => record.isSign).length;
  const voidedCount = signing.signRecords.filter((record) => record.isCancelled).length;
  const contractViewUrl = signing.documentUrl || signing.contractDownloadUrl;
  const contractRecordColumns = useMemo<TableColumnProps<OrderHandoverContractSigning["signRecords"][number]>[]>(
    () => [
      {
        title: "签署人",
        dataIndex: "signerName",
        width: 180,
        render: (_, record) => (
          <Space size={6}>
            <span>{record.signerName}</span>
            {record.isMainSigner ? <Tag color="arcoblue">主签</Tag> : null}
            {record.signerRole ? <Tag>{record.signerRole}</Tag> : null}
          </Space>
        ),
      },
      {
        title: "电话",
        dataIndex: "signerPhone",
        width: 130,
        render: (value) => value || "-",
      },
      {
        title: "证件号",
        dataIndex: "signerCertificateNo",
        width: 180,
        render: (value) => value || "-",
      },
      {
        title: "状态",
        dataIndex: "isSign",
        width: 130,
        render: (_, record) => {
          if (record.isCancelled) {
            return <Tag color="red">已作废</Tag>;
          }
          if (signing.signStateCode === "void_applying" && record.isSign) {
            return <Tag color="orangered">待同意作废</Tag>;
          }
          if (signing.signStateCode === "void_applying") {
            return <Tag color="orange">作废处理中</Tag>;
          }
          if (signing.signStateCode === "partial_voided" && record.isSign) {
            return <Tag color="orangered">待同意作废</Tag>;
          }
          return record.isSign ? <Tag color="green">已签署</Tag> : <Tag color="orange">待签署</Tag>;
        },
      },
      {
        title: "短信/通知状态",
        dataIndex: "notificationStateText",
        width: 160,
        render: (_, record) => (
          <Tag color={contractNotificationTagColor(record.notificationStateCode)}>
            {record.notificationChannelText} · {record.notificationStateText}
          </Tag>
        ),
      },
      {
        title: "发送时间",
        dataIndex: "signSendTime",
        width: 170,
        render: (value) => formatOptionalDateTime(value),
      },
      {
        title: "签署时间",
        dataIndex: "signSuccessTime",
        width: 170,
        render: (value) => formatOptionalDateTime(value),
      },
      {
        title: "签署链接",
        dataIndex: "signUrl",
        width: 280,
        render: (value, record) => {
          // 已签署：打开合同查看页（对齐老系统 FddTemplate → ContractUrl）；未签署：打开签署入口。
          const openUrl =
            record.isSign && contractViewUrl ? contractViewUrl : value ? String(value) : "";
          if (!openUrl) {
            return "-";
          }
          return (
            <div className="order-handover-sign-link-actions">
              <Button
                className="order-handover-sign-link-button"
                type="outline"
                size="mini"
                icon={<Link2 size={14} />}
                href={openUrl}
                target="_blank"
                rel="noreferrer"
              >
                {record.isSign ? "查看合同" : "打开"}
              </Button>
              {record.isSign && signing.contractDownloadUrl ? (
                <Button
                  className="order-handover-sign-link-button"
                  type="outline"
                  size="mini"
                  icon={<Download size={14} />}
                  href={signing.contractDownloadUrl}
                  download={signing.contractNo ? `${signing.contractNo}.pdf` : undefined}
                >
                  下载合同
                </Button>
              ) : null}
              <Button
                className="order-handover-sign-link-button"
                type="outline"
                size="mini"
                icon={<Copy size={14} />}
                onClick={() => {
                  void writeClipboardText(openUrl).then(
                    () =>
                      Message.success(record.isSign ? "合同查看链接已复制" : "签署链接已复制"),
                    () => Message.error("复制失败，请手动复制链接"),
                  );
                }}
              >
                复制
              </Button>
              {record.isSign || record.isCancelled ? null : (
                <Button
                  className="order-handover-sign-link-button"
                  type="outline"
                  size="mini"
                  status="warning"
                  icon={<MessageSquareText size={14} />}
                  loading={resendingSignRecordId === record.id}
                  disabled={!canFinalize}
                  onClick={() => onResendSms?.(record.id)}
                >
                  重发短信
                </Button>
              )}
            </div>
          );
        },
      },
    ],
    [canFinalize, contractViewUrl, onResendSms, resendingSignRecordId, signing.signStateCode],
  );

  return (
    <Card
      id="order-handover-contract-signing"
      className="order-handover-contract-signing-card"
      bordered
      title={
        <span className="order-handover-contract-signing-title">
          <FileSignature size={18} />
          合同签订
        </span>
      }
      extra={
        <div className="order-handover-contract-signing-actions">
          <span className="order-handover-contract-signing-provider-label">签约服务</span>
          <Tag color="arcoblue">{displayedProviderName}</Tag>
          {signing.signRecords.length > 0 ? (
            <Button
              type="outline"
              loading={refreshingInvites}
              disabled={!canFinalize}
              onClick={onRefreshInvites}
            >
              刷新签署链接
            </Button>
          ) : null}
          <Button
            type="primary"
            icon={<FileSignature size={16} />}
            loading={applying}
            disabled={!canFinalize || !signing.canApply}
            onClick={onApply}
          >
            生成合同
          </Button>
        </div>
      }
    >
      <div className="order-handover-contract-signing-grid">
        <SummaryMetric label="合同号" value={signing.contractNo ?? "待生成"} tone="link" />
        <SummaryMetric label="第三方号" value={signing.externalContractNo ?? "待返回"} />
        <SummaryMetric label="服务商" value={displayedProviderName} />
        <SummaryMetric label="签订状态" value={signing.signStateText} />
        <SummaryMetric
          label="签署进度"
          value={`${signedCount}/${signing.signRecords.length} 已签`}
          tone={signedCount > 0 ? "link" : undefined}
        />
        {voidedCount > 0 ||
        signing.signStateCode === "void_applying" ||
        signing.signStateCode === "partial_voided" ? (
          <SummaryMetric
            label="作废进度"
            value={`${voidedCount}/${signing.signRecords.length} 已作废`}
            tone="danger"
          />
        ) : null}
      </div>
      <div className="order-handover-contract-signing-meta">
        <Tag color={contractStateTagColor(signing.signStateCode)}>{signing.signStateText}</Tag>
        <span>申请：{formatOptionalDateTime(signing.applyTime)}</span>
        <span>完成：{formatOptionalDateTime(signing.completedAt)}</span>
        {signing.invalidatedAt ? (
          <span>作废时间：{formatOptionalDateTime(signing.invalidatedAt)}</span>
        ) : null}
      </div>
      {signing.previousVoidedContractNo ? (
        <Alert
          className="order-handover-inline-alert"
          type="info"
          content={`上一份合同 ${signing.previousVoidedContractNo} ${
            signing.previousVoidedSignStateText ?? "已作废"
          }${
            signing.previousVoidedAt
              ? `（${formatOptionalDateTime(signing.previousVoidedAt)}）`
              : ""
          }`}
          showIcon
        />
      ) : null}
      {signing.blockedReason ? (
        <Alert
          className="order-handover-inline-alert"
          type="warning"
          content={signing.blockedReason}
          showIcon
        />
      ) : null}
      {errorMessage &&
      !(
        signing.blockedReason &&
        (errorMessage === signing.blockedReason ||
          (errorMessage.includes("若需重新生成") &&
            signing.blockedReason.includes("若需重新生成")))
      ) ? (
        <Alert
          className="order-handover-inline-alert"
          type="error"
          content={errorMessage}
          showIcon
        />
      ) : null}
      <Table
        className="order-handover-contract-signer-table"
        rowKey="id"
        columns={contractRecordColumns}
        data={signing.signRecords}
        pagination={CONTRACT_SIGNER_TABLE_PAGINATION}
        border={false}
        size="mini"
        tableLayoutFixed
        noDataElement="申请合同后将在这里展示签署人进度"
      />
    </Card>
  );
}

/** 渲染销售交接信息字段块。 */
function InfoBlock({
  label,
  value,
  subValue,
  tone,
  wide,
  full,
  textRow,
  note,
}: {
  label: string;
  value: string;
  subValue?: string;
  tone?: SalesInfoTone;
  wide?: boolean;
  full?: boolean;
  textRow?: boolean;
  note?: boolean;
}) {
  const displayTone = tone ?? getSalesInfoTone(label);
  return (
    <div className={buildInfoBlockClassName({ wide, full, textRow, note })}>
      <span>{label}</span>
      <strong className={displayTone ? `is-${displayTone}` : undefined}>{value}</strong>
      {subValue ? <small>{subValue}</small> : null}
    </div>
  );
}

/** 渲染销售交接卡片内的业务分组。 */
function SalesSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="order-handover-sales-section">
      <div className="order-handover-sales-section-title">{title}</div>
      {children}
    </section>
  );
}

/** 渲染旧系统 Excel 风格的合同交接表。 */
function ContractHandoverSheet({
  amount,
  catchOrderSelectOptions,
  contractSignerDisplayName,
  contractSignerOptions,
  dispatcherSelectOptions,
  draftValues,
  editing,
  itinerary,
  itinerarySelectDisabled,
  preferLiveItinerary = false,
  majorNameOptions,
  majorOptionsLoading,
  onActiveFieldChange: setActiveSalesField,
  onDraftValuesChange,
  onSelectItinerary,
  orderLines,
  requiredMissingFields = [],
  routeCellRef,
  routeTeamIdNeedsConfirm,
  rosterMembers,
  rosterStats,
  sales,
  salesUserSelectOptions,
  schoolSiteOptions,
  schoolSiteOptionsLoading,
  serviceConsultantSelectOptions,
  staffOptionsLoading,
  summary,
}: {
  amount: string;
  catchOrderSelectOptions: OrderHandoverStaffOption[];
  contractSignerDisplayName: string;
  contractSignerOptions: EditableSelectOption[];
  dispatcherSelectOptions: OrderHandoverStaffOption[];
  draftValues?: SalesHandoverFormValues;
  editing: boolean;
  itinerary?: HandoverItineraryOption;
  itinerarySelectDisabled?: boolean;
  /** 待保存行程时优先展示 live 标题/日期，避免旧 routeNameSnapshot 盖住选中结果。 */
  preferLiveItinerary?: boolean;
  majorNameOptions: EditableSelectOption[];
  majorOptionsLoading: boolean;
  onActiveFieldChange: (field: SalesSheetFieldName | undefined) => void;
  onDraftValuesChange: (values: Partial<SalesHandoverFormValues>) => void;
  onSelectItinerary?: () => void;
  orderLines: OrderHandoverOrderLine[];
  requiredMissingFields?: SalesHandoverRequiredFieldKey[];
  routeCellRef?: RefObject<HTMLTableCellElement | null>;
  routeTeamIdNeedsConfirm?: boolean;
  rosterMembers: OrderRosterMember[];
  rosterStats: { total: number; student: number; teacher: number; guide: number };
  sales?: OrderHandoverSalesHandover;
  salesUserSelectOptions: OrderHandoverStaffOption[];
  schoolSiteOptions: EditableSelectOption[];
  schoolSiteOptionsLoading: boolean;
  serviceConsultantSelectOptions: OrderHandoverStaffOption[];
  staffOptionsLoading: boolean;
  summary?: OrderHandoverSummary;
}) {
  const signType = summary?.signType?.trim() || "团队";
  const isSingleCar = signType === "单订车";
  const isSingleHotel = signType === "单订房";
  const isSingleOrder = isSingleCar || isSingleHotel;
  const sheetTitle = resolveContractHandoverSheetTitle(signType);
  const actualLines = buildActualReceivableLines({
    orderLines,
    rosterMembers,
    rosterStats,
    sales,
  });
  const actualReceivableTotalText = buildActualReceivableTotalText(actualLines);
  const teacherContacts = buildTeacherContactRows(rosterMembers);
  const actualLineCount = Math.max(actualLines.length, 1);
  const [fullPreviewVisible, setFullPreviewVisible] = useState(false);
  const [previewScale, setPreviewScale] = useState(LEGACY_CONTRACT_SHEET_DEFAULT_PREVIEW_SCALE);
  const [sheetHeight, setSheetHeight] = useState(LEGACY_CONTRACT_SHEET_DEFAULT_HEIGHT);
  const previewWidthRef = useRef<HTMLDivElement>(null);
  const previewContainerRef = useRef<HTMLDivElement>(null);
  const sheetTableRef = useRef<HTMLTableElement>(null);
  // 预览与编辑共用等比缩放；编辑态用 zoom 替代 transform，保证输入框可点可选。
  const fitPreview = !fullPreviewVisible;
  const salesDraftValues = editing ? draftValues : undefined;
  const currentContractTemplateType =
    typeof salesDraftValues?.contractTemplateType === "string"
      ? salesDraftValues.contractTemplateType
      : sales?.contractTemplateType;
  const invoiceRequirementLabel = getContractInvoiceLabel(currentContractTemplateType);
  const previewScaleStyle = {
    "--sheet-preview-scale": String(previewScale),
    // +1px 避免缩放取整后内容刚好顶满，触发横向滚动条显隐抖动。
    "--sheet-preview-height": `${Math.ceil(sheetHeight * previewScale) + 1}px`,
  } as CSSProperties;
  const previewText = useCallback(
    (
      field: SalesSheetFieldName,
      persistedValue: string | number | undefined,
      fallback: string,
    ) => {
      const hasDraftValue = Boolean(salesDraftValues && field in salesDraftValues);
      return displayText(
        hasDraftValue ? toSalesPreviewText(salesDraftValues?.[field]) : toInputText(persistedValue),
        fallback,
      );
    },
    [salesDraftValues],
  );
  const previewBoolean = useCallback(
    (field: BooleanSalesField, persistedValue: boolean | undefined) =>
      formatBooleanValue(
        salesDraftValues && field in salesDraftValues
          ? fromBooleanSelectValue(salesDraftValues[field])
          : persistedValue,
      ),
    [salesDraftValues],
  );
  const previewStaff = useCallback(
    (
      field: "serviceConsultantUserId" | "catchOrderUserId" | "salesUserId" | "dispatcherUserId",
      options: OrderHandoverStaffOption[],
      persistedName: string | undefined,
      fallback: string,
    ) =>
      displayText(
        salesDraftValues && field in salesDraftValues
          ? getStaffDisplayName(options, salesDraftValues[field])
          : persistedName,
        fallback,
      ),
    [salesDraftValues],
  );
  const previewContractSignerDisplayName = useMemo(() => {
    if (!salesDraftValues || !("contractSignerMemberId" in salesDraftValues)) {
      return contractSignerDisplayName;
    }
    if (!salesDraftValues.contractSignerMemberId) {
      return "待从名单选择";
    }
    const option = contractSignerOptions.find((item) => item.value === salesDraftValues.contractSignerMemberId);
    return option?.label ?? contractSignerDisplayName;
  }, [contractSignerDisplayName, contractSignerOptions, salesDraftValues?.contractSignerMemberId]);
  const previewContractAmount = useMemo(
    () => {
      const draftAmount = calculateContractAmountTextFromInput(
        previewText("contractPeopleCount", sales?.contractPeopleCount, ""),
        previewText("contractUnitPriceAmount", sales?.contractUnitPriceAmount, ""),
      );
      const persistedOrderAmount = orderLines.length > 0 ? sales?.orderTotalAmountText : undefined;
      if (editing) {
        return draftAmount ?? persistedOrderAmount ?? calculateContractAmountText(sales) ?? sales?.orderTotalAmountText ?? amount;
      }
      return persistedOrderAmount ?? draftAmount ?? calculateContractAmountText(sales) ?? sales?.orderTotalAmountText ?? amount;
    },
    [amount, editing, orderLines.length, previewText, sales],
  );

  const onActiveFieldChange = useCallback(
    (field: SalesSheetFieldName | undefined) => {
      setActiveSalesField(field);
    },
    [setActiveSalesField],
  );
  const sheetEditorProps = {
    catchOrderSelectOptions,
    contractSignerOptions,
    dispatcherSelectOptions,
    majorNameOptions,
    majorOptionsLoading,
    schoolSiteOptions,
    schoolSiteOptionsLoading,
    serviceConsultantSelectOptions,
    staffOptionsLoading,
    salesUserSelectOptions,
    onActiveFieldChange,
  };
  const requiredMissingSet = useMemo(
    () => new Set<string>(requiredMissingFields),
    [requiredMissingFields],
  );

  useEffect(() => {
    if (editing) {
      setFullPreviewVisible(false);
    }
  }, [editing]);

  useLayoutEffect(() => {
    const updatePreviewMetrics = () => {
      // 宽度从外侧测量节点读取，避免滚动条显隐改变 scroll 容器 clientWidth 后反向改 scale。
      const containerWidth = previewWidthRef.current?.clientWidth ?? 0;
      const nextScale = containerWidth > 0
        ? Math.min(
            1,
            Math.max(
              LEGACY_CONTRACT_SHEET_MIN_PREVIEW_SCALE,
              Math.floor((containerWidth / LEGACY_CONTRACT_SHEET_WIDTH) * 1000) / 1000,
            ),
          )
        : LEGACY_CONTRACT_SHEET_DEFAULT_PREVIEW_SCALE;
      const nextHeight = Math.ceil(sheetTableRef.current?.offsetHeight || LEGACY_CONTRACT_SHEET_DEFAULT_HEIGHT);

      setPreviewScale((current) => (current === nextScale ? current : nextScale));
      setSheetHeight((current) => (current === nextHeight ? current : nextHeight));
    };

    updatePreviewMetrics();
    const resizeObserver = typeof ResizeObserver === "undefined" ? undefined : new ResizeObserver(updatePreviewMetrics);
    if (previewWidthRef.current && resizeObserver) {
      resizeObserver.observe(previewWidthRef.current);
    }
    if (sheetTableRef.current && resizeObserver) {
      resizeObserver.observe(sheetTableRef.current);
    }
    window.addEventListener("resize", updatePreviewMetrics);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener("resize", updatePreviewMetrics);
    };
  }, [editing, fullPreviewVisible, rosterMembers.length, sales?.updatedAt]);

  useEffect(() => {
    if (!fullPreviewVisible) {
      return undefined;
    }
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setFullPreviewVisible(false);
      }
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [fullPreviewVisible]);

  return (
    <SalesRequiredMissingContext.Provider value={requiredMissingSet}>
    <div
      aria-label={
        fullPreviewVisible
          ? editing
            ? `${sheetTitle}全屏编辑`
            : `${sheetTitle}完整预览`
          : undefined
      }
      aria-modal={fullPreviewVisible ? true : undefined}
      className={[
        "order-handover-contract-sheet-shell",
        fitPreview ? "is-preview-fit" : "",
        fullPreviewVisible ? "is-full-preview" : "",
      ].filter(Boolean).join(" ")}
      role={fullPreviewVisible ? "dialog" : undefined}
    >
      <LegacySalesSectionIndex
        amount={previewContractAmount}
        contractSignerDisplayName={previewContractSignerDisplayName}
        orderLines={orderLines}
        sales={sales}
        signType={signType}
      />
      <div className="order-handover-contract-sheet-toolbar">
        <Button
          icon={fullPreviewVisible ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
          size="small"
          type="outline"
          onClick={() => setFullPreviewVisible((value) => !value)}
        >
          {fullPreviewVisible
            ? editing
              ? "退出全屏编辑"
              : "退出放大"
            : editing
              ? "全屏编辑"
              : "放大全部预览"}
        </Button>
      </div>
      <div className={`order-handover-contract-sheet-workspace${editing ? " is-editing" : ""}`}>
        <div ref={previewWidthRef} className="order-handover-contract-sheet-measure">
        <div
          ref={previewContainerRef}
          className={[
            "order-handover-contract-sheet-scroll",
            fitPreview ? "is-fit-preview" : "",
            fitPreview && editing ? "is-editing-fit" : "",
            fullPreviewVisible ? "is-full-preview" : "",
          ].filter(Boolean).join(" ")}
          style={fitPreview ? previewScaleStyle : undefined}
        >
          <div className="order-handover-contract-sheet-canvas">
          <table
            ref={sheetTableRef}
            className="order-handover-contract-sheet"
            aria-label={sheetTitle}
            data-sign-type={signType}
            data-template="legacy-show-excel-vue"
            style={{ width: LEGACY_CONTRACT_SHEET_WIDTH }}
          >
            <colgroup>
              {LEGACY_CONTRACT_SHEET_COLUMN_WIDTHS.map((width, index) => (
                <col key={index} style={{ width }} />
              ))}
            </colgroup>
            <tbody>
              <tr {...legacySheetRowProps("title", 63)}>
                <th className="order-handover-sheet-title" colSpan={11}>
                  <span className="order-handover-sheet-status">{formatOrderStatus(summary?.orderStatus)}</span>
                  {sheetTitle}
                </th>
              </tr>
              <tr {...legacySheetRowProps("route", 33)}>
                <SheetLabelCell label="行程起止日期" />
                <SheetValueCell
                  label="行程起止日期"
                  value={
                    preferLiveItinerary
                      ? (itinerary?.dateRange ?? "待选择")
                      : (summary?.dateRange ?? itinerary?.dateRange ?? "待选择")
                  }
                />
                <SheetLabelCell label="线路" />
                <SheetValueCell
                  cellRef={routeCellRef}
                  className={[
                    itinerarySelectDisabled ? "" : "is-route-selectable",
                    routeTeamIdNeedsConfirm ? "is-route-team-confirm" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  colSpan={2}
                  label="线路"
                  title={itinerarySelectDisabled ? undefined : "点击选择行程"}
                  topHint={routeTeamIdNeedsConfirm ? "请检查线路" : undefined}
                  tone={routeTeamIdNeedsConfirm ? "danger" : "route"}
                  value={formatContractRouteDisplay({
                    routeNameSnapshot: preferLiveItinerary
                      ? undefined
                      : summary?.routeNameSnapshot,
                    title: itinerary?.title ?? summary?.projectTitle,
                  })}
                  onClick={() => {
                    if (itinerarySelectDisabled) {
                      return;
                    }
                    onSelectItinerary?.();
                  }}
                />
                <SheetLabelCell label="客户单位" />
                <SheetValueCell label="客户单位" value={itinerary?.school ?? "待填写"} />
                <SheetLabelCell label="专业名称" />
                <SheetValueCell
                  label="院系专业"
                  value={previewText("majorNames", sales?.majorNamesText ?? itinerary?.majorNamesText, "待填写")}
                  field="majorNames"
                  colSpan={3}
                  editing={editing}
                  editor={<SheetEditableControl field="majorNames" label="院系专业" {...sheetEditorProps} />}
                  onActiveFieldChange={onActiveFieldChange}
                  tone="source"
                />
              </tr>
              <tr {...legacySheetRowProps("customer-type", 33)}>
                <SheetLabelCell label="新客户类型" rowSpan={4} emphasis />
                <SheetValueCell
                  label="新客户类型"
                  value={previewText("customerNoveltyType", sales?.customerNoveltyType, "待确认")}
                  field="customerNoveltyType"
                  rowSpan={4}
                  editing={editing}
                  editor={<SheetEditableControl field="customerNoveltyType" label="新客户类型" {...sheetEditorProps} />}
                  onActiveFieldChange={onActiveFieldChange}
                  tone="source"
                />
                <SheetLabelCell label="合同签定人" rowSpan={4} emphasis required />
                <SheetValueCell
                  label="主签合同人"
                  value={previewContractSignerDisplayName}
                  field="contractSignerMemberId"
                  rowSpan={4}
                  colSpan={2}
                  editing={editing}
                  editor={<SheetEditableControl field="contractSignerMemberId" label="主签合同人" {...sheetEditorProps} />}
                  onActiveFieldChange={onActiveFieldChange}
                  tone="link"
                />
                <SheetLabelCell label="所属小B" />
                <SheetValueCell label="所属小B" value="待同步" />
                <SheetLabelCell label="方案组计调" rowSpan={2} />
                <SheetValueCell label="方案组计调" value={itinerary?.dispatcherUserName ?? sales?.dispatcherUserName ?? "待选择"} rowSpan={2} colSpan={3} tone="staff" />
              </tr>
              <tr {...legacySheetRowProps("catch-order", 33)}>
                <SheetLabelCell label="抓单人" emphasis required />
                <SheetValueCell
                  label="抓单人"
                  value={previewStaff("catchOrderUserId", catchOrderSelectOptions, sales?.catchOrderUserName, "待填写")}
                  field="catchOrderUserId"
                  editing={editing}
                  editor={<SheetEditableControl field="catchOrderUserId" label="抓单人" {...sheetEditorProps} />}
                  onActiveFieldChange={onActiveFieldChange}
                  tone="staff"
                />
              </tr>
              <tr {...legacySheetRowProps("service-consultant", 33)}>
                <SheetLabelCell label="签单后的服务顾问" emphasis required />
                <SheetValueCell
                  label="服务顾问"
                  value={previewStaff("serviceConsultantUserId", serviceConsultantSelectOptions, sales?.serviceConsultantName, "待填写")}
                  field="serviceConsultantUserId"
                  editing={editing}
                  editor={<SheetEditableControl field="serviceConsultantUserId" label="服务顾问" {...sheetEditorProps} />}
                  onActiveFieldChange={onActiveFieldChange}
                  tone="staff"
                />
                <SheetLabelCell label="执行组计调" rowSpan={2} emphasis required />
                <SheetValueCell
                  label="计调执行人"
                  value={previewStaff("dispatcherUserId", dispatcherSelectOptions, sales?.dispatcherUserName ?? itinerary?.dispatcherUserName, "待选择")}
                  field="dispatcherUserId"
                  rowSpan={2}
                  colSpan={3}
                  editing={editing}
                  editor={<SheetEditableControl field="dispatcherUserId" label="计调执行人" {...sheetEditorProps} />}
                  onActiveFieldChange={onActiveFieldChange}
                  tone="staff"
                />
              </tr>
              <tr {...legacySheetRowProps("sales-user", 33)}>
                <SheetLabelCell label="签单人" />
                <SheetValueCell
                  label="签单人"
                  value={previewStaff("salesUserId", salesUserSelectOptions, sales?.salesUserName, "待选择")}
                  field="salesUserId"
                  editing={editing}
                  editor={<SheetEditableControl field="salesUserId" label="签单人" {...sheetEditorProps} />}
                  onActiveFieldChange={onActiveFieldChange}
                  tone="staff"
                />
              </tr>
              <tr {...legacySheetRowProps("form-time", 33)}>
                <SheetLabelCell label="填表时间" />
                <SheetValueCell label="填表时间" value={formatDateOnly(sales?.updatedAt)} colSpan={3} />
                <SheetLabelCell label="合同金额" />
                <SheetLabelCell label="集合出发时间、地点" />
                <SheetValueCell
                  label="写明校区或具体地址"
                  value={previewText("schoolSiteText", sales?.schoolSiteText, "待维护校区地址")}
                  field="schoolSiteText"
                  colSpan={3}
                  editing={editing}
                  editor={<SheetEditableControl field="schoolSiteText" label="写明校区或具体地址" {...sheetEditorProps} />}
                  onActiveFieldChange={onActiveFieldChange}
                  tone="route"
                />
                <SheetLabelCell label="实际收款金额" />
                <SheetLabelCell label="备注" />
              </tr>
              {actualLines.map((actualLine, index) => (
                <ActualReceivableRow
                  key={`${actualLine.label}-${actualLine.price}-${index}`}
                  contractAmount={index === 0 ? previewContractAmount : undefined}
                  contractPeopleCount={
                    index === 0 ? previewText("contractPeopleCount", sales?.contractPeopleCount, "") : undefined
                  }
                  contractUnitPriceAmount={
                    index === 0 ? previewText("contractUnitPriceAmount", sales?.contractUnitPriceAmount, "") : undefined
                  }
                  editing={index === 0 ? editing : undefined}
                  line={actualLine}
                  onActiveFieldChange={index === 0 ? onActiveFieldChange : undefined}
                  rowName={`actual-receivable-${index}`}
                  rowSpan={index === 0 ? actualLineCount : undefined}
                  sheetEditorProps={index === 0 ? sheetEditorProps : undefined}
                />
              ))}
              <tr {...legacySheetRowProps("contract-total", 33)}>
                <SheetLabelCell label="合同总计金额" colSpan={4} />
                <SheetValueCell label="合同总计金额" value={previewContractAmount} tone="danger" highlight />
                <SheetValueCell label="合同总计备注" value="" colSpan={5} />
              </tr>
              {isSingleOrder ? (
                <SingleOrderPricingSheetRows
                  editing={editing}
                  orderLines={filterSingleOrderPricingLines(
                    orderLines,
                    isSingleCar ? "单订车" : "单订房",
                  )}
                  serviceFee={
                    typeof salesDraftValues?.singleOrderServiceFee === "string"
                      ? salesDraftValues.singleOrderServiceFee
                      : (sales?.singleOrderServiceFee ?? "0")
                  }
                  salePriceById={buildSingleOrderSalePriceDraft(
                    filterSingleOrderPricingLines(
                      orderLines,
                      isSingleCar ? "单订车" : "单订房",
                    ),
                    salesDraftValues?.singleOrderPricingLines,
                  )}
                  signType={isSingleCar ? "单订车" : "单订房"}
                  onServiceFeeChange={(value) =>
                    onDraftValuesChange({ singleOrderServiceFee: value })
                  }
                  onSalePriceChange={(lineId, value) => {
                    const next = buildSingleOrderSalePriceDraft(
                      filterSingleOrderPricingLines(
                        orderLines,
                        isSingleCar ? "单订车" : "单订房",
                      ),
                      salesDraftValues?.singleOrderPricingLines,
                    );
                    next[lineId] = value;
                    onDraftValuesChange({
                      singleOrderPricingLines: Object.entries(next).map(([id, salesPriceAmount]) => ({
                        id,
                        salesPriceAmount,
                      })),
                    });
                  }}
                />
              ) : null}
              {!isSingleOrder ? (
                <>
              <tr {...legacySheetRowProps("transport-fee", 33)}>
                <SheetLabelCell label="是否代收代付大交通（火车票，机票）" emphasis danger required />
                <SheetValueCell
                  label="是否代收代付大交通"
                  value={previewBoolean("isCollectTransportFee", sales?.isCollectTransportFee)}
                  field="isCollectTransportFee"
                  editing={editing}
                  editor={<SheetEditableControl field="isCollectTransportFee" label="是否代收代付大交通" {...sheetEditorProps} />}
                  onActiveFieldChange={onActiveFieldChange}
                  tone="money"
                />
                <SheetLabelCell label="代收代付大交通金额" />
                <SheetValueCell
                  label="代收代付火车票金额"
                  value={`火车票：${previewText("collectTrainTicketAmount", sales?.collectTrainTicketAmount, "0")}，机票：${previewText("collectFlightTicketAmount", sales?.collectFlightTicketAmount, "0")}`}
                  field="collectTrainTicketAmount"
                  colSpan={8}
                  editing={editing}
                  editor={
                    <div className="order-handover-sheet-inline-pair">
                      <SheetEditableControl field="collectTrainTicketAmount" label="代收代付火车票金额" {...sheetEditorProps} />
                      <SheetEditableControl field="collectFlightTicketAmount" label="代收代付机票金额" {...sheetEditorProps} />
                    </div>
                  }
                  onActiveFieldChange={onActiveFieldChange}
                  tone="money"
                />
              </tr>
              <tr {...legacySheetRowProps("intangible-fee", 33)}>
                <SheetLabelCell label="是否代收代付非遗校企" emphasis danger required />
                <SheetValueCell
                  label="是否代收代付非遗校企"
                  value={previewBoolean("isCollectIntangibleOrSchoolEnterpriseFee", sales?.isCollectIntangibleOrSchoolEnterpriseFee)}
                  field="isCollectIntangibleOrSchoolEnterpriseFee"
                  editing={editing}
                  editor={<SheetEditableControl field="isCollectIntangibleOrSchoolEnterpriseFee" label="是否代收代付非遗校企" {...sheetEditorProps} />}
                  onActiveFieldChange={onActiveFieldChange}
                  tone="money"
                />
                <SheetLabelCell label="代收代付非遗金额" emphasis required />
                <SheetValueCell
                  label="代收代付非遗金额"
                  value={previewText("collectIntangibleAmount", sales?.collectIntangibleAmount, "0")}
                  field="collectIntangibleAmount"
                  editing={editing}
                  editor={<SheetEditableControl field="collectIntangibleAmount" label="代收代付非遗金额" {...sheetEditorProps} />}
                  onActiveFieldChange={onActiveFieldChange}
                  tone="money"
                />
                <SheetLabelCell label="代收代付校企金额" emphasis required />
                <SheetValueCell
                  label="代收代付校企金额"
                  value={previewText("collectSchoolEnterpriseAmount", sales?.collectSchoolEnterpriseAmount, "0")}
                  field="collectSchoolEnterpriseAmount"
                  colSpan={6}
                  editing={editing}
                  editor={<SheetEditableControl field="collectSchoolEnterpriseAmount" label="代收代付校企金额" {...sheetEditorProps} />}
                  onActiveFieldChange={onActiveFieldChange}
                  tone="money"
                />
              </tr>
              <tr {...legacySheetRowProps("teacher-pos", 33)}>
                <SheetLabelCell label="老师是否当地刷卡" emphasis />
                <SheetValueCell
                  label="老师是否当地刷卡"
                  value={previewBoolean("teacherLocalPosFlag", sales?.teacherLocalPosFlag)}
                  field="teacherLocalPosFlag"
                  colSpan={10}
                  editing={editing}
                  editor={<SheetEditableControl field="teacherLocalPosFlag" label="老师是否当地刷卡" {...sheetEditorProps} />}
                  onActiveFieldChange={onActiveFieldChange}
                  tone="teacher"
                />
              </tr>
                </>
              ) : null}
              <tr {...legacySheetRowProps("pay", 36)}>
                <SheetLabelCell label="旅游费用支付时间" emphasis required />
                <SheetValueCell
                  label="支付时间"
                  value={previewText("travelFeePayTimeText", sales?.travelFeePayTimeText, "待填写")}
                  field="travelFeePayTimeText"
                  editing={editing}
                  editor={<SheetEditableControl field="travelFeePayTimeText" label="支付时间" {...sheetEditorProps} />}
                  onActiveFieldChange={onActiveFieldChange}
                  tone="money"
                />
                <SheetLabelCell label="收款账户" />
                <SheetValueCell
                  label="收款账户"
                  value={previewText("receivingAccountText", sales?.receivingAccountText, "待填写")}
                  field="receivingAccountText"
                  colSpan={2}
                  editing={editing}
                  editor={<SheetEditableControl field="receivingAccountText" label="收款账户" {...sheetEditorProps} />}
                  onActiveFieldChange={onActiveFieldChange}
                  tone="money"
                />
                <SheetLabelCell label="收款备注" highlight />
                <SheetValueCell
                  label="收款备注"
                  value={previewText("receivingRemark", sales?.receivingRemark, "待填写")}
                  field="receivingRemark"
                  colSpan={2}
                  editing={editing}
                  editor={<SheetEditableControl field="receivingRemark" label="收款备注" {...sheetEditorProps} />}
                  onActiveFieldChange={onActiveFieldChange}
                  tone="money"
                  highlight
                />
                <SheetLabelCell label="实际收款总计" />
                <SheetValueCell label="实际收款总计" value={actualReceivableTotalText} tone="danger" highlight />
                <SheetValueCell label="备注" value="" />
              </tr>
              <tr {...legacySheetRowProps("contract-version", 36)}>
                <SheetLabelCell label="合同版本" emphasis danger required />
                <SheetValueCell
                  label="合同类型"
                  value={previewText("contractTemplateType", sales?.contractTemplateType, "待选择")}
                  field="contractTemplateType"
                  colSpan={4}
                  editing={editing}
                  editor={<SheetEditableControl field="contractTemplateType" label="合同类型" {...sheetEditorProps} />}
                  onActiveFieldChange={onActiveFieldChange}
                  tone="link"
                />
                <SheetLabelCell label="签团机制" />
                <SheetValueCell
                  label="签团机制"
                  value={previewText("signTeamStyle", sales?.signTeamStyle, "正常")}
                  field="signTeamStyle"
                  colSpan={4}
                  editing={editing}
                  editor={<SheetEditableControl field="signTeamStyle" label="签团机制" {...sheetEditorProps} />}
                  onActiveFieldChange={onActiveFieldChange}
                  tone="contract"
                  highlight
                />
                <SheetValueCell label="空白" value="" />
              </tr>
              {!isSingleOrder ? (
                <>
              <tr {...legacySheetRowProps("banner", 32)}>
                <SheetLabelCell label="横幅内容" />
                <SheetValueCell
                  label="横幅内容"
                  value={previewText("bannerDesc", sales?.bannerDesc, "待填写")}
                  field="bannerDesc"
                  colSpan={6}
                  editing={editing}
                  editor={<SheetEditableControl field="bannerDesc" label="横幅内容" {...sheetEditorProps} />}
                  onActiveFieldChange={onActiveFieldChange}
                  tone="route"
                />
                <SheetLabelCell label="横幅颜色" />
                <SheetValueCell
                  label="横幅颜色"
                  value={previewText("bannerColor", sales?.bannerColor, "待填写")}
                  field="bannerColor"
                  colSpan={2}
                  editing={editing}
                  editor={<SheetEditableControl field="bannerColor" label="横幅颜色" {...sheetEditorProps} />}
                  onActiveFieldChange={onActiveFieldChange}
                  tone="route"
                />
                <SheetValueCell label="空白" value="" />
              </tr>
                </>
              ) : null}
              <tr {...legacySheetRowProps("invoice", 36)}>
                <SheetLabelCell label={invoiceRequirementLabel} emphasis danger />
                <SheetValueCell
                  label="发票要求"
                  value={previewText("invoiceDesc", sales?.invoiceDesc, "待填写")}
                  field="invoiceDesc"
                  colSpan={4}
                  editing={editing}
                  editor={<SheetEditableControl field="invoiceDesc" label="发票要求" {...sheetEditorProps} />}
                  onActiveFieldChange={onActiveFieldChange}
                  tone="money"
                />
                <SheetLabelCell label="发票金额" emphasis danger required />
                <SheetValueCell
                  label="当地发票要求金额"
                  value={previewText("localInvoiceAmountDesc", sales?.localInvoiceAmountDesc, "待填写")}
                  field="localInvoiceAmountDesc"
                  colSpan={4}
                  editing={editing}
                  editor={<SheetEditableControl field="localInvoiceAmountDesc" label="当地发票要求金额" {...sheetEditorProps} />}
                  onActiveFieldChange={onActiveFieldChange}
                  tone="money"
                />
                <SheetValueCell label="空白" value="" />
              </tr>
              {!isSingleOrder ? (
                <>
              <tr {...legacySheetRowProps("go-out", 33)}>
                <SheetLabelCell label="行程说明补充" />
                <SheetValueCell
                  label="行程说明补充"
                  value={previewText("goOutRemark", sales?.goOutRemark, "待填写")}
                  field="goOutRemark"
                  colSpan={4}
                  editing={editing}
                  editor={<SheetEditableControl field="goOutRemark" label="行程说明补充" {...sheetEditorProps} />}
                  onActiveFieldChange={onActiveFieldChange}
                  tone="route"
                />
                <SheetLabelCell label="其他" />
                <SheetValueCell
                  label="其他"
                  value={mergeLegacyPreviewText(
                    previewText("otherRemark", sales?.otherRemark, ""),
                    previewText("giftRemark", sales?.giftRemark, ""),
                    "礼品发放",
                  )}
                  field="otherRemark"
                  colSpan={4}
                  editing={editing}
                  editor={
                    <div className="order-handover-sheet-inline-pair">
                      <SheetEditableControl field="otherRemark" label="其他" {...sheetEditorProps} />
                      <SheetEditableControl field="giftRemark" label="礼品发放" {...sheetEditorProps} />
                    </div>
                  }
                  onActiveFieldChange={onActiveFieldChange}
                  tone="neutral"
                />
                <SheetValueCell label="空白" value="" />
              </tr>
              <tr {...legacySheetRowProps("interview", 33)}>
                <SheetLabelCell label="采访要求" />
                <SheetValueCell
                  label="采访要求"
                  value={previewText("interviewRemark", sales?.interviewRemark, "待填写")}
                  field="interviewRemark"
                  colSpan={4}
                  editing={editing}
                  editor={<SheetEditableControl field="interviewRemark" label="采访要求" {...sheetEditorProps} />}
                  onActiveFieldChange={onActiveFieldChange}
                  tone="route"
                />
                <SheetLabelCell label="领队要求" />
                <SheetValueCell
                  label="领队要求"
                  value={mergeLegacyPreviewText(
                    previewText("guideRemark", sales?.guideRemark, ""),
                    previewText("teamCareRequirement", sales?.teamCareRequirement, ""),
                    "团中照顾要求",
                  )}
                  field="guideRemark"
                  colSpan={4}
                  editing={editing}
                  editor={
                    <div className="order-handover-sheet-inline-pair">
                      <SheetEditableControl field="guideRemark" label="领队要求" {...sheetEditorProps} />
                      <SheetEditableControl
                        field="teamCareRequirement"
                        label="团中照顾要求"
                        {...sheetEditorProps}
                      />
                    </div>
                  }
                  onActiveFieldChange={onActiveFieldChange}
                  tone="route"
                />
                <SheetValueCell label="空白" value="" />
              </tr>
              <tr {...legacySheetRowProps("teacher-standard", 33)}>
                <SheetLabelCell label="老师服务标准" />
                <SheetValueCell
                  label="老师住宿标准"
                  value={`住：${previewText("teacherHouseRemark", sales?.teacherHouseRemark, "待填写")}`}
                  field="teacherHouseRemark"
                  colSpan={2}
                  editing={editing}
                  required
                  editor={<SheetEditableControl field="teacherHouseRemark" label="老师住宿标准" {...sheetEditorProps} />}
                  onActiveFieldChange={onActiveFieldChange}
                  tone="teacher"
                />
                <SheetValueCell
                  label="老师餐标"
                  value={`餐补：${previewText("teacherFoodRemark", sales?.teacherFoodRemark, "待填写")}`}
                  field="teacherFoodRemark"
                  colSpan={3}
                  editing={editing}
                  required
                  editor={<SheetEditableControl field="teacherFoodRemark" label="老师餐标" {...sheetEditorProps} />}
                  onActiveFieldChange={onActiveFieldChange}
                  tone="teacher"
                />
                <SheetValueCell
                  label="老师门票要求"
                  value={`门票：${previewText("teacherTicketRemark", sales?.teacherTicketRemark, "待填写")}`}
                  field="teacherTicketRemark"
                  colSpan={2}
                  editing={editing}
                  required
                  editor={<SheetEditableControl field="teacherTicketRemark" label="老师门票要求" {...sheetEditorProps} />}
                  onActiveFieldChange={onActiveFieldChange}
                  tone="teacher"
                />
                <SheetValueCell
                  label="老师交通要求"
                  value={`大交通：${previewText("teacherTransportRemark", sales?.teacherTransportRemark, "待填写")}`}
                  field="teacherTransportRemark"
                  colSpan={3}
                  editing={editing}
                  required
                  editor={<SheetEditableControl field="teacherTransportRemark" label="老师交通要求" {...sheetEditorProps} />}
                  onActiveFieldChange={onActiveFieldChange}
                  tone="teacher"
                />
              </tr>
              <tr {...legacySheetRowProps("arbitration", 33)}>
                <SheetLabelCell label="仲裁单位" />
                <SheetValueCell
                  label="合同中的仲裁单位描述"
                  value={previewText("arbitrationDesc", sales?.arbitrationDesc, "待填写")}
                  field="arbitrationDesc"
                  colSpan={10}
                  editing={editing}
                  editor={<SheetEditableControl field="arbitrationDesc" label="合同中的仲裁单位描述" {...sheetEditorProps} />}
                  onActiveFieldChange={onActiveFieldChange}
                  tone="contract"
                />
              </tr>
                </>
              ) : null}
              <tr {...legacySheetRowProps("contact-title", 33)}>
                <SheetValueCell className="order-handover-sheet-contact-title" label="联系方式" value="联系方式" colSpan={11} />
              </tr>
              <tr {...legacySheetRowProps("contact-header", 33)}>
                <SheetLabelCell label="客户姓名" />
                <SheetLabelCell label="职务" />
                <SheetLabelCell label="电话" />
                <SheetLabelCell label="带队注意事项" colSpan={8} />
              </tr>
              {teacherContacts.map((member) => (
                <tr key={member.id} {...legacySheetRowProps("contact-member", 33)}>
                  <SheetValueCell label="客户姓名" value={member.name} />
                  <SheetValueCell label="职务" value={member.teacherType ?? member.role} />
                  <SheetValueCell label="电话" value={member.phone ?? ""} />
                  <SheetValueCell label="带队注意事项" value={member.guidanceNote ?? ""} colSpan={8} />
                </tr>
              ))}
              {sales?.contractModifyReason || editing ? (
                <tr {...legacySheetRowProps("contract-modify-reason", 33)}>
                  <SheetLabelCell label="合同修改原因" emphasis danger required />
                  <SheetValueCell
                    label="合同修改原因"
                    value={previewText("contractModifyReason", sales?.contractModifyReason, "待填写")}
                    field="contractModifyReason"
                    colSpan={10}
                    editing={editing}
                    editor={<SheetEditableControl field="contractModifyReason" label="合同修改原因" {...sheetEditorProps} />}
                    onActiveFieldChange={onActiveFieldChange}
                    tone="warning"
                  />
                </tr>
              ) : null}
              {sales?.isAdvanceMoney || editing ? (
                <tr {...legacySheetRowProps("advance-money", 33)}>
                  <SheetLabelCell label="是否虚拟团" emphasis danger required />
                  <SheetValueCell
                    label="是否虚拟团"
                    value={previewBoolean("isAdvanceMoney", sales?.isAdvanceMoney)}
                    field="isAdvanceMoney"
                    colSpan={10}
                    editing={editing}
                    editor={<SheetEditableControl field="isAdvanceMoney" label="是否虚拟团" {...sheetEditorProps} />}
                    onActiveFieldChange={onActiveFieldChange}
                    tone="contract"
                  />
                </tr>
              ) : null}
            </tbody>
          </table>
          </div>
        </div>
        </div>
      </div>
    </div>
    </SalesRequiredMissingContext.Provider>
  );
}

/** 过渡期保留原销售交接分组语义，便于旧断言和辅助测试函数稳定迁移。 */
function LegacySalesSectionIndex({
  amount,
  contractSignerDisplayName,
  orderLines,
  sales,
  signType = "团队",
}: {
  amount: string;
  contractSignerDisplayName: string;
  orderLines: OrderHandoverOrderLine[];
  sales?: OrderHandoverSalesHandover;
  signType?: string;
}) {
  const isSingleCar = signType === "单订车";
  const isSingleHotel = signType === "单订房";
  const isSingleOrder = isSingleCar || isSingleHotel;
  return (
    <div className="order-handover-sales-sections order-handover-sales-section-index" aria-hidden="true">
      <SalesSection title="基础交接">
        <div className="order-handover-sales-grid">
          <InfoBlock label="新客户类型" value={sales?.customerNoveltyType ?? "待确认"} />
          <InfoBlock label="服务顾问" value={sales?.serviceConsultantName ?? "待填写"} />
          <InfoBlock label="抓单人" value={sales?.catchOrderUserName ?? "待填写"} />
          <InfoBlock label="签单人" value={sales?.salesUserName ?? "待选择"} />
          <InfoBlock label="计调执行人" value={sales?.dispatcherUserName ?? "待选择"} />
          <InfoBlock label="主签合同人" value={contractSignerDisplayName} tone="link" />
          <InfoBlock label="院系专业" value={displayText(sales?.majorNamesText, "待填写")} wide />
          <InfoBlock label="写明校区或具体地址" value={displayText(sales?.schoolSiteText, "待维护校区地址")} wide note />
        </div>
      </SalesSection>
      <SalesSection title="合同数据">
        <div className="order-handover-sales-grid is-contract-grid">
          <InfoBlock label="是否虚拟团" value={formatBooleanValue(sales?.isAdvanceMoney)} />
          <InfoBlock label="合同类型" value={displayText(sales?.contractTemplateType, "待选择")} tone="link" />
          <InfoBlock label="收款账户" value={displayText(sales?.receivingAccountText, "待填写")} wide note />
          <InfoBlock label="收款备注" value={displayText(sales?.receivingRemark, "待填写")} wide note />
          <InfoBlock label="发票要求" value={displayText(sales?.invoiceDesc, "待填写")} wide />
          <InfoBlock label="当地发票要求金额" value={displayText(sales?.localInvoiceAmountDesc, "待填写")} wide />
          <InfoBlock label="签团机制" value={displayText(sales?.signTeamStyle, "正常")} />
          <InfoBlock label="合同人数" value={toInputText(sales?.contractPeopleCount) || "待填写"} />
          <InfoBlock label="合同单价" value={displayText(sales?.contractUnitPriceAmount, "待填写")} />
          <InfoBlock label="合同总金额" value={amount} tone="danger" />
          <InfoBlock label="支付时间" value={displayText(sales?.travelFeePayTimeText, "待填写")} full note />
        </div>
      </SalesSection>
      {isSingleOrder ? (
        <SalesSection title={isSingleCar ? "订车" : "订房"}>
          <div className="order-handover-sales-grid">
            {filterSingleOrderPricingLines(orderLines, isSingleCar ? "单订车" : "单订房").length ===
            0 ? (
              <InfoBlock label={isSingleCar ? "用车明细" : "酒店明细"} value={isSingleCar ? "暂无销售行，请先维护核价包车行" : "暂无销售行，请先维护确认件住宿行"} wide />
            ) : (
              filterSingleOrderPricingLines(orderLines, isSingleCar ? "单订车" : "单订房").map((line) => (
                <InfoBlock
                  key={line.id}
                  label={line.lineName}
                  value={`${line.quantity}${line.unit} × ${line.salesUnitPriceAmount} = ${line.salesTotalAmount}`}
                  wide
                />
              ))
            )}
            <InfoBlock
              label="单订服务费"
              value={displayText(sales?.singleOrderServiceFee, "0")}
            />
            <InfoBlock
              label="总销售额"
              value={sumOrderLineAmounts(
                filterSingleOrderPricingLines(orderLines, isSingleCar ? "单订车" : "单订房"),
              )}
              tone="danger"
            />
          </div>
        </SalesSection>
      ) : (
        <SalesSection title="出团要求">
          <div className="order-handover-sales-grid">
            <InfoBlock label="是否代收代付大交通" value={formatBooleanValue(sales?.isCollectTransportFee)} />
            <InfoBlock label="代收代付火车票金额" value={displayText(sales?.collectTrainTicketAmount, "待填写")} />
            <InfoBlock label="代收代付机票金额" value={displayText(sales?.collectFlightTicketAmount, "待填写")} />
            <InfoBlock label="老师住宿标准" value={displayText(sales?.teacherHouseRemark, "待填写")} wide />
            <InfoBlock label="老师餐标" value={displayText(sales?.teacherFoodRemark, "待填写")} wide />
            <InfoBlock label="老师门票要求" value={displayText(sales?.teacherTicketRemark, "待填写")} wide />
            <InfoBlock label="老师交通要求" value={displayText(sales?.teacherTransportRemark, "待填写")} wide />
            <InfoBlock label="领队要求" value={displayText(sales?.guideRemark, "待填写")} wide note />
            <InfoBlock label="是否代收代付非遗校企" value={formatBooleanValue(sales?.isCollectIntangibleOrSchoolEnterpriseFee)} />
            <InfoBlock label="代收代付非遗金额" value={displayText(sales?.collectIntangibleAmount, "待填写")} />
            <InfoBlock label="代收代付校企金额" value={displayText(sales?.collectSchoolEnterpriseAmount, "待填写")} />
            <InfoBlock label="老师是否当地刷卡" value={formatBooleanValue(sales?.teacherLocalPosFlag)} />
            <InfoBlock label="礼品发放" value={displayText(sales?.giftRemark, "待填写")} />
            <InfoBlock label="横幅内容" value={displayText(sales?.bannerDesc, "待填写")} />
            <InfoBlock label="横幅颜色" value={displayText(sales?.bannerColor, "待填写")} />
            <InfoBlock label="行程说明补充" value={displayText(sales?.goOutRemark, "待填写")} wide note />
            <InfoBlock label="其他" value={displayText(sales?.otherRemark, "待填写")} />
            <InfoBlock label="采访要求" value={displayText(sales?.interviewRemark, "待填写")} />
            <InfoBlock label="团中需要照顾到的附加要求" value={displayText(sales?.teamCareRequirement, "待填写")} wide note />
            <InfoBlock label="合同中的仲裁单位描述" value={displayText(sales?.arbitrationDesc, "待填写")} wide note />
            <InfoBlock label="合同修改原因" value={displayText(sales?.contractModifyReason, "待填写")} wide note />
          </div>
        </SalesSection>
      )}
    </div>
  );
}

/** 单订车/单订房价目区：对齐老系统 SingleBusInfo / SingleHouseInfo，草稿可编辑销售价与单订服务费。 */
function SingleOrderPricingSheetRows({
  orderLines,
  signType,
  editing,
  salePriceById,
  serviceFee,
  onSalePriceChange,
  onServiceFeeChange,
}: {
  orderLines: OrderHandoverOrderLine[];
  signType: "单订车" | "单订房";
  editing: boolean;
  salePriceById: Record<string, string>;
  serviceFee: string;
  onSalePriceChange: (lineId: string, value: string) => void;
  onServiceFeeChange: (value: string) => void;
}) {
  const sectionLabel = signType === "单订车" ? "订车" : "订房";
  const emptyLabel = signType === "单订车" ? "暂无用车销售行" : "暂无酒店销售行";
  // 受控输入必须用本地 state；父级 onDraft 只写 ref，不会触发重渲染。
  const [localSalePrices, setLocalSalePrices] = useState<Record<string, string>>(salePriceById);
  const [localServiceFee, setLocalServiceFee] = useState(serviceFee);
  const lineIdsKey = orderLines.map((line) => line.id).join("|");

  useEffect(() => {
    // 仅在进入/退出编辑或行集合变化时同步，避免父级重渲染把正在输入的内容冲掉。
    // salePriceById / serviceFee 故意不进依赖：它们在编辑过程中由本地 state 接管。
    setLocalSalePrices(salePriceById);
    setLocalServiceFee(serviceFee);
  }, [editing, lineIdsKey]);

  const saleTotal = orderLines.reduce((sum, line) => {
    const amount = Number.parseFloat(
      String(localSalePrices[line.id] ?? line.salesTotalAmount).replace(/[^\d.-]/g, ""),
    );
    return sum + (Number.isFinite(amount) ? amount : 0);
  }, 0);
  const feeAmount = Number.parseFloat(String(localServiceFee).replace(/[^\d.-]/g, ""));
  const feeTotal = Number.isFinite(feeAmount) ? feeAmount : 0;

  /** 更新单行销售价本地态并回写草稿。 */
  function handleSalePriceChange(lineId: string, value: string) {
    setLocalSalePrices((current) => ({ ...current, [lineId]: value }));
    onSalePriceChange(lineId, value);
  }

  /** 更新服务费本地态并回写草稿。 */
  function handleServiceFeeChange(value: string) {
    setLocalServiceFee(value);
    onServiceFeeChange(value);
  }

  return (
    <>
      <tr {...legacySheetRowProps("single-order-pricing-title", 33)}>
        <SheetValueCell
          className="order-handover-sheet-contact-title"
          label={sectionLabel}
          value={sectionLabel}
          colSpan={11}
        />
      </tr>
      <tr {...legacySheetRowProps("single-order-pricing-header", 33)}>
        <SheetLabelCell label={signType === "单订车" ? "车型/项目" : "酒店/项目"} colSpan={3} />
        <SheetLabelCell label="成本明细" colSpan={3} />
        <SheetLabelCell label="销售价" colSpan={2} emphasis />
        <SheetLabelCell label="说明" colSpan={3} />
      </tr>
      {orderLines.length === 0 ? (
        <tr {...legacySheetRowProps("single-order-pricing-empty", 33)}>
          <SheetValueCell label={emptyLabel} value={emptyLabel} colSpan={11} />
        </tr>
      ) : (
        orderLines.map((line) => {
          const salePrice = localSalePrices[line.id] ?? line.salesTotalAmount;
          return (
            <tr key={line.id} {...legacySheetRowProps("single-order-pricing-line", 33)}>
              <SheetValueCell label={line.lineName} value={line.lineName} colSpan={3} />
              <SheetValueCell
                label="成本明细"
                value={formatSingleOrderCostBasis(line)}
                colSpan={3}
              />
              <td
                className={`order-handover-sheet-value order-handover-sheet-cell order-handover-single-order-input-cell${editing ? " is-editable is-inline-editing" : ""}`}
                colSpan={2}
                data-label="销售价"
                onMouseDown={(event) => event.stopPropagation()}
                onClick={(event) => event.stopPropagation()}
              >
                {editing ? (
                  <Input
                    aria-label={`${line.lineName}销售价`}
                    className="order-handover-single-order-sale-input"
                    value={salePrice}
                    onChange={handleSalePriceChange.bind(null, line.id)}
                    onClick={(event) => event.stopPropagation()}
                    onMouseDown={(event) => event.stopPropagation()}
                  />
                ) : (
                  <strong className="is-money">{salePrice}</strong>
                )}
              </td>
              <SheetValueCell label="说明" value={line.lineDesc ?? ""} colSpan={3} />
            </tr>
          );
        })
      )}
      <tr {...legacySheetRowProps("single-order-service-fee", 33)}>
        <SheetLabelCell label="单订服务费" colSpan={6} emphasis />
        <td
          className={`order-handover-sheet-value order-handover-sheet-cell order-handover-single-order-input-cell${editing ? " is-editable is-inline-editing" : ""}`}
          colSpan={5}
          data-label="单订服务费"
          onMouseDown={(event) => event.stopPropagation()}
          onClick={(event) => event.stopPropagation()}
        >
          {editing ? (
            <Input
              aria-label="单订服务费"
              className="order-handover-single-order-sale-input"
              value={localServiceFee}
              onChange={handleServiceFeeChange}
              onClick={(event) => event.stopPropagation()}
              onMouseDown={(event) => event.stopPropagation()}
            />
          ) : (
            <strong className="is-money">{localServiceFee || "0"}</strong>
          )}
        </td>
      </tr>
      <tr {...legacySheetRowProps("single-order-pricing-total", 33)}>
        <SheetLabelCell label="总销售额" colSpan={6} />
        <SheetValueCell
          label="总销售额"
          value={`${saleTotal.toFixed(2)}元（不含服务费 ${feeTotal.toFixed(2)}）`}
          colSpan={5}
          tone="danger"
          highlight
        />
      </tr>
    </>
  );
}

/** 对齐老系统成本展示：3辆*1000元=3000元。 */
function formatSingleOrderCostBasis(line: OrderHandoverOrderLine): string {
  const qty = String(line.quantity || "").trim() || "1";
  const unit = String(line.unit || "").trim();
  const unitPrice = String(line.costUnitPriceAmount || line.salesUnitPriceAmount || "").trim() || "0";
  const total = String(line.costTotalAmount || "").trim();
  const computedTotal =
    total ||
    (() => {
      const q = Number.parseFloat(qty);
      const p = Number.parseFloat(unitPrice);
      if (!Number.isFinite(q) || !Number.isFinite(p)) {
        return "";
      }
      return (q * p).toFixed(2);
    })();
  if (!computedTotal) {
    return `${qty}${unit}`;
  }
  return `${qty}${unit}*${unitPrice}元=${computedTotal}元`;
}

/** 合并草稿销售价与已保存销售行。 */
function buildSingleOrderSalePriceDraft(
  orderLines: OrderHandoverOrderLine[],
  draftLines: Array<{ id: string; salesPriceAmount: string }> | undefined,
): Record<string, string> {
  const result: Record<string, string> = {};
  for (const line of orderLines) {
    result[line.id] = line.salesTotalAmount;
  }
  for (const item of draftLines ?? []) {
    if (item.id) {
      result[item.id] = item.salesPriceAmount;
    }
  }
  return result;
}

/** 单订车只展示包车行，单订房只展示住宿行；排除单订服务费行。 */
function filterSingleOrderPricingLines(
  orderLines: OrderHandoverOrderLine[],
  signType: "单订车" | "单订房",
): OrderHandoverOrderLine[] {
  const resourceType = signType === "单订车" ? "包车" : "住宿";
  const matched = orderLines.filter((line) => {
    if (isSingleOrderServiceFeeLine(line)) {
      return false;
    }
    if (line.resourceType === resourceType) {
      return true;
    }
    if (line.sourceMode === "quote_budget" || line.sourceMode === "fulfillment_copy") {
      return true;
    }
    return line.lineTypeCode === "resource_service" && !line.resourceType;
  });
  if (matched.length > 0) {
    return matched;
  }
  return orderLines.filter(
    (line) => line.lineTypeCode !== "traveler_group" && !isSingleOrderServiceFeeLine(line),
  );
}

/** 判断销售行是否为单订服务费行。 */
function isSingleOrderServiceFeeLine(line: OrderHandoverOrderLine): boolean {
  const name = line.lineName?.trim();
  return name === "单订服务费" || name === "单订费用";
}

/** 汇总销售行金额文本。 */
function sumOrderLineAmounts(orderLines: OrderHandoverOrderLine[]): string {
  const total = orderLines.reduce((sum, line) => {
    const amount = Number.parseFloat(String(line.salesTotalAmount).replace(/[^\d.-]/g, ""));
    return sum + (Number.isFinite(amount) ? amount : 0);
  }, 0);
  return `¥${total.toFixed(2)}`;
}

/** 按签单类型解析合同交接表标题。 */
function resolveContractHandoverSheetTitle(signType?: string): string {
  const normalized = signType?.trim();
  if (normalized === "单订车") {
    return "单订车线上合同交接表";
  }
  if (normalized === "单订房") {
    return "单订房线上合同交接表";
  }
  return "团队线上合同交接表";
}

/** 线路展示：定稿后优先用团号快照（省份码+日期+标题），否则显示行程标题。 */
function formatContractRouteDisplay({
  routeNameSnapshot,
  title,
}: {
  routeNameSnapshot?: string;
  title?: string;
}): string {
  const snapshot = routeNameSnapshot?.trim();
  if (snapshot) {
    return snapshot;
  }
  return title?.trim() || "待选择行程";
}

/** 渲染旧系统表格中的字段标题单元格。 */
function SheetLabelCell({
  label,
  colSpan,
  rowSpan,
  emphasis,
  highlight,
  danger,
  required,
}: {
  label: string;
  colSpan?: number;
  rowSpan?: number;
  emphasis?: boolean;
  highlight?: boolean;
  danger?: boolean;
  required?: boolean;
}) {
  return (
    <td
      className={[
        "order-handover-sheet-label",
        emphasis ? "is-emphasis" : "",
        highlight ? "is-highlight" : "",
        danger ? "is-danger" : "",
      ].filter(Boolean).join(" ")}
      colSpan={colSpan}
      rowSpan={rowSpan}
    >
      {label}
      {required ? <i className="order-handover-sheet-required">*</i> : null}
    </td>
  );
}

/** 生成旧系统行高和调试标识属性。 */
function legacySheetRowProps(row: string, height: number): Record<string, string> {
  return {
    "data-legacy-row": row,
    height: String(height),
  };
}

/** 渲染旧系统表格中的字段值单元格；编辑态直接内联控件，不再弹窗。 */
function SheetValueCell({
  label,
  value,
  field,
  editing,
  editor,
  colSpan,
  rowSpan,
  tone,
  highlight,
  className,
  cellRef,
  title,
  topHint,
  required,
  onClick,
  onActiveFieldChange,
}: {
  label: string;
  value: string;
  field?: SalesSheetFieldName;
  editing?: boolean;
  editor?: ReactNode;
  colSpan?: number;
  rowSpan?: number;
  tone?: SalesInfoTone;
  highlight?: boolean;
  className?: string;
  cellRef?: RefObject<HTMLTableCellElement | null>;
  title?: string;
  topHint?: string;
  required?: boolean;
  onClick?: () => void;
  onActiveFieldChange?: (field: SalesSheetFieldName | undefined) => void;
}) {
  const requiredMissingFields = useContext(SalesRequiredMissingContext);
  const requiredMissing = Boolean(field && requiredMissingFields.has(field));
  const displayTone = tone ?? getSalesInfoTone(label);
  const showInlineEditor = Boolean(editing && editor);
  return (
    <td
      ref={cellRef}
      className={[
        "order-handover-sheet-value",
        "order-handover-sheet-cell",
        highlight ? "is-highlight" : "",
        field ? "is-sheet-field" : "",
        showInlineEditor ? "is-inline-editing" : "",
        editing && field ? "is-editable" : "",
        requiredMissing ? "is-required-missing" : "",
        onClick ? "is-clickable" : "",
        topHint ? "has-top-hint" : "",
        className ?? "",
      ].filter(Boolean).join(" ")}
      colSpan={colSpan}
      rowSpan={rowSpan}
      data-label={label}
      data-required-field={field}
      title={title}
      onClick={() => {
        if (onClick) {
          onClick();
          return;
        }
        if (field && editing) {
          onActiveFieldChange?.(field);
        }
      }}
    >
      <span>
        {label}
        {required ? <i className="order-handover-sheet-required">*</i> : null}
      </span>
      {showInlineEditor ? (
        <div
          className="order-handover-sheet-inline-editor"
          onClick={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
        >
          {required ? <i className="order-handover-sheet-required">*</i> : null}
          {editor}
        </div>
      ) : (
        <div className="order-handover-sheet-value-body">
          {topHint ? <em className="order-handover-sheet-top-hint">{topHint}</em> : null}
          <strong className={displayTone ? `is-${displayTone}` : undefined}>
            {required ? <i className="order-handover-sheet-required">*</i> : null}
            {value}
          </strong>
        </div>
      )}
    </td>
  );
}

/** 根据销售交接字段类型渲染表格内编辑控件。 */
function SheetEditableControl({
  field,
  label,
  catchOrderSelectOptions,
  contractSignerOptions,
  dispatcherSelectOptions,
  majorNameOptions,
  majorOptionsLoading,
  onActiveFieldChange,
  salesUserSelectOptions,
  schoolSiteOptions,
  schoolSiteOptionsLoading,
  serviceConsultantSelectOptions,
  staffOptionsLoading,
}: {
  field: SalesSheetFieldName;
  label: string;
  catchOrderSelectOptions: OrderHandoverStaffOption[];
  contractSignerOptions: EditableSelectOption[];
  dispatcherSelectOptions: OrderHandoverStaffOption[];
  majorNameOptions: EditableSelectOption[];
  majorOptionsLoading: boolean;
  onActiveFieldChange: (field: SalesSheetFieldName | undefined) => void;
  salesUserSelectOptions: OrderHandoverStaffOption[];
  schoolSiteOptions: EditableSelectOption[];
  schoolSiteOptionsLoading: boolean;
  serviceConsultantSelectOptions: OrderHandoverStaffOption[];
  staffOptionsLoading: boolean;
}) {
  const commonProps = {
    onFocus: () => onActiveFieldChange(field),
    onClick: () => onActiveFieldChange(field),
  };
  if (field === "majorNames") {
    return (
      <Form.Item noStyle field="majorNames">
        <Select
          aria-label={label}
          data-testid="major-select-majorNames"
          mode="multiple"
          allowClear
          allowCreate
          showSearch
          loading={majorOptionsLoading}
          placeholder="请选择或输入院系专业"
          filterOption={(inputValue, option) => filterEditableOption(inputValue, option, majorNameOptions)}
          {...commonProps}
        >
          {majorNameOptions.map((option) => (
            <Select.Option key={option.value} value={option.value} disabled={option.disabled}>
              {option.label}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
    );
  }
  if (field === "schoolSiteText") {
    return (
      <Form.Item noStyle field="schoolSiteText">
        <Select
          aria-label={label}
          data-testid="school-site-select-schoolSiteText"
          allowClear
          allowCreate
          showSearch
          loading={schoolSiteOptionsLoading}
          placeholder="请输入或选择校区地址"
          filterOption={(inputValue, option) => filterEditableOption(inputValue, option, schoolSiteOptions)}
          {...commonProps}
        >
          {schoolSiteOptions.map((option) => (
            <Select.Option key={option.value} value={option.value} disabled={option.disabled}>
              {option.label}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
    );
  }
  if (isStaffSheetField(field)) {
    const options = getStaffSheetOptions(field, {
      catchOrderSelectOptions,
      dispatcherSelectOptions,
      salesUserSelectOptions,
      serviceConsultantSelectOptions,
    });
    return (
      <Form.Item noStyle field={field}>
        <Select
          aria-label={label}
          data-testid={`staff-select-${field}`}
          allowClear
          showSearch
          loading={staffOptionsLoading}
          placeholder={`请选择${label}`}
          filterOption={(inputValue, option) => filterStaffOption(inputValue, option, options)}
          {...commonProps}
        >
          {options.map((option) => (
            <Select.Option key={option.id} value={option.id} disabled={option.disabled}>
              {formatStaffOptionLabel(option)}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
    );
  }
  if (isBooleanSheetField(field)) {
    return (
      <Form.Item noStyle field={field}>
        <Select
          aria-label={label}
          data-testid={`boolean-select-${field}`}
          allowClear
          placeholder="请选择"
          {...commonProps}
        >
          {BOOLEAN_OPTIONS.map((option) => (
            <Select.Option key={option.value} value={option.value}>
              {option.label}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
    );
  }
  const optionMap: Partial<Record<SalesSheetFieldName, EditableSelectOption[]>> = {
    contractSignerMemberId: contractSignerOptions,
    contractTemplateType: CONTRACT_TEMPLATE_TYPE_OPTIONS,
    customerNoveltyType: CUSTOMER_NOVELTY_TYPE_OPTIONS,
    signTeamStyle: SIGN_TEAM_STYLE_OPTIONS,
  };
  const options = optionMap[field];
  if (options) {
    return (
      <Form.Item noStyle field={field}>
        <Select
          aria-label={label}
          data-testid={`info-select-${String(field)}`}
          allowClear
          showSearch
          placeholder={`请选择${label}`}
          filterOption={(inputValue, option) => filterEditableOption(inputValue, option, options)}
          {...commonProps}
        >
          {options.map((option) => (
            <Select.Option key={option.value} value={option.value} disabled={option.disabled}>
              {option.label}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
    );
  }
  const textareaFields = new Set<SalesSheetFieldName>([
    "arbitrationDesc",
    "bannerDesc",
    "contractModifyReason",
    "giftRemark",
    "goOutRemark",
    "guideRemark",
    "interviewRemark",
    "otherRemark",
    "receivingAccountText",
    "receivingRemark",
    "teamCareRequirement",
    "travelFeePayTimeText",
  ]);
  return (
    <Form.Item noStyle field={field}>
      {textareaFields.has(field) ? (
        <Input.TextArea
          aria-label={label}
          autoSize={{ minRows: 1, maxRows: 4 }}
          allowClear
          maxLength={field === "arbitrationDesc" ? 2000 : undefined}
          placeholder={`请输入${label}`}
          {...commonProps}
        />
      ) : (
        <Input aria-label={label} allowClear placeholder={`请输入${label}`} {...commonProps} />
      )}
    </Form.Item>
  );
}

/** 渲染合同金额区域中的实际应收行。 */
function ActualReceivableRow({
  contractAmount,
  contractPeopleCount,
  contractUnitPriceAmount,
  editing,
  line,
  onActiveFieldChange,
  rowName,
  rowSpan,
  sheetEditorProps,
}: {
  contractAmount?: string;
  contractPeopleCount?: number | string;
  contractUnitPriceAmount?: string;
  editing?: boolean;
  line: ActualReceivableLine;
  onActiveFieldChange?: (
    field: SalesSheetFieldName | undefined,
    label?: string,
    openEditor?: boolean,
  ) => void;
  rowName?: string;
  rowSpan?: number;
  sheetEditorProps?: Omit<Parameters<typeof SheetEditableControl>[0], "field" | "label">;
}) {
  return (
    <tr {...legacySheetRowProps(rowName ?? "actual-receivable", 33)}>
      {rowSpan ? (
        <>
          <SheetLabelCell label="合同人数" rowSpan={rowSpan} emphasis required />
          <SheetValueCell
            label="合同人数"
            value={toInputText(contractPeopleCount) || "待填写"}
            field="contractPeopleCount"
            rowSpan={rowSpan}
            editing={editing}
            editor={sheetEditorProps ? <SheetEditableControl field="contractPeopleCount" label="合同人数" {...sheetEditorProps} /> : undefined}
            onActiveFieldChange={onActiveFieldChange}
            tone="money"
          />
          <SheetLabelCell label="合同单价" rowSpan={rowSpan} emphasis required />
          <SheetValueCell
            label="合同单价"
            value={displayText(contractUnitPriceAmount, "待填写")}
            field="contractUnitPriceAmount"
            rowSpan={rowSpan}
            editing={editing}
            editor={sheetEditorProps ? <SheetEditableControl field="contractUnitPriceAmount" label="合同单价" {...sheetEditorProps} /> : undefined}
            onActiveFieldChange={onActiveFieldChange}
            tone="money"
          />
          <SheetValueCell label="合同总金额" value={contractAmount ?? "¥0.00"} rowSpan={rowSpan} tone="danger" highlight />
        </>
      ) : null}
      <SheetLabelCell label={line.label} />
      <SheetValueCell label={line.label} value={line.count} />
      <SheetLabelCell label="实际应收单价" />
      <SheetValueCell label={`${line.label}单价`} value={line.price} />
      <SheetValueCell label={`${line.label}金额`} value={line.total} highlight />
      <SheetValueCell label={`${line.label}备注`} value={line.remark} />
    </tr>
  );
}

/** 新增单个出行名单成员的抽屉表单。 */
function RosterMemberCreateForm({
  form,
  classGroupOptions,
  travelerTypeOptions,
  teacherTypeOptions,
  certificateTypeOptions,
  genderOptions,
}: {
  form: FormInstance<RosterMemberEditableDraft>;
  classGroupOptions: EditableSelectOption[];
  travelerTypeOptions: EditableSelectOption[];
  teacherTypeOptions: EditableSelectOption[];
  certificateTypeOptions: EditableSelectOption[];
  genderOptions: EditableSelectOption[];
}) {
  return (
    <Form
      form={form}
      layout="vertical"
      className="order-handover-roster-create-form"
      onValuesChange={(changed, values) => {
        syncRosterIdCardDerivedFields(form, changed, values);
      }}
    >
      <div className="order-handover-roster-create-grid">
        <Form.Item
          field="memberName"
          label="新增姓名"
          rules={[{ required: true, message: "请输入姓名" }]}
        >
          <Input aria-label="新增姓名" {...ROSTER_CREATE_TEXT_INPUT_PROPS} allowClear placeholder="请输入姓名" />
        </Form.Item>
        <Form.Item
          field="mobile"
          label="新增电话"
          rules={[
            { required: true, message: "请输入电话" },
            {
              validator: (value, callback) => {
                const errorMessage = getRosterMobileValidationError(String(value ?? ""));
                if (errorMessage) {
                  callback(errorMessage);
                  return;
                }
                callback();
              },
            },
          ]}
        >
          <Input
            aria-label="新增电话"
            {...ROSTER_CREATE_TEXT_INPUT_PROPS}
            allowClear
            inputMode="numeric"
            placeholder="请输入手机号"
          />
        </Form.Item>
        <RosterCreateSelectField
          field="travelerTypeLabel"
          label="人员类型"
          testId="roster-create-select-travelerTypeLabel"
          options={travelerTypeOptions}
          placeholder="请选择人员类型"
          allowCreate
        />
        <RosterCreateSelectField
          field="certificateType"
          label="证件类型"
          testId="roster-create-select-certificateType"
          options={certificateTypeOptions}
          placeholder="请选择证件类型"
          allowCreate
        />
        <Form.Item
          field="certificateNo"
          label="新增证件号"
          className="is-cert-no"
          rules={[
            { required: true, message: "请输入证件号" },
            {
              validator: (value, callback) => {
                const errorMessage = getRosterCertificateNoValidationError(
                  String(form.getFieldValue("certificateType") ?? ""),
                  String(value ?? ""),
                );
                if (errorMessage) {
                  callback(errorMessage);
                  return;
                }
                callback();
              },
            },
          ]}
        >
          <Input
            aria-label="新增证件号"
            {...ROSTER_CREATE_TEXT_INPUT_PROPS}
            allowClear
            placeholder="请输入证件号"
          />
        </Form.Item>
        <RosterCreateSelectField
          field="classGroupLabel"
          label="专业/班级"
          testId="roster-create-select-classGroupLabel"
          options={classGroupOptions}
          placeholder="请选择或输入专业/班级"
          allowCreate
        />
        <RosterCreateSelectField
          field="teacherTypeLabel"
          label="职务/职位"
          testId="roster-create-select-teacherTypeLabel"
          options={teacherTypeOptions}
          placeholder="请选择或输入职务/职位"
          allowCreate
        />
        <Form.Item field="studentNo" label="学号">
          <Input aria-label="新增学号" {...ROSTER_CREATE_TEXT_INPUT_PROPS} allowClear placeholder="请输入学号" />
        </Form.Item>
        <RosterCreateSelectField
          field="gender"
          label="性别"
          testId="roster-create-select-gender"
          options={genderOptions}
          placeholder="请选择性别"
          allowCreate
        />
        <Form.Item field="age" label="年龄">
          <Input aria-label="新增年龄" {...ROSTER_CREATE_TEXT_INPUT_PROPS} allowClear placeholder="请输入年龄" />
        </Form.Item>
        <Form.Item field="birthday" label="生日">
          <Input aria-label="新增生日" {...ROSTER_CREATE_TEXT_INPUT_PROPS} allowClear placeholder="YYYY-MM-DD" />
        </Form.Item>
        <Form.Item field="bigTrafficInfo" label="大交通信息" className="is-wide">
          <Input aria-label="新增大交通信息" {...ROSTER_CREATE_TEXT_INPUT_PROPS} allowClear placeholder="例如硬卧下铺、航班号" />
        </Form.Item>
        <Form.Item field="mainSignerAddress" label="主签合同人地址" className="is-wide">
          <Input
            aria-label="新增主签合同人地址"
            {...ROSTER_CREATE_TEXT_INPUT_PROPS}
            allowClear
            placeholder="请输入主签合同人地址"
          />
        </Form.Item>
        <Form.Item field="guidanceNote" label="特殊情况" className="is-wide">
          <Input.TextArea
            aria-label="新增特殊情况"
            autoComplete="off"
            autoSize={{ minRows: 2, maxRows: 4 }}
            allowClear
            placeholder="请输入特殊情况"
          />
        </Form.Item>
        <Form.Item field="remark" label="销售备注" className="is-wide">
          <Input.TextArea
            aria-label="新增销售备注"
            autoComplete="off"
            autoSize={{ minRows: 2, maxRows: 4 }}
            allowClear
            placeholder="请输入销售备注"
          />
        </Form.Item>
      </div>
    </Form>
  );
}

/** 新增成员表单内的可创建下拉字段。 */
function RosterCreateSelectField({
  field,
  label,
  testId,
  options,
  placeholder,
  allowCreate,
}: {
  field: keyof RosterMemberEditableDraft;
  label: string;
  testId: string;
  options: EditableSelectOption[];
  placeholder: string;
  allowCreate?: boolean;
}) {
  return (
    <Form.Item field={field} label={label}>
      <Select
        aria-label={label}
        data-testid={testId}
        allowClear
        allowCreate={allowCreate}
        showSearch
        placeholder={placeholder}
        filterOption={(inputValue, option) => {
          const optionValue = getSelectOptionValue(option);
          const selected = options.find((item) => item.value === optionValue);
          const searchText = selected?.searchText ?? selected?.label ?? optionValue ?? "";
          return searchText.toLowerCase().includes(inputValue.toLowerCase());
        }}
      >
        {options.map((option) => (
          <Select.Option key={option.value} value={option.value} disabled={option.disabled}>
            {option.label}
          </Select.Option>
        ))}
      </Select>
    </Form.Item>
  );
}

/** 构建销售交接字段块 class，集中约束跨列与说明类样式。 */
function buildInfoBlockClassName({
  wide,
  full,
  textRow,
  note,
}: {
  wide?: boolean;
  full?: boolean;
  textRow?: boolean;
  note?: boolean;
}): string {
  return [
    "order-handover-info-block",
    wide ? "is-wide" : "",
    full ? "is-full" : "",
    textRow ? "is-text-row" : "",
    note ? "is-note" : "",
  ].filter(Boolean).join(" ");
}

/** 按销售交接业务语义给只读字段生成色标。 */
function getSalesInfoBadges(label: string): SalesInfoBadge[] {
  const map: Record<string, SalesInfoBadge[]> = {
    新客户类型: [{ text: "客户", tone: "source" }],
    服务顾问: [{ text: "服务", tone: "staff" }],
    抓单人: [{ text: "跟进", tone: "staff" }],
    签单人: [{ text: "签单", tone: "staff" }],
    计调执行人: [{ text: "计调", tone: "staff" }],
    主签合同人: [{ text: "签约", tone: "contract" }],
    院系专业: [{ text: "专业", tone: "source" }],
    写明校区或具体地址: [{ text: "地址", tone: "route" }],
    是否虚拟团: [{ text: "合同属性", tone: "contract" }],
    合同类型: [{ text: "合同模板", tone: "contract" }],
    收款账户: [{ text: "收款", tone: "money" }],
    收款备注: [{ text: "收款", tone: "money" }],
    发票要求: [{ text: "发票", tone: "money" }],
    当地发票要求金额: [{ text: "发票", tone: "money" }],
    签团机制: [{ text: "签团", tone: "contract" }],
    合同人数: [{ text: "合同金额", tone: "money" }],
    合同单价: [{ text: "合同金额", tone: "money" }],
    合同总金额: [{ text: "合同金额", tone: "money" }],
    支付时间: [{ text: "支付", tone: "money" }],
    是否代收代付大交通: [{ text: "代收代付", tone: "money" }],
    代收代付火车票金额: [{ text: "大交通", tone: "money" }],
    代收代付机票金额: [{ text: "大交通", tone: "money" }],
    老师住宿标准: [{ text: "老师标准", tone: "teacher" }],
    老师餐标: [{ text: "老师标准", tone: "teacher" }],
    老师门票要求: [{ text: "老师标准", tone: "teacher" }],
    老师交通要求: [{ text: "老师标准", tone: "teacher" }],
    领队要求: [{ text: "领队", tone: "route" }],
    是否代收代付非遗校企: [{ text: "非遗校企", tone: "money" }],
    代收代付非遗金额: [{ text: "非遗校企", tone: "money" }],
    代收代付校企金额: [{ text: "非遗校企", tone: "money" }],
    老师是否当地刷卡: [{ text: "刷卡", tone: "teacher" }],
    礼品发放: [{ text: "礼品", tone: "warning" }],
    横幅内容: [{ text: "横幅", tone: "route" }],
    横幅颜色: [{ text: "横幅", tone: "route" }],
    行程说明补充: [{ text: "行程", tone: "route" }],
    其他: [{ text: "其他", tone: "neutral" }],
    采访要求: [{ text: "采访", tone: "route" }],
    团中需要照顾到的附加要求: [{ text: "照顾", tone: "warning" }],
    合同中的仲裁单位描述: [{ text: "仲裁", tone: "contract" }],
    合同修改原因: [{ text: "合同改动", tone: "warning" }],
  };
  return map[label] ?? [];
}

/** 取字段的第一业务语义作为文字颜色，原型图只区分文字色，不显示额外标签。 */
function getSalesInfoTone(label: string): SalesInfoBadgeTone | undefined {
  return getSalesInfoBadges(label)[0]?.tone;
}

/** 将销售交接表单值转换为旧模板预览文本。 */
function toSalesPreviewText(value: SalesHandoverFormValues[SalesSheetFieldName] | undefined): string | undefined {
  if (Array.isArray(value)) {
    return value.filter(Boolean).join("、") || undefined;
  }
  if (typeof value === "string") {
    return value;
  }
  return undefined;
}

/** 把后端销售交接快照规整为表单初始值。 */
function buildSalesFormValues(
  sales: OrderHandoverSalesHandover | undefined,
  rosterMembers: OrderRosterMember[],
): SalesHandoverFormValues {
  const contractSignerMember =
    findRosterMainSigner(rosterMembers) ??
    findRosterContractSignerByName(rosterMembers, sales?.contractSignerNames);
  return {
    contractSignerNames: contractSignerMember?.name ?? sales?.contractSignerNames,
    contractSignerMemberId: contractSignerMember?.id,
    majorNames: splitMajorNames(sales?.majorNamesText),
    schoolSiteText: sales?.schoolSiteText,
    customerNoveltyType: sales?.customerNoveltyType,
    salesUserId: sales?.salesUserId,
    serviceConsultantUserId: sales?.serviceConsultantUserId,
    serviceConsultantNameSnapshot: sales?.serviceConsultantName,
    dispatcherUserId: sales?.dispatcherUserId,
    catchOrderUserId: sales?.catchOrderUserId,
    catchOrderUserNameSnapshot: sales?.catchOrderUserName,
    contractTemplateType: sales?.contractTemplateType,
    isAdvanceMoney: toBooleanSelectValue(sales?.isAdvanceMoney),
    contractPeopleCount: toInputText(sales?.contractPeopleCount),
    contractUnitPriceAmount: sales?.contractUnitPriceAmount,
    travelFeePayTimeText: sales?.travelFeePayTimeText,
    receivingAccountText: sales?.receivingAccountText,
    receivingRemark: sales?.receivingRemark,
    invoiceDesc: sales?.invoiceDesc,
    localInvoiceAmountDesc: sales?.localInvoiceAmountDesc,
    signTeamStyle: sales?.signTeamStyle,
    isCollectTransportFee: toBooleanSelectValue(sales?.isCollectTransportFee),
    collectTrainTicketAmount: sales?.collectTrainTicketAmount,
    collectFlightTicketAmount: sales?.collectFlightTicketAmount,
    teacherHouseRemark: sales?.teacherHouseRemark,
    teacherFoodRemark: sales?.teacherFoodRemark,
    teacherTicketRemark: sales?.teacherTicketRemark,
    teacherTransportRemark: sales?.teacherTransportRemark,
    guideRemark: sales?.guideRemark,
    isCollectIntangibleOrSchoolEnterpriseFee: toBooleanSelectValue(
      sales?.isCollectIntangibleOrSchoolEnterpriseFee,
    ),
    collectIntangibleAmount: sales?.collectIntangibleAmount,
    collectSchoolEnterpriseAmount: sales?.collectSchoolEnterpriseAmount,
    teacherLocalPosFlag: toBooleanSelectValue(sales?.teacherLocalPosFlag),
    giftRemark: sales?.giftRemark,
    bannerDesc: sales?.bannerDesc,
    bannerColor: sales?.bannerColor,
    goOutRemark: sales?.goOutRemark,
    otherRemark: sales?.otherRemark,
    interviewRemark: sales?.interviewRemark,
    teamCareRequirement: sales?.teamCareRequirement,
    arbitrationDesc: sales?.arbitrationDesc,
    contractModifyReason: sales?.contractModifyReason,
    singleOrderServiceFee: sales?.singleOrderServiceFee ?? "0",
    singleOrderPricingLines: undefined,
  };
}

/** 将选中行程已有值合并到销售交接表单草稿，等待用户确认保存。 */
function mergeItinerarySalesDraftValues(
  values: SalesHandoverFormValues,
  itinerary: HandoverItineraryOption | undefined,
): SalesHandoverFormValues {
  if (!itinerary) {
    return values;
  }
  const nextValues: SalesHandoverFormValues = { ...values };
  const majorNames = splitMajorNames(itinerary.majorNamesText);
  if (majorNames.length > 0) {
    nextValues.majorNames = majorNames;
  }
  if (hasUsefulItineraryText(itinerary.school)) {
    nextValues.schoolSiteText = itinerary.school;
  }
  return nextValues;
}

/** 判断行程字段是否是真实内容，避免用占位文案覆盖表单。 */
function hasUsefulItineraryText(value: string | undefined): value is string {
  const text = value?.trim();
  return Boolean(text && !["待填写", "待选择", "未填写", "未维护", "待确认"].includes(text));
}

/** 把销售交接表单值转换成接口需要的布尔字段。 */
function normalizeSalesHandoverFormValues(
  values: SalesHandoverFormValues,
): OrderHandoverSalesHandoverUpdate {
  const payloadValues = { ...values };
  delete payloadValues.contractSignerMemberId;
  return {
    ...payloadValues,
    isAdvanceMoney: fromBooleanSelectValue(values.isAdvanceMoney),
    isCollectTransportFee: fromBooleanSelectValue(values.isCollectTransportFee),
    isCollectIntangibleOrSchoolEnterpriseFee: fromBooleanSelectValue(
      values.isCollectIntangibleOrSchoolEnterpriseFee,
    ),
    teacherLocalPosFlag: fromBooleanSelectValue(values.teacherLocalPosFlag),
  };
}

/** 把布尔值映射为 Arco Select 可承载的字符串值。 */
function toBooleanSelectValue(value: boolean | undefined): BooleanSelectValue | undefined {
  if (value === undefined) {
    return undefined;
  }
  return value ? "true" : "false";
}

/** 把 Arco Select 字符串值还原为接口布尔值。 */
function fromBooleanSelectValue(value: BooleanSelectValue | undefined): boolean | undefined {
  if (value === undefined) {
    return undefined;
  }
  return value === "true";
}

/** 判断销售交接字段是否使用是/否控件。 */
function isBooleanSheetField(field: SalesSheetFieldName): field is BooleanSalesField {
  return [
    "isAdvanceMoney",
    "isCollectTransportFee",
    "isCollectIntangibleOrSchoolEnterpriseFee",
    "teacherLocalPosFlag",
  ].includes(field);
}

/** 判断销售交接字段是否使用员工下拉控件。 */
function isStaffSheetField(
  field: SalesSheetFieldName,
): field is "serviceConsultantUserId" | "catchOrderUserId" | "salesUserId" | "dispatcherUserId" {
  return [
    "serviceConsultantUserId",
    "catchOrderUserId",
    "salesUserId",
    "dispatcherUserId",
  ].includes(field);
}

/** 返回表格编辑字段对应的员工候选项。 */
function getStaffSheetOptions(
  field: "serviceConsultantUserId" | "catchOrderUserId" | "salesUserId" | "dispatcherUserId",
  options: {
    catchOrderSelectOptions: OrderHandoverStaffOption[];
    dispatcherSelectOptions: OrderHandoverStaffOption[];
    salesUserSelectOptions: OrderHandoverStaffOption[];
    serviceConsultantSelectOptions: OrderHandoverStaffOption[];
  },
): OrderHandoverStaffOption[] {
  if (field === "serviceConsultantUserId") {
    return options.serviceConsultantSelectOptions;
  }
  if (field === "catchOrderUserId") {
    return options.catchOrderSelectOptions;
  }
  if (field === "salesUserId") {
    return options.salesUserSelectOptions;
  }
  return options.dispatcherSelectOptions;
}

/** 过滤可新建选择框中的本地候选项。 */
function filterEditableOption(
  inputValue: string,
  option: { props?: unknown },
  options: EditableSelectOption[],
): boolean {
  const value = getSelectOptionValue(option);
  const selected = options.find((item) => item.value === value);
  const searchText = selected?.searchText ?? selected?.label ?? value ?? "";
  return searchText.toLowerCase().includes(inputValue.toLowerCase());
}

/** 过滤员工选择框中的候选项。 */
function filterStaffOption(
  inputValue: string,
  option: { props?: unknown },
  options: OrderHandoverStaffOption[],
): boolean {
  const value = getSelectOptionValue(option);
  const selected = options.find((item) => item.id === value);
  const searchText = selected
    ? [
        selected.displayName,
        selected.username,
        selected.mainDeptName,
        selected.phone,
        selected.id,
      ]
        .filter(Boolean)
        .join(" ")
    : "";
  return searchText.toLowerCase().includes(inputValue.toLowerCase());
}

/** 安全读取 Arco Select 过滤回调里的 option value。 */
function getSelectOptionValue(option: { props?: unknown }): string | undefined {
  if (!option.props || typeof option.props !== "object" || !("value" in option.props)) {
    return undefined;
  }
  const value = (option.props as { value?: unknown }).value;
  return typeof value === "string" ? value : undefined;
}

/** 合并当前已选人员，避免元数据为空时下拉只显示用户 ID。 */
function mergeStaffOptions(
  options: OrderHandoverStaffOption[],
  current: { id?: string; name?: string },
): OrderHandoverStaffOption[] {
  const id = current.id?.trim();
  const name = current.name?.trim();
  if (!id || !name || STAFF_PLACEHOLDER_NAMES.has(name)) {
    return options;
  }
  if (options.some((option) => option.id === id)) {
    return options;
  }
  return [
    ...options,
    {
      id,
      displayName: name,
      username: undefined,
      mainDeptName: undefined,
      phone: undefined,
      disabled: false,
    },
  ];
}

/** 格式化人员下拉展示文本。 */
function formatStaffOptionLabel(option: OrderHandoverStaffOption): string {
  if (!option.username || option.displayName === option.username) {
    return option.displayName;
  }
  return `${option.displayName} ${option.username}`;
}

/** 按用户 ID 查找人员展示名。 */
function getStaffDisplayName(
  options: OrderHandoverStaffOption[],
  userId?: string,
): string | undefined {
  const option = options.find((item) => item.id === userId);
  return option?.displayName;
}

/** 判断名单成员是否可作为主签合同人候选。 */
function isRosterContractSigner(member: OrderRosterMember): boolean {
  return member.isSigner && !member.isStopped;
}

/** 查找当前名单主签合同人。 */
function findRosterMainSigner(members: OrderRosterMember[]): OrderRosterMember | undefined {
  return members.find((member) => member.isMainSigner && !member.isStopped);
}

/** 按姓名快照兜底匹配名单合同人。 */
function findRosterContractSignerByName(
  members: OrderRosterMember[],
  name?: string,
): OrderRosterMember | undefined {
  const cleanName = name?.trim();
  if (!cleanName) {
    return undefined;
  }
  return members.find((member) => isRosterContractSigner(member) && member.name === cleanName);
}

/** 按名单成员 ID 查找成员。 */
function getRosterMemberById(
  members: OrderRosterMember[],
  memberId?: string,
): OrderRosterMember | undefined {
  if (!memberId) {
    return undefined;
  }
  return members.find((member) => member.id === memberId);
}

/** 销售交接只读态展示当前名单主签合同人。 */
function getContractSignerDisplayName(
  sales: OrderHandoverSalesHandover | undefined,
  rosterMembers: OrderRosterMember[],
): string {
  return (
    findRosterMainSigner(rosterMembers)?.name ??
    displayText(sales?.contractSignerNames, "待从名单选择")
  );
}

/** 把出行名单合同人转换为主签合同人下拉选项。 */
function toRosterContractSignerOptions(members: OrderRosterMember[]): EditableSelectOption[] {
  return members.filter(isRosterContractSigner).map((member) => {
    const suffix = [member.role, member.phone].filter(Boolean).join(" ");
    return {
      label: suffix ? `${member.name} ${suffix}` : member.name,
      value: member.id,
      searchText: [
        member.name,
        member.role,
        member.teacherType,
        member.classGroup,
        member.phone,
        member.certificateNo,
      ]
        .filter(Boolean)
        .join(" "),
    };
  });
}

/** 拆分院系专业快照文本为多选值。 */
function splitMajorNames(value?: string): string[] {
  return uniqueCleanTexts((value ?? "").split(/[、,，]/));
}

/** 把已选专业快照和标准专业字典合并为可选项。 */
function toMajorOptions(
  value: string | undefined,
  candidates: OrderHandoverMajorOption[],
): EditableSelectOption[] {
  const options = new Map<string, EditableSelectOption>();
  splitMajorNames(value).forEach((item) => {
    options.set(item, {
      label: item,
      value: item,
      searchText: item,
    });
  });
  candidates.forEach((item) => {
    if (!item.majorName) {
      return;
    }
    const searchText = `${item.majorName} ${item.majorCategory} ${item.id}`;
    options.set(item.majorName, {
      label: item.majorName,
      value: item.majorName,
      disabled: item.disabled,
      searchText,
    });
  });
  return Array.from(options.values());
}

/** 把学校校区候选和当前保存文本合并为可输入选择选项。 */
function toSchoolSiteOptions(
  value: string | undefined,
  candidates: OrderHandoverSchoolSiteOption[],
): EditableSelectOption[] {
  const options = new Map<string, EditableSelectOption>();
  candidates.forEach((item) => {
    const text = item.value.trim();
    if (!text) {
      return;
    }
    const mainSiteText = item.isMainSite ? " 主校区" : "";
    options.set(text, {
      label: text,
      value: text,
      searchText: [
        text,
        item.siteName,
        item.address,
        item.siteId,
        mainSiteText.trim(),
      ]
        .filter(Boolean)
        .join(" "),
    });
  });
  const currentText = value?.trim();
  if (currentText && !options.has(currentText)) {
    options.set(currentText, {
      label: currentText,
      value: currentText,
      searchText: currentText,
    });
  }
  return Array.from(options.values());
}

/** 把固定候选与当前值合并，旧导入值不在字典中时也不丢失。 */
function mergeSelectOptions(
  options: EditableSelectOption[],
  ...values: Array<string | undefined>
): EditableSelectOption[] {
  const result = new Map<string, EditableSelectOption>();
  options.forEach((option) => {
    result.set(option.value, option);
  });
  values.forEach((value) => {
    const text = value?.trim();
    if (!text || result.has(text)) {
      return;
    }
    result.set(text, { label: text, value: text, searchText: text });
  });
  return Array.from(result.values());
}

/** 把数字或金额值规整为表单输入文本。 */
function toInputText(value: number | string | undefined): string | undefined {
  if (value === undefined) {
    return undefined;
  }
  return String(value);
}

/** 格式化是否类展示值。 */
function formatBooleanValue(value: boolean | undefined): string {
  if (value === undefined) {
    return "待选择";
  }
  return value ? "是" : "否";
}

/** 清理并去重文本列表。 */
function uniqueCleanTexts(values: string[]): string[] {
  const result: string[] = [];
  values.forEach((value) => {
    const text = value.trim();
    if (text && !result.includes(text)) {
      result.push(text);
    }
  });
  return result;
}

/** 空字符串按业务占位展示，避免卡片留白。 */
function displayText(value: string | undefined, fallback: string): string {
  const text = value?.trim();
  return text || fallback;
}

/** 格式化销售交接卡片右上角的签单人与更新时间。 */
function formatSalesHandoverMeta(
  sales: { salesUserName?: string; updatedAt?: string } | undefined,
): string {
  const name = displayText(sales?.salesUserName, "待选择签单人");
  const updatedAt = formatShortDateTime(sales?.updatedAt);
  return updatedAt ? `${name} · ${updatedAt}` : name;
}

/** 格式化后端北京时间为 YYYY-MM-DD HH:mm。 */
function formatShortDateTime(value?: string): string {
  if (!value) {
    return "";
  }
  const matched = value.match(/^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})/);
  if (!matched) {
    return "";
  }
  return `${matched[1]}-${matched[2]}-${matched[3]} ${matched[4]}:${matched[5]}`;
}

/** 格式化后端北京时间，空值显示占位。 */
function formatOptionalDateTime(value?: string): string {
  return formatShortDateTime(value) || "-";
}

/** 签约通知状态标签颜色。 */
function contractNotificationTagColor(stateCode: string): string {
  if (stateCode === "sent") {
    return "green";
  }
  if (stateCode === "blocked") {
    return "red";
  }
  if (stateCode === "dry_run" || stateCode === "pending") {
    return "orange";
  }
  return "gray";
}

/** 合同签订状态标签颜色。 */
function contractStateTagColor(signStateCode: string): string {
  if (signStateCode === "all_signed") {
    return "green";
  }
  if (signStateCode === "partial_signed" || signStateCode === "sent") {
    return "orange";
  }
  if (signStateCode === "void_applying" || signStateCode === "partial_voided") {
    return "red";
  }
  if (signStateCode === "voided" || signStateCode === "invalid") {
    return "gray";
  }
  return "arcoblue";
}

/** 构建行程选择抽屉表格列。 */
function buildItineraryColumns(
  onSelect: (option: HandoverItineraryOption) => void,
  loading: boolean,
  canManage: boolean,
  selectedId?: string,
): TableColumnProps<HandoverItineraryOption>[] {
  return [
    {
      title: "行程",
      dataIndex: "title",
      render: (_, item) => {
        const isCurrent = item.id === selectedId;
        return (
          <div className="order-handover-itinerary-title">
            <strong>
              {item.title}
              {isCurrent ? (
                <Tag className="order-handover-itinerary-current-tag" color="green" size="small">
                  当前
                </Tag>
              ) : null}
            </strong>
            <span>
              {item.school} · {item.destination}
            </span>
          </div>
        );
      },
    },
    { title: "版本", dataIndex: "version", width: 90, render: (_, item) => <Tag color="arcoblue">{item.version}</Tag> },
    { title: "日期", dataIndex: "dateRange", width: 150 },
    { title: "人数", width: 150, render: (_, item) => formatItineraryPeople(item) },
    {
      title: "操作",
      width: 100,
      render: (_, item) => {
        const isCurrent = item.id === selectedId;
        return (
          <Button
            type={isCurrent ? "outline" : "primary"}
            status={isCurrent ? "success" : undefined}
            size="small"
            loading={loading}
            disabled={!canManage}
            onClick={() => onSelect(item)}
          >
            {isCurrent ? "已选中" : "选中"}
          </Button>
        );
      },
    },
  ];
}

/** 渲染出行名单底部汇总项。 */
function RosterSummaryItem({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "danger" | "warning" | "success";
}) {
  return (
    <div className="order-handover-roster-summary-item">
      <span>{label}</span>
      <strong className={tone ? `is-${tone}` : undefined}>{value}</strong>
    </div>
  );
}

/** 根据候选配置和名单成员构建人员类型筛选候选。 */
function buildRosterTypeOptions(
  members: OrderRosterMember[],
  travelerTypeOptions: EditableSelectOption[],
): string[] {
  const options = new Set<string>(["全部"]);
  travelerTypeOptions.forEach((option) => {
    if (option.value.trim()) {
      options.add(option.value);
    }
  });
  members.forEach((member) => {
    const role = member.role.trim();
    if (role) {
      options.add(role);
    }
  });
  return Array.from(options);
}

/** 从当前名单成员去重生成班级类型筛选候选，不含专业字典里未出现的项。 */
function buildRosterClassGroupFilterOptions(members: OrderRosterMember[]): string[] {
  const options = new Set<string>(["全部"]);
  members.forEach((member) => {
    const classGroup = member.classGroup?.trim();
    if (classGroup) {
      options.add(classGroup);
    }
  });
  return Array.from(options);
}

/** 按关键字、人员类型、班级类型和删除状态筛选名单成员。 */
function filterRosterMembers(
  members: OrderRosterMember[],
  filters: { keyword: string; type: string; classGroup: string; stopped: string },
): OrderRosterMember[] {
  const keyword = filters.keyword.trim().toLowerCase();
  return members.filter((member) => {
    if (filters.type !== "全部" && member.role !== filters.type) {
      return false;
    }
    if (filters.classGroup !== "全部" && (member.classGroup ?? "").trim() !== filters.classGroup) {
      return false;
    }
    if (filters.stopped === "active" && member.isStopped) {
      return false;
    }
    if (filters.stopped === "stopped" && !member.isStopped) {
      return false;
    }
    if (!keyword) {
      return true;
    }
    return [
      member.name,
      member.phone,
      member.certificateNo,
      member.studentNo,
      member.classGroup,
      member.teacherType,
      member.remark,
    ].some((value) => value?.toLowerCase().includes(keyword));
  });
}

/** 生成空名单成员编辑草稿。 */
function emptyRosterMemberDraft(): RosterMemberEditableDraft {
  return {
    memberName: "",
    travelerTypeLabel: "",
    teacherTypeLabel: "",
    classGroupLabel: "",
    certificateType: "身份证",
    certificateNo: "",
    mobile: "",
    gender: "",
    age: "",
    birthday: "",
    studentNo: "",
    roomingLabel: "",
    trafficTypeLabel: "",
    vehicleAssignmentLabel: "",
    bigTrafficInfo: "",
    guidanceNote: "",
    actualReceivableAmount: "",
    lineAmount: "",
    mainSignerAddress: "",
    remark: "",
  };
}

/** 把后端名单成员转换为在线编辑草稿。 */
function buildRosterMemberDraft(member: OrderRosterMember): RosterMemberEditableDraft {
  return {
    memberName: cleanRosterText(member.name),
    travelerTypeLabel: cleanRosterText(member.role),
    teacherTypeLabel: cleanRosterText(member.teacherType),
    classGroupLabel: cleanRosterText(member.classGroup),
    certificateType: cleanRosterText(member.certificateType),
    certificateNo: cleanRosterText(member.certificateNo),
    mobile: cleanRosterText(member.phone),
    gender: cleanRosterText(member.gender),
    age: member.age > 0 ? String(member.age) : "",
    birthday: cleanRosterText(member.birthday),
    studentNo: cleanRosterText(member.studentNo),
    roomingLabel: cleanRosterText(member.rooming),
    trafficTypeLabel: cleanRosterText(member.vehicle),
    vehicleAssignmentLabel: cleanRosterText(member.vehicleAssignment),
    bigTrafficInfo: cleanRosterText(member.bigTrafficInfo),
    guidanceNote: cleanRosterText(member.guidanceNote),
    actualReceivableAmount: cleanRosterText(member.actualReceivableAmount),
    lineAmount: cleanRosterText(member.lineAmount),
    mainSignerAddress: cleanRosterText(member.mainSignerAddress),
    remark: cleanRosterText(member.remark),
  };
}

/** 判断名单成员草稿是否被修改。 */
function isRosterDraftChanged(
  member: OrderRosterMember,
  draft: RosterMemberEditableDraft,
): boolean {
  const origin = buildRosterMemberDraft(member);
  return (Object.keys(origin) as Array<keyof RosterMemberEditableDraft>).some(
    (field) => origin[field] !== draft[field],
  );
}

/** 校验名单在线编辑中实际发生变化的电话和证件字段。 */
function validateRosterMemberDraftChanges(
  member: OrderRosterMember,
  draft: RosterMemberEditableDraft,
): string[] {
  const origin = buildRosterMemberDraft(member);
  const errors: string[] = [];
  if (draft.mobile !== origin.mobile) {
    const mobileError = getRosterMobileValidationError(draft.mobile);
    if (mobileError) {
      errors.push(mobileError);
    }
  }
  if (
    draft.certificateType !== origin.certificateType ||
    draft.certificateNo !== origin.certificateNo
  ) {
    const certificateError = getRosterCertificateNoValidationError(
      draft.certificateType,
      draft.certificateNo,
    );
    if (certificateError) {
      errors.push(certificateError);
    }
  }
  return errors;
}

/** 校验大陆手机号。 */
function getRosterMobileValidationError(value: string): string | undefined {
  const text = value.trim();
  if (!text) {
    return "请输入电话";
  }
  return MAINLAND_MOBILE_PATTERN.test(text) ? undefined : "请输入正确的11位大陆手机号";
}

/** 按证件类型校验证件号码。 */
function getRosterCertificateNoValidationError(
  certificateType: string,
  certificateNo: string,
): string | undefined {
  const text = certificateNo.trim();
  if (!text) {
    return "请输入证件号";
  }
  if (isRosterIdCardType(certificateType)) {
    return isValidMainlandIdCard(text) ? undefined : "请输入正确的身份证号";
  }
  return OTHER_CERTIFICATE_NO_PATTERN.test(text)
    ? undefined
    : "证件号需为5至30位字母、数字或连字符";
}

/** 空证件类型沿用后端默认值“身份证”。 */
function isRosterIdCardType(value: string): boolean {
  const text = value.trim();
  const normalized = text.toLowerCase();
  return !text || text.includes("身份证") || ["id_card", "identity_card"].includes(normalized);
}

/** 校验 15/18 位大陆身份证的日期与 18 位校验码。 */
function isValidMainlandIdCard(value: string): boolean {
  const text = value.trim().toUpperCase();
  if (/^[1-9]\d{14}$/.test(text)) {
    return isValidRosterBirthday(
      1900 + Number(text.slice(6, 8)),
      Number(text.slice(8, 10)),
      Number(text.slice(10, 12)),
    );
  }
  if (!/^[1-9]\d{16}[\dX]$/.test(text)) {
    return false;
  }
  if (
    !isValidRosterBirthday(
      Number(text.slice(6, 10)),
      Number(text.slice(10, 12)),
      Number(text.slice(12, 14)),
    )
  ) {
    return false;
  }
  const checksum = MAINLAND_ID_CARD_WEIGHTS.reduce(
    (sum, weight, index) => sum + Number(text[index]) * weight,
    0,
  );
  return text[17] === MAINLAND_ID_CARD_CHECK_CODES[checksum % 11];
}

/** 从身份证号解析性别、生日与周岁；非身份证或格式无效时返回 null。 */
function parseMainlandIdCardProfile(
  certificateNo: string,
  certificateType: string,
): { gender: string; birthday: string; age: number } | null {
  if (!isRosterIdCardType(certificateType)) {
    return null;
  }
  const text = certificateNo.trim().toUpperCase();
  if (!isValidMainlandIdCard(text)) {
    return null;
  }
  const year =
    text.length === 15 ? 1900 + Number(text.slice(6, 8)) : Number(text.slice(6, 10));
  const month = text.length === 15 ? Number(text.slice(8, 10)) : Number(text.slice(10, 12));
  const day = text.length === 15 ? Number(text.slice(10, 12)) : Number(text.slice(12, 14));
  const genderCode = Number(text.length === 15 ? text[14] : text[16]);
  return {
    gender: genderCode % 2 === 1 ? "男" : "女",
    birthday: `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`,
    age: ageFromRosterBirthday(year, month, day),
  };
}

/** 按当天日期计算周岁。 */
function ageFromRosterBirthday(year: number, month: number, day: number): number {
  const today = new Date();
  let age = today.getFullYear() - year;
  if (
    today.getMonth() + 1 < month ||
    (today.getMonth() + 1 === month && today.getDate() < day)
  ) {
    age -= 1;
  }
  return Math.max(age, 0);
}

/** 身份证号或证件类型变化时，自动回填性别、生日、年龄。 */
function syncRosterIdCardDerivedFields(
  form: FormInstance<RosterMemberEditableDraft>,
  changed: Partial<RosterMemberEditableDraft>,
  values: Partial<RosterMemberEditableDraft>,
) {
  if (!("certificateNo" in changed) && !("certificateType" in changed)) {
    return;
  }
  const profile = parseMainlandIdCardProfile(
    String(values.certificateNo ?? ""),
    String(values.certificateType ?? ""),
  );
  if (!profile) {
    return;
  }
  const next = {
    gender: profile.gender,
    birthday: profile.birthday,
    age: String(profile.age),
  };
  if (
    values.gender === next.gender &&
    values.birthday === next.birthday &&
    values.age === next.age
  ) {
    return;
  }
  form.setFieldsValue(next);
}

/** 严格比较年月日，避免 JavaScript 自动滚动到下个月。 */
function isValidRosterBirthday(year: number, month: number, day: number): boolean {
  if (year < 1800 || year > new Date().getFullYear()) {
    return false;
  }
  const candidate = new Date(Date.UTC(year, month - 1, day));
  return (
    candidate.getUTCFullYear() === year &&
    candidate.getUTCMonth() === month - 1 &&
    candidate.getUTCDate() === day
  );
}

/** 构建名单成员在线保存请求。 */
function buildRosterMemberUpdatePayload(
  member: OrderRosterMember,
  draft: RosterMemberEditableDraft,
): OrderHandoverRosterMemberUpdatePayload {
  const origin = buildRosterMemberDraft(member);
  const payload: OrderHandoverRosterMemberUpdatePayload = {};
  if (draft.memberName !== origin.memberName) {
    payload.memberName = draft.memberName;
  }
  if (draft.travelerTypeLabel !== origin.travelerTypeLabel) {
    payload.travelerTypeLabel = draft.travelerTypeLabel;
  }
  if (draft.teacherTypeLabel !== origin.teacherTypeLabel) {
    payload.teacherTypeLabel = draft.teacherTypeLabel;
  }
  if (draft.classGroupLabel !== origin.classGroupLabel) {
    payload.classGroupLabel = draft.classGroupLabel;
  }
  if (draft.certificateType !== origin.certificateType) {
    payload.certificateType = draft.certificateType.trim();
  }
  if (draft.certificateNo !== origin.certificateNo) {
    payload.certificateNo = draft.certificateNo.trim();
  }
  if (draft.mobile !== origin.mobile) {
    payload.mobile = draft.mobile.trim();
  }
  if (draft.gender !== origin.gender) {
    payload.gender = draft.gender;
  }
  if (draft.age !== origin.age) {
    payload.age = draft.age.trim() ? Number(draft.age) : undefined;
  }
  if (draft.birthday !== origin.birthday) {
    payload.birthday = optionalRosterText(draft.birthday);
  }
  if (draft.studentNo !== origin.studentNo) {
    payload.studentNo = draft.studentNo;
  }
  if (draft.roomingLabel !== origin.roomingLabel) {
    payload.roomingLabel = draft.roomingLabel;
  }
  if (draft.trafficTypeLabel !== origin.trafficTypeLabel) {
    payload.trafficTypeLabel = draft.trafficTypeLabel;
  }
  if (draft.vehicleAssignmentLabel !== origin.vehicleAssignmentLabel) {
    payload.vehicleAssignmentLabel = draft.vehicleAssignmentLabel;
  }
  if (draft.bigTrafficInfo !== origin.bigTrafficInfo) {
    payload.bigTrafficInfo = draft.bigTrafficInfo;
  }
  if (draft.guidanceNote !== origin.guidanceNote) {
    payload.guidanceNote = draft.guidanceNote;
  }
  if (draft.actualReceivableAmount !== origin.actualReceivableAmount) {
    payload.actualReceivableAmount = optionalRosterText(draft.actualReceivableAmount);
  }
  if (draft.lineAmount !== origin.lineAmount) {
    payload.lineAmount = optionalRosterText(draft.lineAmount);
  }
  if (draft.mainSignerAddress !== origin.mainSignerAddress) {
    payload.mainSignerAddress = draft.mainSignerAddress;
  }
  if (draft.remark !== origin.remark) {
    payload.remark = draft.remark;
  }
  return omitUndefinedRosterPayload(payload);
}

/** 把时间戳压缩为旧系统表格中的日期文本。 */
function formatDateOnly(value?: string): string {
  const matched = value?.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!matched) {
    return "待填写";
  }
  return `${matched[1]}-${matched[2]}-${matched[3]}`;
}

/** 把订单状态映射为表格标题左侧状态。 */
function formatOrderStatus(value?: string): string {
  if (value === "finalized") {
    return "定稿";
  }
  if (value === "formed") {
    return "已挂网";
  }
  return "草稿";
}

/** 按合同人数和单价计算合同金额展示文本。 */
function calculateContractAmountText(
  sales: OrderHandoverSalesHandover | undefined,
): string | undefined {
  return calculateContractAmountTextFromInput(
    toInputText(sales?.contractPeopleCount),
    sales?.contractUnitPriceAmount,
  );
}

/** 按合同人数与单价输入文本计算合同金额展示文本。 */
function calculateContractAmountTextFromInput(
  peopleCountText: string | undefined,
  unitPriceText: string | undefined,
): string | undefined {
  const count = Number(peopleCountText);
  const price = Number(unitPriceText);
  if (!Number.isFinite(count) || !Number.isFinite(price) || count <= 0 || price < 0) {
    return undefined;
  }
  return formatSheetMoney(count * price);
}

/** 生成旧系统表格里的实际应收分组行。 */
function buildActualReceivableLines({
  orderLines,
  rosterMembers,
  rosterStats,
  sales,
}: {
  orderLines: OrderHandoverOrderLine[];
  rosterMembers: OrderRosterMember[];
  rosterStats: { student: number; teacher: number };
  sales?: OrderHandoverSalesHandover;
}): ActualReceivableLine[] {
  // 合同交接表只展示名单计价/手工等汇总行，不展开成交报价明细。
  const sheetOrderLines = orderLines.filter((line) => line.sourceMode !== "customer_quote");
  const memberLines = buildOrderLinesFromRosterMembers(rosterMembers);
  const sourceLines = ensureStandardActualReceivablePlaceholders(
    sheetOrderLines.length > 0
      ? sheetOrderLines
      : memberLines.length > 0
        ? memberLines
        : buildFallbackOrderLines(rosterMembers, rosterStats, sales),
  );
  return sourceLines.map((line, index) => {
    const count = line.peopleCount ?? (Number(line.quantity) || 0);
    const price = Number(line.salesUnitPriceAmount) || 0;
    const total = Number(line.salesTotalAmount) || count * price;
    return {
      label: line.lineName || emptyActualReceivableLine(index).label,
      count: count > 0 ? String(count) : "",
      price: price > 0 ? formatDecimalText(price) : "",
      total: total > 0 ? formatSheetMoney(total) : "",
      totalAmount: total,
      remark: line.lineDesc ?? "",
    };
  });
}

/** 固定补齐老师/其他/合同外空行，旧模板这几行即使无人也要展示。 */
function ensureStandardActualReceivablePlaceholders(
  lines: OrderHandoverOrderLine[],
): OrderHandoverOrderLine[] {
  const requiredLabels = [
    "实际出团学生人数",
    "实际出团老师人数",
    "实际出团其他人数",
    "合同外收费人数",
  ] as const;
  const result = [...lines];
  for (const label of requiredLabels) {
    if (!result.some((line) => line.lineName === label)) {
      result.push(toFallbackLine(label, 0, 0));
    }
  }
  const groupOrder = (name: string | undefined): number => {
    if (name === "实际出团学生人数") {
      return 0;
    }
    if (name === "实际出团老师人数") {
      return 1;
    }
    if (name === "实际出团其他人数") {
      return 2;
    }
    if (name === "合同外收费人数") {
      return 3;
    }
    return 4;
  };
  return result.sort((left, right) => {
    const leftOrder = groupOrder(left.lineName);
    const rightOrder = groupOrder(right.lineName);
    if (leftOrder !== rightOrder) {
      return leftOrder - rightOrder;
    }
    return (Number(right.salesUnitPriceAmount) || 0) - (Number(left.salesUnitPriceAmount) || 0);
  });
}

/** 按名单最终应收聚合「实际出团」分项：同类型同单价合并，例如 8×98 + 2×150。 */
function buildOrderLinesFromRosterMembers(
  rosterMembers: OrderRosterMember[],
): OrderHandoverOrderLine[] {
  type GroupKey = string;
  const grouped = new Map<
    GroupKey,
    { groupOrder: number; lineName: string; amount: number; peopleCount: number }
  >();
  for (const member of rosterMembers) {
    if (member.isStopped) {
      continue;
    }
    const [groupOrder, lineName] = rosterPricingLineGroup(member);
    const amount = Number(member.actualReceivableAmount) || 0;
    const key = `${groupOrder}|${lineName}|${amount}`;
    const current = grouped.get(key);
    if (current) {
      current.peopleCount += 1;
    } else {
      grouped.set(key, { groupOrder, lineName, amount, peopleCount: 1 });
    }
  }
  return [...grouped.values()]
    .sort((left, right) => {
      if (left.groupOrder !== right.groupOrder) {
        return left.groupOrder - right.groupOrder;
      }
      if (right.amount !== left.amount) {
        return right.amount - left.amount;
      }
      return left.lineName.localeCompare(right.lineName, "zh-CN");
    })
    .map((group) =>
      toFallbackLine(group.lineName, group.peopleCount, group.amount),
    );
}

/** 与后端名单定稿收费行分组一致：合同外 / 学生 / 老师 / 其他。 */
function rosterPricingLineGroup(member: OrderRosterMember): [number, string] {
  if (!member.isShowInContract) {
    return [3, "合同外收费人数"];
  }
  const groupOrder = rosterMemberRoleGroup(member);
  if (groupOrder === 0) {
    return [0, "实际出团学生人数"];
  }
  if (groupOrder === 1) {
    return [1, "实际出团老师人数"];
  }
  return [2, "实际出团其他人数"];
}

/** 根据人员类型和职务文本归类学生、老师、其他。 */
function rosterMemberRoleGroup(member: OrderRosterMember): number {
  const travelerType = member.role.trim();
  const teacherType = (member.teacherType ?? "").trim();
  const role = `${travelerType} ${teacherType}`.trim();
  if (["其他", "随行", "家长"].some((keyword) => role.includes(keyword))) {
    return 2;
  }
  if (travelerType.includes("学生") || travelerType.includes("学员")) {
    return 0;
  }
  if (["老师", "教师"].some((keyword) => travelerType.includes(keyword))) {
    return 1;
  }
  if (teacherType.includes("学生") || teacherType.includes("学员")) {
    return 0;
  }
  if (["老师", "教师", "带队", "班主任"].some((keyword) => teacherType.includes(keyword))) {
    return 1;
  }
  return 2;
}

/** 临时汇总旧模板实际收款分项，不落库。 */
function buildActualReceivableTotalText(lines: ActualReceivableLine[]): string {
  const total = lines.reduce((sum, line) => sum + (Number(line.totalAmount) || 0), 0);
  return formatSheetMoney(total);
}

/** 在后端未返回销售行时按名单统计兜底生成分组行。 */
function buildFallbackOrderLines(
  rosterMembers: OrderRosterMember[],
  rosterStats: { student: number; teacher: number },
  sales: OrderHandoverSalesHandover | undefined,
): OrderHandoverOrderLine[] {
  const unitPrice = Number(sales?.contractUnitPriceAmount) || 0;
  const studentCount = rosterStats.student;
  const teacherCount = rosterStats.teacher;
  const otherCount = rosterMembers.filter((member) => {
    const role = member.role.trim();
    return role && !role.includes("学生") && !role.includes("老师") && !role.includes("教师");
  }).length;
  return [
    toFallbackLine("实际出团学生人数", studentCount, unitPrice),
    toFallbackLine("实际出团老师人数", teacherCount, 0),
    toFallbackLine("实际出团其他人数", otherCount, 0),
    toFallbackLine("合同外收费人数", 0, 0),
  ];
}

/** 构造一行兜底销售行数据。 */
function toFallbackLine(name: string, count: number, price: number): OrderHandoverOrderLine {
  return {
    id: `fallback-${name}`,
    lineName: name,
    lineTypeCode: "traveler_group",
    peopleCount: count,
    quantity: String(count),
    unit: "人",
    salesUnitPriceAmount: formatDecimalText(price),
    salesTotalAmount: formatDecimalText(count * price),
    lineDesc: "",
  };
}

/** 返回旧表实际应收区的空行占位。 */
function emptyActualReceivableLine(index: number): ActualReceivableLine {
  const labels = [
    "实际出团学生人数",
    "实际出团老师人数",
    "实际出团其他人数",
    "合同外收费人数",
  ];
  return {
    label: labels[index] ?? "实际出团人数",
    count: "",
    price: "",
    total: "",
    totalAmount: 0,
    remark: "",
  };
}

/** 只提取老师或带队人员作为联系方式行。 */
function buildTeacherContactRows(members: OrderRosterMember[]): OrderRosterMember[] {
  return members.filter((member) => {
    const roleText = `${member.role} ${member.teacherType ?? ""}`;
    return !member.isStopped && (roleText.includes("老师") || roleText.includes("教师") || roleText.includes("带队"));
  });
}

/** 把新系统补充字段合并到旧模板已有文本格。 */
function mergeLegacyPreviewText(
  baseValue: string | undefined,
  extraValue: string | undefined,
  extraLabel: string,
): string {
  const baseText = normalizeLegacyPreviewText(baseValue);
  const extraText = normalizeLegacyPreviewText(extraValue);
  if (baseText && extraText) {
    return `${baseText}；${extraLabel}：${extraText}`;
  }
  if (extraText) {
    return `${extraLabel}：${extraText}`;
  }
  return baseText || "待填写";
}

/** 清理旧模板合并展示前的占位文本。 */
function normalizeLegacyPreviewText(value: string | undefined): string {
  const text = value?.trim();
  if (!text || ["待填写", "待选择", "未填写", "未维护", "待确认"].includes(text)) {
    return "";
  }
  return text;
}

/** 格式化旧系统表格中的金额文本。 */
function formatSheetMoney(value: number): string {
  return `¥${formatDecimalText(value)}`;
}

/** 格式化两位小数文本。 */
function formatDecimalText(value: number): string {
  if (!Number.isFinite(value)) {
    return "0.00";
  }
  return value.toFixed(2);
}

/** 构建新增名单成员请求，只提交用户填写过的字段。 */
function buildRosterMemberCreatePayload(
  draft: RosterMemberEditableDraft,
): OrderHandoverRosterMemberCreatePayload {
  const payload: OrderHandoverRosterMemberCreatePayload = {
    memberName: draft.memberName.trim(),
    certificateNo: draft.certificateNo.trim(),
    mobile: draft.mobile.trim(),
  };
  if (draft.travelerTypeLabel.trim()) {
    payload.travelerTypeLabel = draft.travelerTypeLabel.trim();
  }
  if (draft.teacherTypeLabel.trim()) {
    payload.teacherTypeLabel = draft.teacherTypeLabel.trim();
  }
  if (draft.classGroupLabel.trim()) {
    payload.classGroupLabel = draft.classGroupLabel.trim();
  }
  if (draft.certificateType.trim()) {
    payload.certificateType = draft.certificateType.trim();
  }
  if (draft.gender.trim()) {
    payload.gender = draft.gender.trim();
  }
  if (draft.age.trim()) {
    payload.age = Number(draft.age.trim());
  }
  if (draft.birthday.trim()) {
    payload.birthday = draft.birthday.trim();
  }
  if (draft.studentNo.trim()) {
    payload.studentNo = draft.studentNo.trim();
  }
  if (draft.bigTrafficInfo.trim()) {
    payload.bigTrafficInfo = draft.bigTrafficInfo.trim();
  }
  if (draft.guidanceNote.trim()) {
    payload.guidanceNote = draft.guidanceNote.trim();
  }
  if (draft.mainSignerAddress.trim()) {
    payload.mainSignerAddress = draft.mainSignerAddress.trim();
  }
  if (draft.remark.trim()) {
    payload.remark = draft.remark.trim();
  }
  return payload;
}

/** 可空日期/金额输入留空时不提交，避免后端 date/decimal 校验 422。 */
function optionalRosterText(value: string): string | undefined {
  const text = value.trim();
  return text ? text : undefined;
}

/** 删除未填写的可空字段，避免 mock payload 与真实 JSON 请求含义不一致。 */
function omitUndefinedRosterPayload(
  payload: OrderHandoverRosterMemberUpdatePayload,
): OrderHandoverRosterMemberUpdatePayload {
  return Object.fromEntries(
    Object.entries(payload).filter(([, value]) => value !== undefined),
  ) as OrderHandoverRosterMemberUpdatePayload;
}

/** 判断 Arco 表单校验异常，避免把前端必填错误覆盖成接口错误。 */
function isFormValidationError(error: unknown): boolean {
  if (!error || typeof error !== "object") {
    return false;
  }
  // Arco ValidateError：message 固定英文，errors 为字段错误对象（非数组）。
  if (
    error instanceof Error &&
    error.message.includes("form validate error")
  ) {
    return true;
  }
  if (!("errors" in error)) {
    return false;
  }
  const fieldErrors = (error as { errors?: unknown }).errors;
  return fieldErrors != null && typeof fieldErrors === "object";
}

/** 提取后端业务错误文案，兜底保留通用失败提示。 */
function getErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }
  return fallback;
}

/** 清理名单表格占位文本，避免把“-”保存回数据库。 */
function cleanRosterText(value: string | undefined): string {
  const text = value?.trim();
  return text && text !== "-" ? text : "";
}

/** 构建出行名单表格列。 */
function buildRosterColumns(options: RosterColumnOptions): TableColumnProps<OrderRosterMember>[] {
  const {
    editingMemberIdsRef,
    editingStore,
    majorOptions,
    travelerTypeOptions,
    teacherTypeOptions,
    saving,
    selectedIdsRef,
    selectionStore,
    feeAdjustmentRecords,
    feeAdjustmentAvailable,
    showFeeAdjustment,
    visibleMemberIdsRef,
    currentPageMemberIdsRef,
    selectScopeRef,
    locked,
    onToggleSelection,
    onOpenMemberFeeAdjustment,
    onRequestSelectAll,
    onClearVisibleSelection,
    onDraftInputChange,
    onOpenEdit,
    onCancelEdit,
    onSaveEdit,
    onToggleStop,
    onClearMemberFlag,
    onClearAllMemberFlags,
  } = options;
  const processedReceivableByMemberId = new Map<string, number>();
  for (const record of feeAdjustmentRecords) {
    if (record.status !== "processed") continue;
    for (const member of record.members) {
      const amount = Number(member.receivable_delta_amount || "0");
      processedReceivableByMemberId.set(
        member.roster_member_id,
        (processedReceivableByMemberId.get(member.roster_member_id) ?? 0) + amount,
      );
    }
  }
  return [
    {
      title: (
        <RosterSelectAllCheckbox
          selectedIdsRef={selectedIdsRef}
          visibleMemberIdsRef={visibleMemberIdsRef}
          currentPageMemberIdsRef={currentPageMemberIdsRef}
          selectScopeRef={selectScopeRef}
          store={selectionStore}
          onRequestSelectAll={onRequestSelectAll}
          onClearSelection={onClearVisibleSelection}
        />
      ),
      width: 52,
      fixed: "left",
      render: (_, item) => (
        <RosterRowCheckbox
          member={item}
          selectedIdsRef={selectedIdsRef}
          store={selectionStore}
          onToggle={onToggleSelection}
        />
      ),
    },
    {
      title: "序号",
      width: 64,
      render: (_, __, index) => <span className="order-handover-roster-index">{index + 1}</span>,
    },
    {
      title: "姓名",
      dataIndex: "name",
      width: 150,
      render: (_, item) => {
        const badges = renderRosterBadges(item);
        return (
          <RosterMemberNameCell
            item={item}
            badges={badges}
            editingMemberIdsRef={editingMemberIdsRef}
            editingStore={editingStore}
            onDraftInputChange={onDraftInputChange}
          />
        );
      },
    },
    {
      title: "人员类型",
      width: 112,
      render: (_, item) => (
        <RosterTravelerTypeCell
          item={item}
          travelerTypeOptions={travelerTypeOptions}
          editingMemberIdsRef={editingMemberIdsRef}
          editingStore={editingStore}
          onDraftInputChange={onDraftInputChange}
        />
      ),
    },
    {
      title: "证件号",
      width: 190,
      render: (_, item) =>
        renderRosterEditableTextCell({
          editingMemberIdsRef,
          editingStore,
          item,
          field: "certificateNo",
          label: "证件号",
          value: item.certificateNo,
          readOnlyValue: maskMiddleText(item.certificateNo, 6, 4),
          onDraftInputChange,
        }),
    },
    {
      title: "电话",
      width: 132,
      render: (_, item) =>
        renderRosterEditableTextCell({
          editingMemberIdsRef,
          editingStore,
          item,
          field: "mobile",
          label: "电话",
          value: item.phone,
          readOnlyValue: maskMiddleText(item.phone, 3, 4),
          onDraftInputChange,
        }),
    },
    {
      title: "专业/班级类型",
      width: 150,
      render: (_, item) => (
        <RosterClassGroupCell
          item={item}
          majorOptions={majorOptions}
          editingMemberIdsRef={editingMemberIdsRef}
          editingStore={editingStore}
          onDraftInputChange={onDraftInputChange}
        />
      ),
    },
    {
      title: "职务/职位",
      width: 140,
      render: (_, item) =>
        renderRosterEditableTextCell({
          editingMemberIdsRef,
          editingStore,
          item,
          field: "teacherTypeLabel",
          label: "职务/职位",
          value: item.teacherType,
          emptyText: "未标注职务",
          options: mergeSelectOptions(teacherTypeOptions, item.teacherType),
          allowCreate: true,
          onDraftInputChange,
        }),
    },
    {
      title: showFeeAdjustment && feeAdjustmentAvailable ? "冻结基准应收" : "应收金额",
      width: 152,
      render: (_, item) => <div className="order-handover-roster-amount-cell"><strong className="is-danger">{formatRosterAmount(item.actualReceivableAmount)}</strong></div>,
    },
    ...(showFeeAdjustment && feeAdjustmentAvailable
      ? ([
          {
            title: "费用调整",
            width: 136,
            render: (_: unknown, item: OrderRosterMember) => (
              <span>{formatFeeAdjustmentMoney(formatDecimalText(
                processedReceivableByMemberId.get(item.id) ?? 0,
              ))}</span>
            ),
          },
          {
            title: "当前个人应收",
            width: 116,
            render: (_: unknown, item: OrderRosterMember) => (
              <span>{formatFeeAdjustmentMoney(formatDecimalText(
                Number(item.actualReceivableAmount || "0") + (processedReceivableByMemberId.get(item.id) ?? 0),
              ))}</span>
            ),
          },
        ] as TableColumnProps<OrderRosterMember>[])
      : []),
    {
      title: "火车硬座硬卧",
      width: 150,
      render: (_, item) =>
        renderRosterEditableTextCell({
          editingMemberIdsRef,
          editingStore,
          item,
          field: "bigTrafficInfo",
          label: "火车硬座硬卧",
          value: item.bigTrafficInfo,
          emptyText: "大交通待补",
          onDraftInputChange,
        }),
    },
    {
      title: "特殊情况",
      width: 150,
      render: (_, item) =>
        renderRosterEditableTextCell({
          editingMemberIdsRef,
          editingStore,
          item,
          field: "guidanceNote",
          label: "特殊情况",
          value: item.guidanceNote,
          emptyText: "无特殊情况",
          tone: item.guidanceNote ? "warning" : undefined,
          onDraftInputChange,
        }),
    },
    {
      title: "学号",
      width: 120,
      render: (_, item) =>
        renderRosterEditableTextCell({
          editingMemberIdsRef,
          editingStore,
          item,
          field: "studentNo",
          label: "学号",
          value: item.studentNo,
          emptyText: "学号待补",
          onDraftInputChange,
        }),
    },
    {
      title: "年龄",
      width: 92,
      render: (_, item) =>
        renderRosterEditableTextCell({
          editingMemberIdsRef,
          editingStore,
          item,
          field: "age",
          label: "年龄",
          value: item.age > 0 ? String(item.age) : "",
          emptyText: "年龄待补",
          onDraftInputChange,
        }),
    },
    {
      title: "性别",
      width: 86,
      render: (_, item) =>
        renderRosterEditableTextCell({
          editingMemberIdsRef,
          editingStore,
          item,
          field: "gender",
          label: "性别",
          value: item.gender,
          onDraftInputChange,
        }),
    },
    {
      title: "生日",
      width: 126,
      render: (_, item) =>
        renderRosterEditableTextCell({
          editingMemberIdsRef,
          editingStore,
          item,
          field: "birthday",
          label: "生日",
          value: item.birthday,
          emptyText: "生日待补",
          onDraftInputChange,
        }),
    },
    {
      title: "主签合同人地址",
      width: 180,
      render: (_, item) =>
        renderRosterEditableTextCell({
          editingMemberIdsRef,
          editingStore,
          item,
          field: "mainSignerAddress",
          label: "主签地址",
          value: item.mainSignerAddress,
          emptyText: "主签地址待补",
          onDraftInputChange,
        }),
    },
    {
      title: "备注",
      width: 150,
      render: (_, item) =>
        renderRosterEditableTextCell({
          editingMemberIdsRef,
          editingStore,
          item,
          field: "remark",
          label: "备注",
          value: item.remark,
          emptyText: "暂无备注",
          onDraftInputChange,
        }),
    },
    {
      title: "标记",
      width: 220,
      fixed: "right",
      render: (_, item) => (
        <RosterMemberTagsCell
          item={item}
          locked={locked}
          saving={saving}
          onClearFlag={onClearMemberFlag}
          onClearAll={onClearAllMemberFlags}
        />
      ),
    },
    {
      title: "操作",
      width: 240,
      fixed: "right",
      render: (_, item) => (
        <Space size={2}>
          {showFeeAdjustment && feeAdjustmentAvailable ? <Button size="mini" type="text" aria-label={`查看调整记录-${item.name}`} onClick={() => onOpenMemberFeeAdjustment(item)}>查看调整记录</Button> : null}
          <RosterOperationCell
            item={item}
            editingMemberIdsRef={editingMemberIdsRef}
            editingStore={editingStore}
            saving={saving}
            locked={locked}
            onOpenEdit={onOpenEdit}
            onCancelEdit={onCancelEdit}
            onSaveEdit={onSaveEdit}
            onToggleStop={onToggleStop}
          />
        </Space>
      ),
    },
  ];
}

/** 订阅表头选择版本，避免进入 Table 内置选择刷新链路。 */
function useRosterSelectionHeaderVersion(store: RosterSelectionStore): number {
  return useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
}

/** 订阅单行选择版本。 */
function useRosterSelectionMemberVersion(memberId: string, store: RosterSelectionStore): number {
  return useSyncExternalStore(
    (listener) => store.subscribeMember(memberId, listener),
    store.getSnapshot,
    store.getSnapshot,
  );
}

/** 订阅单行编辑版本。 */
function useRosterEditingMemberVersion(memberId: string, store: RosterEditingStore): number {
  return useSyncExternalStore(
    (listener) => store.subscribeMember(memberId, listener),
    store.getSnapshot,
    store.getSnapshot,
  );
}

/** 订阅操作列编辑版本。 */
function useRosterOperationVersion(store: RosterEditingStore): number {
  return useSyncExternalStore(store.subscribeOperations, store.getSnapshot, store.getSnapshot);
}

/** 判断当前名单行是否处于编辑态。 */
function useRosterRowEditing(
  memberId: string,
  editingMemberIdsRef: MutableRefObject<Set<string>>,
  store: RosterEditingStore,
): boolean {
  useRosterEditingMemberVersion(memberId, store);
  return editingMemberIdsRef.current.has(memberId);
}

/** 出行名单表头选择框：勾选时先确认当前页或全部页。 */
function RosterSelectAllCheckbox({
  selectedIdsRef,
  visibleMemberIdsRef,
  currentPageMemberIdsRef,
  selectScopeRef,
  store,
  onRequestSelectAll,
  onClearSelection,
}: {
  selectedIdsRef: MutableRefObject<Set<string>>;
  visibleMemberIdsRef: MutableRefObject<string[]>;
  currentPageMemberIdsRef: MutableRefObject<string[]>;
  selectScopeRef: MutableRefObject<RosterSelectScope | null>;
  store: RosterSelectionStore;
  onRequestSelectAll: () => void;
  onClearSelection: () => void;
}) {
  useRosterSelectionHeaderVersion(store);
  const scope = selectScopeRef.current;
  const targetIds =
    scope === "page" ? currentPageMemberIdsRef.current : visibleMemberIdsRef.current;
  const selectedTargetCount = targetIds.filter((memberId) =>
    selectedIdsRef.current.has(memberId),
  ).length;
  const checked = targetIds.length > 0 && selectedTargetCount === targetIds.length;
  const indeterminate = selectedIdsRef.current.size > 0 && !checked;

  return (
    <Checkbox
      aria-label="全选名单成员"
      title="全选：可选择仅当前页或全部筛选结果"
      checked={checked}
      indeterminate={indeterminate}
      disabled={visibleMemberIdsRef.current.length === 0}
      onChange={(nextChecked) => {
        if (nextChecked) {
          onRequestSelectAll();
          return;
        }
        onClearSelection();
      }}
    />
  );
}

/** 出行名单行选择框。 */
function RosterRowCheckbox({
  member,
  selectedIdsRef,
  store,
  onToggle,
}: {
  member: OrderRosterMember;
  selectedIdsRef: MutableRefObject<Set<string>>;
  store: RosterSelectionStore;
  onToggle: (memberId: string, checked: boolean) => void;
}) {
  useRosterSelectionMemberVersion(member.id, store);
  return (
    <Checkbox
      aria-label={`选择${member.name}`}
      checked={selectedIdsRef.current.has(member.id)}
      onChange={(checked) => onToggle(member.id, Boolean(checked))}
    />
  );
}

/** 出行名单姓名列，编辑态只订阅当前行判断。 */
function RosterMemberNameCell({
  item,
  badges,
  editingMemberIdsRef,
  editingStore,
  onDraftInputChange,
}: {
  item: OrderRosterMember;
  badges: ReactNode[];
  editingMemberIdsRef: MutableRefObject<Set<string>>;
  editingStore: RosterEditingStore;
  onDraftInputChange: RosterColumnOptions["onDraftInputChange"];
}) {
  const editing = useRosterRowEditing(item.id, editingMemberIdsRef, editingStore);
  return (
    <div className="order-handover-roster-member">
      {editing ? (
        <RosterEditableTextInput
          ariaLabel={`编辑姓名-${item.name}`}
          value={item.name}
          onChange={(value) => onDraftInputChange(item.id, "memberName", value)}
        />
      ) : (
        <strong>{item.name}</strong>
      )}
      {badges.length > 0 ? <div className="order-handover-roster-tags">{badges}</div> : null}
    </div>
  );
}

/** 出行名单专业/班级类型列。 */
function RosterClassGroupCell({
  item,
  majorOptions,
  editingMemberIdsRef,
  editingStore,
  onDraftInputChange,
}: {
  item: OrderRosterMember;
  majorOptions: EditableSelectOption[];
  editingMemberIdsRef: MutableRefObject<Set<string>>;
  editingStore: RosterEditingStore;
  onDraftInputChange: RosterColumnOptions["onDraftInputChange"];
}) {
  const editing = useRosterRowEditing(item.id, editingMemberIdsRef, editingStore);
  return (
    <div className="order-handover-roster-cell">
      {editing ? (
        <RosterEditableSelect
          testId={`roster-select-classGroupLabel-${item.id}`}
          ariaLabel={`编辑专业/班级类型-${item.name}`}
          value={item.classGroup ?? ""}
          options={mergeSelectOptions(majorOptions, item.classGroup)}
          onChange={(nextValue) => onDraftInputChange(item.id, "classGroupLabel", nextValue)}
        />
      ) : (
        <RosterLabelTag tone={majorTagTone(item.classGroup)}>
          {displayText(item.classGroup, "未填写")}
        </RosterLabelTag>
      )}
    </div>
  );
}

/** 出行名单人员类型列。 */
function RosterTravelerTypeCell({
  item,
  travelerTypeOptions,
  editingMemberIdsRef,
  editingStore,
  onDraftInputChange,
}: {
  item: OrderRosterMember;
  travelerTypeOptions: EditableSelectOption[];
  editingMemberIdsRef: MutableRefObject<Set<string>>;
  editingStore: RosterEditingStore;
  onDraftInputChange: RosterColumnOptions["onDraftInputChange"];
}) {
  const editing = useRosterRowEditing(item.id, editingMemberIdsRef, editingStore);
  return (
    <div className="order-handover-roster-cell">
      {editing ? (
        <RosterEditableSelect
          testId={`roster-select-travelerTypeLabel-${item.id}`}
          ariaLabel={`编辑人员类型-${item.name}`}
          value={item.role}
          options={mergeSelectOptions(travelerTypeOptions, item.role)}
          allowCreate
          onChange={(value) => onDraftInputChange(item.id, "travelerTypeLabel", value)}
        />
      ) : (
        <RosterLabelTag tone={roleTagTone(item.role)}>{item.role}</RosterLabelTag>
      )}
    </div>
  );
}


/** 出行名单操作列。 */
function RosterOperationCell({
  item,
  editingMemberIdsRef,
  editingStore,
  saving,
  locked,
  onOpenEdit,
  onCancelEdit,
  onSaveEdit,
  onToggleStop,
}: {
  item: OrderRosterMember;
  editingMemberIdsRef: MutableRefObject<Set<string>>;
  editingStore: RosterEditingStore;
  saving: boolean;
  locked: boolean;
  onOpenEdit: (member: OrderRosterMember) => void;
  onCancelEdit: (member: OrderRosterMember) => void;
  onSaveEdit: (member: OrderRosterMember) => void;
  onToggleStop: (member: OrderRosterMember) => void;
}) {
  useRosterOperationVersion(editingStore);
  const editingMemberIds = editingMemberIdsRef.current;
  const editing = editingMemberIds.has(item.id);
  if (editing) {
    return (
      <Space size={2}>
        <Button
          size="mini"
          type="text"
          loading={saving}
          aria-label={`保存 ${item.name}`}
          onClick={() => onSaveEdit(item)}
        >
          保存
        </Button>
        <Button
          size="mini"
          type="text"
          aria-label={`取消 ${item.name}`}
          disabled={saving}
          onClick={() => onCancelEdit(item)}
        >
          取消
        </Button>
      </Space>
    );
  }
  return (
    <Space size={2}>
      <Button
        size="mini"
        type="text"
        aria-label={`编辑 ${item.name}`}
        disabled={editingMemberIds.size > 0 || locked}
        onClick={() => onOpenEdit(item)}
      >
        编辑
      </Button>
      <Button
        size="mini"
        type={item.isStopped ? "text" : "primary"}
        status={item.isStopped ? undefined : "danger"}
        className={item.isStopped ? undefined : "order-handover-roster-delete-button"}
        aria-label={`${item.isStopped ? "恢复" : "删除"} ${item.name}`}
        icon={item.isStopped ? undefined : <Trash2 size={13} />}
        disabled={locked}
        onClick={() => onToggleStop(item)}
      >
        {item.isStopped ? "恢复" : "删除"}
      </Button>
    </Space>
  );
}

/** 渲染名单列中的单字段可编辑文本。 */
function renderRosterEditableTextCell({
  editingMemberIdsRef,
  editingStore,
  item,
  field,
  label,
  value,
  readOnlyValue,
  emptyText = "-",
  tone,
  options,
  allowCreate,
  onDraftInputChange,
}: {
  editingMemberIdsRef: MutableRefObject<Set<string>>;
  editingStore: RosterEditingStore;
  item: OrderRosterMember;
  field: keyof RosterMemberEditableDraft;
  label: string;
  value: string | undefined;
  readOnlyValue?: string;
  emptyText?: string;
  tone?: "warning";
  options?: EditableSelectOption[];
  allowCreate?: boolean;
  onDraftInputChange: RosterColumnOptions["onDraftInputChange"];
}): ReactNode {
  return (
    <RosterEditableTextCell
      editingMemberIdsRef={editingMemberIdsRef}
      editingStore={editingStore}
      item={item}
      field={field}
      label={label}
      value={value}
      readOnlyValue={readOnlyValue}
      emptyText={emptyText}
      tone={tone}
      options={options}
      allowCreate={allowCreate}
      onDraftInputChange={onDraftInputChange}
    />
  );
}

/** 出行名单普通文本单元格。 */
function RosterEditableTextCell({
  editingMemberIdsRef,
  editingStore,
  item,
  field,
  label,
  value,
  readOnlyValue,
  emptyText,
  tone,
  options,
  allowCreate,
  onDraftInputChange,
}: {
  editingMemberIdsRef: MutableRefObject<Set<string>>;
  editingStore: RosterEditingStore;
  item: OrderRosterMember;
  field: keyof RosterMemberEditableDraft;
  label: string;
  value: string | undefined;
  readOnlyValue?: string;
  emptyText: string;
  tone?: "warning";
  options?: EditableSelectOption[];
  allowCreate?: boolean;
  onDraftInputChange: RosterColumnOptions["onDraftInputChange"];
}) {
  const editing = useRosterRowEditing(item.id, editingMemberIdsRef, editingStore);
  return (
    <div className="order-handover-roster-cell">
      {editing && options ? (
        <RosterEditableSelect
          testId={`roster-select-${String(field)}-${item.id}`}
          ariaLabel={`编辑${label}-${item.name}`}
          value={value ?? ""}
          options={options}
          allowCreate={allowCreate}
          onChange={(nextValue) => onDraftInputChange(item.id, field, nextValue)}
        />
      ) : editing ? (
        <RosterEditableTextInput
          ariaLabel={`编辑${label}-${item.name}`}
          value={value ?? ""}
          inputMode={field === "mobile" ? "numeric" : undefined}
          onChange={(nextValue) => onDraftInputChange(item.id, field, nextValue)}
        />
      ) : (
        <span className={tone ? `is-${tone}` : undefined}>{displayText(readOnlyValue ?? value, emptyText)}</span>
      )}
    </div>
  );
}

/** 名单编辑输入框，本地承接输入，避免每个字符都重绘整张表。 */
function RosterEditableTextInput({
  ariaLabel,
  value,
  inputMode,
  onChange,
}: {
  ariaLabel: string;
  value: string;
  inputMode?: "numeric";
  onChange: (value: string) => void;
}) {
  return (
    <Input
      className="order-handover-roster-editor"
      aria-label={ariaLabel}
      size="small"
      defaultValue={value}
      inputMode={inputMode}
      onChange={onChange}
    />
  );
}

/** 名单编辑下拉，保持 Arco 组件风格，并通过非受控默认值减少整表重绘。 */
function RosterEditableSelect({
  testId,
  ariaLabel,
  value,
  options,
  allowCreate,
  onChange,
}: {
  testId: string;
  ariaLabel: string;
  value: string;
  options: EditableSelectOption[];
  allowCreate?: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <Select
      aria-label={ariaLabel}
      className="order-handover-roster-editor"
      data-testid={testId}
      defaultValue={value}
      size="mini"
      allowClear
      allowCreate={allowCreate}
      showSearch
      placeholder="请选择"
      filterOption={(inputValue, option) => {
        const optionValue = getSelectOptionValue(option);
        const selected = options.find((item) => item.value === optionValue);
        const searchText = selected?.searchText ?? selected?.label ?? optionValue ?? "";
        return searchText.toLowerCase().includes(inputValue.toLowerCase());
      }}
      virtualListProps={{ height: 224, itemHeight: 32, isStaticItemHeight: true }}
      onChange={(nextValue) => onChange(String(nextValue ?? ""))}
    >
      {options.map((option) => (
        <Select.Option key={option.value} value={option.value} disabled={option.disabled}>
          {option.label}
        </Select.Option>
      ))}
    </Select>
  );
}

/** 出行名单紧凑标签。 */
function RosterLabelTag({
  tone,
  children,
}: {
  tone: "student" | "teacher" | "guide" | "driver" | "majorA" | "majorB" | "majorC" | "neutral";
  children: ReactNode;
}) {
  return <span className={`order-handover-roster-label-tag is-${tone}`}>{children}</span>;
}

/** 渲染名单成员业务标记。 */
function renderRosterBadges(item: OrderRosterMember): ReactNode[] {
  const badges: ReactNode[] = [];
  if (item.isStopped) {
    badges.push(<Tag key="stopped" color="gray">已删除</Tag>);
  }
  return badges;
}

/** 标记列：展示可单独移除的合同标签，并支持清空全部。 */
function RosterMemberTagsCell({
  item,
  locked,
  saving,
  onClearFlag,
  onClearAll,
}: {
  item: OrderRosterMember;
  locked: boolean;
  saving: boolean;
  onClearFlag: (
    member: OrderRosterMember,
    flag: "signer" | "hidden" | "linkman" | "mainSigner",
  ) => void;
  onClearAll: (member: OrderRosterMember) => void;
}) {
  const canEdit = !locked && !saving && !item.isStopped;
  const tags: Array<{
    key: "signer" | "hidden" | "linkman" | "mainSigner";
    label: string;
    color: string;
  }> = [];
  if (item.isMainSigner) {
    tags.push({ key: "mainSigner", label: "主签合同人", color: "red" });
  }
  if (item.isSigner) {
    tags.push({ key: "signer", label: "签合同", color: "red" });
  }
  if (!item.isShowInContract) {
    tags.push({ key: "hidden", label: "不进合同", color: "orange" });
  }
  if (item.isTeamLinkman) {
    tags.push({ key: "linkman", label: "主要联系人", color: "arcoblue" });
  }

  if (tags.length === 0) {
    return (
      <div className="order-handover-roster-tags">
        <Tag color="gray">合同展示</Tag>
      </div>
    );
  }

  return (
    <div className="order-handover-roster-tags">
      {tags.map((tag) => (
        <Tag key={tag.key} color={tag.color} className="order-handover-roster-contract-tag">
          <span>{tag.label}</span>
          {canEdit ? (
            <button
              type="button"
              className="order-handover-roster-tag-close"
              aria-label={`移除标签-${tag.label}-${item.name}`}
              onClick={() => onClearFlag(item, tag.key)}
            >
              ×
            </button>
          ) : null}
        </Tag>
      ))}
      {canEdit ? (
        <Button
          type="text"
          size="mini"
          className="order-handover-roster-tag-clear-all"
          aria-label={`清除全部标签-${item.name}`}
          onClick={() => onClearAll(item)}
        >
          清除全部
        </Button>
      ) : null}
    </div>
  );
}

/** 按角色返回原型风格标签色。 */
function roleTagTone(role: string): "student" | "teacher" | "guide" | "driver" | "neutral" {
  const text = role.trim();
  if (text.includes("老师") || text.includes("教师") || text.includes("班主任")) {
    return "teacher";
  }
  if (text.includes("导游")) {
    return "guide";
  }
  if (text.includes("司机") || text.includes("驾驶")) {
    return "driver";
  }
  if (text.includes("学生")) {
    return "student";
  }
  return "neutral";
}

/** 按专业文本生成稳定标签色。 */
function majorTagTone(value: string | undefined): "majorA" | "majorB" | "majorC" | "neutral" {
  const text = value?.trim();
  if (!text) {
    return "neutral";
  }
  const hash = Array.from(text).reduce((sum, char) => sum + char.charCodeAt(0), 0);
  return (["majorA", "majorB", "majorC"] as const)[hash % 3];
}

/** 默认隐藏证件号和手机号中间段，进入编辑后显示原值。 */
function maskMiddleText(value: string | undefined, prefixLength: number, suffixLength: number): string | undefined {
  const text = value?.trim();
  if (!text) {
    return undefined;
  }
  if (text.length <= prefixLength + suffixLength) {
    return text;
  }
  return `${text.slice(0, prefixLength)}${"*".repeat(text.length - prefixLength - suffixLength)}${text.slice(-suffixLength)}`;
}

/** 格式化名单应收金额。 */
function formatRosterAmount(value?: string): string {
  const text = value?.trim();
  if (!text) {
    return "¥0.00";
  }
  return text.startsWith("¥") ? text : `¥${text}`;
}

/** 汇总住宿安排，优先使用名单头展示缓存。 */
function summarizeRosterAccommodation(
  roster: OrderHandoverRoster | undefined,
  members: OrderRosterMember[],
): string {
  return (
    roster?.accommodationSummaryText?.trim() ||
    summarizeRosterText(members.map((item) => item.rooming), "暂无住宿安排")
  );
}

/** 汇总车辆分配，优先使用名单头展示缓存。 */
function summarizeRosterVehicle(
  roster: OrderHandoverRoster | undefined,
  members: OrderRosterMember[],
): string {
  return (
    roster?.vehicleSummaryText?.trim() ||
    summarizeRosterText(
      members.map((item) => item.vehicleAssignment || item.vehicle),
      "暂无车辆分配",
    )
  );
}

/** 汇总特殊标记，优先使用名单头展示缓存。 */
function summarizeRosterSpecial(
  roster: OrderHandoverRoster | undefined,
  members: OrderRosterMember[],
): string {
  return (
    roster?.specialMarkerSummaryText?.trim() ||
    summarizeRosterText(members.map((item) => item.guidanceNote), "暂无特殊标记")
  );
}

/** 判断是否存在特殊名单标记。 */
function hasRosterSpecial(
  roster: OrderHandoverRoster | undefined,
  members: OrderRosterMember[],
): boolean {
  return summarizeRosterSpecial(roster, members) !== "暂无特殊标记";
}

/** 把名单明细文本压缩为“标签 N人”的汇总。 */
function summarizeRosterText(values: Array<string | undefined>, emptyText: string): string {
  const counts = new Map<string, number>();
  values.forEach((value) => {
    const text = value?.trim();
    if (!text || text === "-" || text.includes("待补") || text.startsWith("无")) {
      return;
    }
    counts.set(text, (counts.get(text) ?? 0) + 1);
  });
  if (counts.size === 0) {
    return emptyText;
  }
  return Array.from(counts.entries())
    .map(([text, count]) => `${text} ${count}人`)
    .join("、");
}

/** 格式化名单应收与合同金额校验结果。 */

/** 格式化名单金额核对结果：对照合同交接表「实际出团」分项，不对照订单头总额。 */
function formatRosterAmountMatch(value: boolean | undefined): string {
  if (value === undefined) {
    return "待核对";
  }
  return value ? "与实际出团一致" : "与实际出团不一致";
}

/** 判断合同或名单状态是否已锁定名单写操作。 */
function isRosterLocked(
  orderStatus: string | undefined,
  roster: OrderHandoverRoster | undefined,
): boolean {
  const normalizedOrderStatus = orderStatus?.trim();
  const normalizedRosterStatus = roster?.status?.trim();
  return (
    normalizedOrderStatus === "finalized" ||
    normalizedOrderStatus === "formed" ||
    normalizedRosterStatus === "finalized" ||
    normalizedRosterStatus === "locked" ||
    normalizedRosterStatus === "formed" ||
    roster?.isTicketLocked === true
  );
}

/** 判断合同交接状态是否已锁定销售交接写操作。 */
function isContractHandoverLocked(orderStatus: string | undefined): boolean {
  const normalizedOrderStatus = orderStatus?.trim();
  return normalizedOrderStatus === "finalized" || normalizedOrderStatus === "formed";
}

/** 解释成交报价采用按钮的锁定来源，避免只显示不可点击状态。 */
function getCustomerQuoteAdoptBlockedReason(
  canManage: boolean,
  orderStatus: string | undefined,
  roster: OrderHandoverRoster | undefined,
): string | undefined {
  if (!canManage) {
    return "缺少合同交接维护权限，不能切换成交报价";
  }
  if (isContractHandoverLocked(orderStatus)) {
    return "合同交接已定稿或已挂网，请先取消下游定稿再切换成交报价";
  }
  const rosterStatus = roster?.status?.trim();
  if (rosterStatus === "finalized" || rosterStatus === "formed") {
    return "名单已定稿，请先取消名单定稿再切换成交报价";
  }
  if (rosterStatus === "locked" || roster?.isTicketLocked === true) {
    return "票务名单已锁定，暂不能切换成交报价";
  }
  return undefined;
}

/** 判断销售交接是否已经定稿或挂网。 */
function isSalesHandoverFinalized(orderStatus: string | undefined): boolean {
  const normalizedOrderStatus = orderStatus?.trim();
  return normalizedOrderStatus === "finalized" || normalizedOrderStatus === "formed";
}

/** 格式化销售交接定稿状态。 */
function formatSalesFinalizeState(orderStatus: string | undefined): string {
  const normalizedOrderStatus = orderStatus?.trim();
  if (normalizedOrderStatus === "formed") {
    return "已定稿";
  }
  if (normalizedOrderStatus === "finalized") {
    return "已定稿";
  }
  return "草稿";
}

/** 按合同交接状态展示销售交接只读原因。 */
function getContractHandoverReadOnlyMessage(orderStatus: string | undefined): string | undefined {
  if (!isContractHandoverLocked(orderStatus)) {
    return undefined;
  }
  return "合同已定稿或已挂网，合同交接信息只读";
}

/** 判断名单是否处于本模块定稿态（含挂网后 formed，与后端写锁口径对齐）。 */
function isRosterFinalized(roster: OrderHandoverRoster | undefined): boolean {
  const status = roster?.status?.trim();
  return status === "finalized" || status === "formed";
}

/** 按锁定来源展示名单只读原因。 */
function getRosterReadOnlyMessage(
  orderStatus: string | undefined,
  roster: OrderHandoverRoster | undefined,
): string {
  const normalizedOrderStatus = orderStatus?.trim();
  const normalizedRosterStatus = roster?.status?.trim();
  if (normalizedRosterStatus === "finalized" || normalizedRosterStatus === "formed") {
    return "名单已定稿，出行名单只读";
  }
  if (normalizedOrderStatus === "finalized" || normalizedOrderStatus === "formed") {
    return "合同已定稿或已挂网，出行名单只读";
  }
  return "出行名单已锁定，出行名单只读";
}

/** 构建名单工具栏 class，编辑态用于收紧布局并避免重复编辑入口。 */
function buildRosterActionsClassName(editing: boolean): string {
  return `order-handover-roster-actions${editing ? " is-editing" : ""}`;
}

/** 是否从返回态带回了非默认筛选，用于恢复时自动展开筛选条。 */
function hasSavedRosterFilters(
  saved: OrderHandoverRosterReturnState | undefined,
): boolean {
  if (!saved) {
    return false;
  }
  return Boolean(
    saved.keyword?.trim() ||
      (saved.type && saved.type !== "全部") ||
      (saved.classGroup && saved.classGroup !== "全部") ||
      (saved.stopped && saved.stopped !== "active"),
  );
}

/** 当前名单筛选是否偏离默认值，用于标题「已筛」提示。 */
function hasActiveRosterFilters(runtime: {
  rosterKeyword: string;
  rosterTypeFilter: string;
  rosterClassGroupFilter: string;
  rosterStoppedFilter: string;
}): boolean {
  return Boolean(
    runtime.rosterKeyword.trim() ||
      runtime.rosterTypeFilter !== "全部" ||
      runtime.rosterClassGroupFilter !== "全部" ||
      runtime.rosterStoppedFilter !== "active",
  );
}

/** 格式化出行名单标题人数胶囊。 */
function formatRosterPeople(stats: { total: number; student: number; teacher: number }): string {
  return `${stats.total}人（学生${stats.student} + 老师${stats.teacher}）`;
}

/** 格式化名单右上角状态摘要。 */
function formatRosterState(stats: {
  student: number;
  teacher: number;
  enteredStudent: number;
  enteredTeacher: number;
}): string {
  if (stats.student + stats.teacher <= 0) {
    return "暂无名单";
  }
  return `学生${stats.enteredStudent}/${stats.student} · 老师${stats.enteredTeacher}/${stats.teacher}`;
}

/** 格式化成交报价金额。 */
function formatCustomerQuoteAmount(value: string): string {
  const amount = Number(value);
  return Number.isFinite(amount)
    ? amount.toLocaleString("zh-CN", { style: "currency", currency: "CNY" })
    : "¥0.00";
}

/** 格式化成交报价人均单价。 */
function formatCustomerQuoteUnitPrice(value: string): string {
  return `${formatCustomerQuoteAmount(value)} / 人`;
}

/** 格式化名单完整度。 */
function formatRosterCompletion(stats: {
  student: number;
  teacher: number;
  enteredStudent: number;
  enteredTeacher: number;
}): string {
  return `学生${stats.enteredStudent}/${stats.student} · 老师${stats.enteredTeacher}/${stats.teacher}人已录入`;
}

/** 计算名单完整度进度条百分比。 */
function calcRosterProgress(stats: { total: number; entered: number }): number {
  if (stats.total <= 0) {
    return 0;
  }
  return Math.min(Math.round((stats.entered / stats.total) * 100), 100);
}
